import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  BellRing,
  Building2,
  CheckCheck,
  ChevronRight,
  Copy,
  Download,
  Eye,
  EyeOff,
  FolderOpen,
  Globe,
  HardDrive,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  LineChart,
  LockKeyhole,
  LogOut,
  Mail,
  MessageSquare,
  MonitorSmartphone,
  QrCode,
  RefreshCw,
  Search,
  ServerCog,
  Settings2,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Trash2,
  UserRound,
  Users,
  Wifi,
} from 'lucide-react';
import type {
  AcknowledgeFeedbackInput,
  DeleteXianyuInventoryCodesInput,
  FeedbackConversationMessage,
  FeedbackEntry,
  FulfillXianyuOrderInput,
  GenerateXianyuCodesInput,
  HostLicenseDurationKey,
  IssueLicenseCodeInput,
  LicenseHostOrderDeliveryActionResult,
  IssueRemoteProfileTransferInput,
  IssueRemoteLicenseInput,
  LicenseHostRemoteUpdateChannelState,
  LicenseSummary,
  LicenseHostIssuedRecord,
  SaveXianyuBotClientInput,
  SaveXianyuOrderInput,
  SaveXianyuTemplateInput,
  SaveXianyuTierInput,
  LicenseHostActivityRecord,
  LicenseHostLoginEvent,
  LicenseHostRemoteClient,
  LicenseHostRemoteClientGroup,
  LicenseHostRemoteDashboard,
  LicenseHostRemoteDashboardResult,
  LicenseHostRemoteProfileSnapshot,
  LicenseHostRemoteActionResult,
  LicenseHostRecordsResult,
  LicenseHostState,
  LicenseHostActionResult,
  LicenseHostOrderDeliveryDashboardResult,
  ProfileSummary,
  ReleaseRemoteAccountRestrictionInput,
  RemoteAccountAccessBadge,
  ResetRemoteAccountPasswordInput,
  ResetRemoteSecondaryPasswordInput,
  RevokeRemoteLicenseInput,
  SetRemoteUpdateChannelInput,
  MarkFeedbackReadInput,
  MarkLicenseHostActivitiesReadInput,
  UsageQuota,
  XianyuManagementSnapshot,
  XianyuCodeChannel,
} from '../../shared/types';
import type { CodexWorkspaceApi } from '../../shared/ipc';
import packageMetadata from '../../package.json';
import appIconSrc from '../assets/icons/openai.png';
import {
  playMessageDropletTone,
  showDesktopNotification,
} from '../lib/message-notification';

interface HostFormState {
  durationKey: HostLicenseDurationKey;
  customDurationDaysInput: string;
  targetLabel: string;
  deviceFingerprintHash: string;
  deviceId: string;
  slotLimitInput: string;
  betaAccess: boolean;
  advancedBetaAccess: boolean;
  note: string;
}

interface RemoteIssueFormState {
  durationKey: HostLicenseDurationKey;
  customDurationDaysInput: string;
  slotLimitInput: string;
  betaAccess: boolean;
  advancedBetaAccess: boolean;
  note: string;
  profileId: string;
  passwordInput: string;
}

interface OrderDeliveryTierFormState {
  id: string;
  title: string;
  tierKey: string;
  skuTitle: string;
  durationKey: HostLicenseDurationKey;
  customDurationDaysInput: string;
  slotLimitInput: string;
  licenseTypeLabel: string;
  tutorialUrl: string;
  noticeText: string;
  signatureText: string;
  autoDeliveryNote: string;
  enabled: boolean;
}

interface OrderDeliveryTemplateFormState {
  id: string;
  tierId: string;
  name: string;
  bodyTemplate: string;
  tutorialUrl: string;
  noticeText: string;
  signatureText: string;
  enabled: boolean;
  isDefault: boolean;
}

interface OrderDeliveryOrderFormState {
  id: string;
  sourceOrderId: string;
  buyerId: string;
  buyerName: string;
  itemTitle: string;
  specName: string;
  specValue: string;
  quantityInput: string;
  amountText: string;
  tierId: string;
  rawPayloadJson: string;
}

interface OrderDeliveryCodeFormState {
  channel: XianyuCodeChannel;
  durationKey: HostLicenseDurationKey;
  customDurationDaysInput: string;
  slotLimitInput: string;
  quantityInput: string;
}

interface OrderDeliveryBotFormState {
  id: string;
  clientName: string;
  endpointUrl: string;
  botKeyHint: string;
  status: string;
  enabled: boolean;
  note: string;
}

interface LicenseHostLocalLoginConfig {
  username: string;
  passwordHash: string;
  updatedAt: string;
}

interface LicenseHostLocalSession {
  username: string;
  unlockedAt: string;
  lastActiveAt: string;
  expiresAt: string;
  configUpdatedAt: string;
}

type OptimisticFeedbackMessageState = 'sending' | 'failed';

interface OptimisticHostFeedbackMessage {
  id: string;
  feedbackId: string;
  body: string;
  createdAt: string;
  state: OptimisticFeedbackMessageState;
  errorMessage: string | null;
}

const MAX_CUSTOM_DURATION_DAYS = 3650;
const ORDER_DELIVERY_CODE_PAGE_SIZE = 12;
const ORDER_DELIVERY_CODE_CHANNEL_OPTIONS: Array<{ value: XianyuCodeChannel; label: string }> = [
  { value: 'stable', label: '普通用户' },
  { value: 'beta', label: '内测' },
  { value: 'advanced-beta', label: '高级内测' },
];
const FEEDBACK_SERVICE_DISPLAY_NAME = '客服';
const FEEDBACK_ACTION_TIMEOUT_MS = 15_000;
const LICENSE_HOST_BROWSER_BRIDGE_URL = 'http://127.0.0.1:5174';
const LICENSE_HOST_BROWSER_API_URL = 'https://api.ainivox.cn';
const LICENSE_HOST_LOGIN_SESSION_KEY = 'auth-api-switcher.license-host.login-session.v1';
const LICENSE_HOST_LOGIN_USERNAME_KEY = 'auth-api-switcher.license-host.login-username.v1';
const LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY = 'auth-api-switcher.license-host.browser-admin-token.v1';
const LICENSE_HOST_BROWSER_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const LICENSE_HOST_DEFAULT_LOGIN_USERNAME = '1870511741@qq.com';
const LICENSE_HOST_FALLBACK_LOGIN_USERNAME = 'root';
const LICENSE_HOST_LOGIN_PASSWORD = 'change2014';
const LICENSE_HOST_LOGIN_CONFIG_UPDATED_AT = 'builtin-2026-04-17';
const LICENSE_HOST_BROWSER_CONSOLE_HOST_ID = 'HOST-CLOUD-WEB';
const LICENSE_HOST_BROWSER_APP_VERSION =
  typeof packageMetadata.version === 'string' && packageMetadata.version.trim()
    ? packageMetadata.version.trim()
    : '0.0.0';
const LICENSE_HOST_FIXED_LOGIN_CONFIG: LicenseHostLocalLoginConfig = {
  username: LICENSE_HOST_DEFAULT_LOGIN_USERNAME,
  passwordHash: 'fixed-password',
  updatedAt: LICENSE_HOST_LOGIN_CONFIG_UPDATED_AT,
};
type LicenseHostWorkspaceSection =
  | 'overview'
  | 'customers'
  | 'orders'
  | 'codes'
  | 'feedback'
  | 'settings';

interface BrowserAdminOverviewPayload {
  profile?: {
    displayName?: string | null;
    email?: string | null;
    createdAt?: string | null;
  } | null;
  stats?: {
    totalLicenses?: number | null;
    activeLicenses?: number | null;
  } | null;
  remote: LicenseHostRemoteDashboard;
  topology: XianyuManagementSnapshot;
  publicUrls?: {
    api?: string | null;
  } | null;
}

interface BrowserAuthLoginResult {
  message: string;
  sessionToken?: string;
}

const LICENSE_HOST_WORKSPACE_MOCK = {
  activeTrend: [
    { label: '周一', value: 42 },
    { label: '周二', value: 48 },
    { label: '周三', value: 45 },
    { label: '周四', value: 57 },
    { label: '周五', value: 62 },
    { label: '周六', value: 58 },
    { label: '周日', value: 54 },
  ],
  licenseTrend: [
    { label: '周一', value: 16 },
    { label: '周二', value: 22 },
    { label: '周三', value: 19 },
    { label: '周四', value: 26 },
    { label: '周五', value: 31 },
    { label: '周六', value: 24 },
    { label: '周日', value: 21 },
  ],
  flowTrend: [
    { label: '09:00', value: 18 },
    { label: '10:00', value: 31 },
    { label: '11:00', value: 27 },
    { label: '12:00', value: 44 },
    { label: '13:00', value: 39 },
    { label: '14:00', value: 58 },
    { label: '15:00', value: 52 },
    { label: '16:00', value: 67 },
  ],
  channelOrders: [
    { id: 'ORD-ALI-2041', channel: '爱发电', status: '已完成', amount: '¥1,296', note: '标准年费授权' },
    { id: 'ORD-WEB-1182', channel: '官网直购', status: '处理中', amount: '¥648', note: '客户待补录激活码' },
    { id: 'ORD-WX-0725', channel: '微信收款', status: '待确认', amount: '¥324', note: '等待人工核单' },
  ],
  memberPermissions: [
    { name: '运营管理员', summary: '管理授权、查看客户和反馈', level: '完全访问' },
    { name: '客服成员', summary: '处理反馈、查看授权状态', level: '工作区访问' },
    { name: '系统维护', summary: '查看主机状态与同步设置', level: '只读监控' },
  ],
  notificationRules: [
    { title: '新登录提醒', detail: '客户端首次登录或异常登录时提醒', enabled: true },
    { title: '客户反馈回执', detail: '未回执反馈持续挂起提醒', enabled: true },
    { title: '云端同步波动', detail: '远程源检查失败时提示维护', enabled: false },
  ],
} as const;

const LICENSE_HOST_SECTION_COPY = {
  overview: {
    title: '运营总览',
    subtitle: '查看运行概况、趋势图和待处理提醒。',
  },
  customers: {
    title: '客户与授权',
    subtitle: '处理客户详情、授权动作、设备空间和客服会话。',
  },
  orders: {
    title: '订单发货',
    subtitle: '处理订单登记、发货模板、自动通道和审计记录。',
  },
  codes: {
    title: '授权码生成',
    subtitle: '批量生成授权码、查看库存状态和绑定账号。',
  },
  feedback: {
    title: '客服反馈',
    subtitle: '集中处理客户会话、收信箱和未读提醒。',
  },
  settings: {
    title: '系统与设置',
    subtitle: '集中管理本地签发、Cloudflare 状态和工作偏好。',
  },
} as const;

const parseLicenseHostStorage = <T,>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const normalizeText = (value?: string | null): string => value?.trim() ?? '';

const normalizeLicenseHostLoginUsername = (value: string): string => value.trim().toLowerCase();

const isLicenseHostAllowedUsername = (value: string): boolean => {
  const normalized = normalizeLicenseHostLoginUsername(value);
  return normalized === LICENSE_HOST_DEFAULT_LOGIN_USERNAME || normalized === LICENSE_HOST_FALLBACK_LOGIN_USERNAME;
};

const loadLicenseHostLoginConfig = (): LicenseHostLocalLoginConfig => LICENSE_HOST_FIXED_LOGIN_CONFIG;

const buildLicenseHostSession = (
  username: string,
  configUpdatedAt: string,
): LicenseHostLocalSession => {
  const now = new Date().toISOString();
  return {
    username,
    unlockedAt: now,
    lastActiveAt: now,
    expiresAt: new Date(Date.now() + LICENSE_HOST_BROWSER_IDLE_TIMEOUT_MS).toISOString(),
    configUpdatedAt,
  };
};

const getLicenseHostSessionExpiresAt = (session: LicenseHostLocalSession): number => {
  const expiresAt = new Date(normalizeText(session.expiresAt)).getTime();
  return Number.isFinite(expiresAt) ? expiresAt : 0;
};

const isLicenseHostSessionValid = (
  session: LicenseHostLocalSession | null,
  configUpdatedAt: string,
): boolean =>
  Boolean(
    session &&
      isLicenseHostAllowedUsername(session.username) &&
      session.configUpdatedAt === configUpdatedAt &&
      getLicenseHostSessionExpiresAt(session) > Date.now(),
  );

const touchLicenseHostSession = (session: LicenseHostLocalSession): LicenseHostLocalSession => {
  const touchedAt = new Date().toISOString();
  return {
    ...session,
    lastActiveAt: touchedAt,
    expiresAt: new Date(Date.now() + LICENSE_HOST_BROWSER_IDLE_TIMEOUT_MS).toISOString(),
  };
};

const loadLicenseHostSession = (): LicenseHostLocalSession | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  window.localStorage.removeItem(LICENSE_HOST_LOGIN_SESSION_KEY);
  return parseLicenseHostStorage<LicenseHostLocalSession>(
    window.sessionStorage.getItem(LICENSE_HOST_LOGIN_SESSION_KEY),
  );
};

const persistLicenseHostSession = (session: LicenseHostLocalSession) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LICENSE_HOST_LOGIN_SESSION_KEY);
  window.sessionStorage.setItem(LICENSE_HOST_LOGIN_SESSION_KEY, JSON.stringify(session));
};

const clearLicenseHostSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LICENSE_HOST_LOGIN_SESSION_KEY);
  window.sessionStorage.removeItem(LICENSE_HOST_LOGIN_SESSION_KEY);
};

const loadLicenseHostBrowserAuthToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  window.localStorage.removeItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY);
  return normalizeText(window.sessionStorage.getItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY)) || null;
};

const persistLicenseHostBrowserAuthToken = (token: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedToken = normalizeText(token);
  if (!normalizedToken) {
    return;
  }

  window.localStorage.removeItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY);
  window.sessionStorage.setItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY, normalizedToken);
};

const clearLicenseHostBrowserAuthToken = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY);
  window.sessionStorage.removeItem(LICENSE_HOST_BROWSER_AUTH_TOKEN_KEY);
};

const loadLicenseHostRememberedUsername = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const username = normalizeLicenseHostLoginUsername(
    normalizeText(window.localStorage.getItem(LICENSE_HOST_LOGIN_USERNAME_KEY)),
  );
  return isLicenseHostAllowedUsername(username) ? username : '';
};

const persistLicenseHostRememberedUsername = (username: string, remember: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!remember) {
    window.localStorage.removeItem(LICENSE_HOST_LOGIN_USERNAME_KEY);
    return;
  }

  const normalizedUsername = normalizeLicenseHostLoginUsername(username);
  if (!isLicenseHostAllowedUsername(normalizedUsername)) {
    window.localStorage.removeItem(LICENSE_HOST_LOGIN_USERNAME_KEY);
    return;
  }
  window.localStorage.setItem(LICENSE_HOST_LOGIN_USERNAME_KEY, normalizedUsername);
};

const resolveLicenseHostBrowserAdminEmail = (username: string): string =>
  normalizeLicenseHostLoginUsername(username) === LICENSE_HOST_FALLBACK_LOGIN_USERNAME
    ? LICENSE_HOST_DEFAULT_LOGIN_USERNAME
    : normalizeLicenseHostLoginUsername(username);

type LicenseHostViewBridge = Pick<
  CodexWorkspaceApi,
  | 'getAppState'
  | 'importProfileTransferPackage'
  | 'searchLicenseHostRecords'
  | 'openPath'
  | 'minimizeWindow'
  | 'toggleMaximizeWindow'
  | 'closeWindow'
  | 'getWindowState'
  | 'onWindowStateChange'
  | 'openLicenseHostWindow'
  | 'restartLicenseHost'
  | 'getLicenseHostState'
  | 'issueLicenseCode'
  | 'getLicenseHostRemoteDashboard'
  | 'issueRemoteLicense'
  | 'setRemoteUpdateChannel'
  | 'revokeRemoteLicense'
  | 'releaseRemoteAccountRestriction'
  | 'resetRemotePassword'
  | 'resetRemoteSecondaryPassword'
  | 'issueRemoteProfileTransfer'
  | 'markLicenseHostActivitiesRead'
  | 'markFeedbackRead'
  | 'acknowledgeFeedback'
  | 'getLicenseHostOrderDeliveryDashboard'
  | 'saveLicenseHostOrderDeliveryTier'
  | 'saveLicenseHostOrderDeliveryTemplate'
  | 'saveLicenseHostOrderDeliveryOrder'
  | 'generateLicenseHostOrderDeliveryCodes'
  | 'deleteLicenseHostOrderDeliveryCodes'
  | 'fulfillLicenseHostOrderDeliveryOrder'
  | 'saveLicenseHostOrderDeliveryBot'
> & {
  openExternal?: CodexWorkspaceApi['openExternal'];
  mode: 'electron' | 'browser';
};

const buildBrowserBridgeUrl = (pathname: string): string =>
  `${LICENSE_HOST_BROWSER_BRIDGE_URL}${pathname}`;

const requestBrowserBridge = async <T,>(pathname: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(buildBrowserBridgeUrl(pathname), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === 'string'
        ? payload
        : payload && typeof payload === 'object' && 'message' in payload
          ? String(payload.message)
          : `浏览器桥接请求失败：${response.status}`;
    throw new Error(message);
  }

  return payload as T;
};

const resolveLicenseHostBrowserApiBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return LICENSE_HOST_BROWSER_API_URL;
  }

  const { protocol, hostname, origin } = window.location;
  const isLocalPreview =
    protocol === 'http:' && (hostname === '127.0.0.1' || hostname === 'localhost');

  return isLocalPreview ? origin : LICENSE_HOST_BROWSER_API_URL;
};

const buildBrowserCloudApiUrl = (pathname: string): string =>
  new URL(pathname, resolveLicenseHostBrowserApiBaseUrl()).toString();

const requestBrowserCloudJson = async <T,>(
  pathname: string,
  init?: RequestInit,
  options?: { requiresAuth?: boolean },
): Promise<T> => {
  const headers = new Headers(init?.headers ?? {});
  if (init?.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json; charset=utf-8');
  }

  if (options?.requiresAuth !== false) {
    const token = loadLicenseHostBrowserAuthToken();
    if (!token) {
      throw new Error('云端管理员会话已过期，请重新登录。');
    }
    headers.set('authorization', `Bearer ${token}`);
  }

  const response = await fetch(buildBrowserCloudApiUrl(pathname), {
    ...init,
    headers,
  });

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      clearLicenseHostBrowserAuthToken();
      clearLicenseHostSession();
    }
    const message =
      typeof payload === 'string'
        ? payload
        : payload && typeof payload === 'object' && 'message' in payload
          ? String(payload.message)
          : `云端控制台请求失败：${response.status}`;
    throw new Error(message);
  }

  return payload as T;
};

const LICENSE_HOST_BROWSER_SUPPORTED_DURATIONS: LicenseHostState['supportedDurations'] = [
  { key: 'one-day', label: '1 天', description: '短期体验授权。' },
  { key: 'one-week', label: '7 天', description: '周维度试用授权。' },
  { key: 'half-month', label: '15 天', description: '半月授权。' },
  { key: 'one-month', label: '30 天', description: '整月授权。' },
  { key: 'half-year', label: '半年', description: '半年授权。' },
  { key: 'one-year', label: '一年', description: '年度授权。' },
  { key: 'custom-days', label: '自定义', description: '按自定义天数下发。' },
  { key: 'permanent', label: '永久', description: '永久授权。' },
];

const buildBrowserCloudHostState = (overview: BrowserAdminOverviewPayload): LicenseHostState => {
  const latestIssueActivity =
    overview.remote.activities.find(
      (item) => item.eventType === 'remote-license-issue' || item.eventType === 'activation',
    ) ?? null;
  const apiSourceUrl =
    normalizeText(overview.publicUrls?.api) || normalizeText(overview.remote.sourceUrl) || LICENSE_HOST_BROWSER_API_URL;

  return {
    initialized: true,
    hostId: LICENSE_HOST_BROWSER_CONSOLE_HOST_ID,
    hostName: '授权控制台',
    hostCreatedAt: overview.profile?.createdAt ?? null,
    publicKeySummary: 'Cloudflare Admin Session',
    publicKeyPem: '',
    publicKeyPath: '',
    storagePath: 'Cloudflare D1 / Workers',
    currentDevice: {
      deviceId: 'WEB-ADMIN',
      fingerprintHash: '',
      fingerprintSummary: 'CLOUD-WEB',
      hostLabel: 'admin.ainivox.cn',
      platformLabel: 'Web',
      machineName: 'Cloud Browser',
    },
    lastIssuedAt: latestIssueActivity?.createdAt ?? null,
    issuedCount: Number(overview.stats?.activeLicenses ?? overview.stats?.totalLicenses ?? 0),
    supportedDurations: LICENSE_HOST_BROWSER_SUPPORTED_DURATIONS,
    recentRecords: [],
    remoteAdminConfigured: true,
    remoteSourceUrl: apiSourceUrl,
  };
};

const createBrowserLocalLicenseHostBridge = (): LicenseHostViewBridge => ({
  mode: 'browser',
  getAppState: () => requestBrowserBridge('/app/state'),
  importProfileTransferPackage: (profilePackage, options) =>
    requestBrowserBridge('/app/import-profile-transfer-package', {
      method: 'POST',
      body: JSON.stringify({ profilePackage, options }),
    }),
  searchLicenseHostRecords: (query) =>
    requestBrowserBridge('/license-host/search-records', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),
  openPath: (targetPath) =>
    requestBrowserBridge('/shell/open-path', {
      method: 'POST',
      body: JSON.stringify({ targetPath }),
    }),
  minimizeWindow: async () => ({ maximized: false, visible: true }),
  toggleMaximizeWindow: async () => ({ maximized: false, visible: true }),
  closeWindow: async () => {
    window.close();
    return { maximized: false, visible: false };
  },
  getWindowState: async () => ({ maximized: false, visible: true }),
  onWindowStateChange: () => () => undefined,
  openLicenseHostWindow: async () => ({ ok: true as const }),
  restartLicenseHost: async () => {
    window.location.reload();
    return { ok: true as const };
  },
  getLicenseHostState: () => requestBrowserBridge('/license-host/state'),
  issueLicenseCode: (input) =>
    requestBrowserBridge('/license-host/issue-code', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getLicenseHostRemoteDashboard: (input) =>
    requestBrowserBridge('/license-host/remote-dashboard', {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    }),
  issueRemoteLicense: (input) =>
    requestBrowserBridge('/license-host/issue-remote-license', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  setRemoteUpdateChannel: (input) =>
    requestBrowserBridge('/license-host/set-remote-update-channel', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeRemoteLicense: (input) =>
    requestBrowserBridge('/license-host/revoke-remote-license', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  releaseRemoteAccountRestriction: (input) =>
    requestBrowserBridge('/license-host/release-remote-account-restriction', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  resetRemotePassword: (input) =>
    requestBrowserBridge('/license-host/reset-remote-password', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  resetRemoteSecondaryPassword: (input) =>
    requestBrowserBridge('/license-host/reset-remote-secondary-password', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  issueRemoteProfileTransfer: (input) =>
    requestBrowserBridge('/license-host/issue-remote-profile-transfer', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  markLicenseHostActivitiesRead: (input) =>
    requestBrowserBridge('/license-host/mark-activities-read', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  markFeedbackRead: (input) =>
    requestBrowserBridge('/license-host/mark-feedback-read', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  acknowledgeFeedback: (input) =>
    requestBrowserBridge('/license-host/acknowledge-feedback', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getLicenseHostOrderDeliveryDashboard: () =>
    requestBrowserBridge('/license-host/order-delivery/dashboard'),
  saveLicenseHostOrderDeliveryTier: (input) =>
    requestBrowserBridge('/license-host/order-delivery/tier/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  saveLicenseHostOrderDeliveryTemplate: (input) =>
    requestBrowserBridge('/license-host/order-delivery/template/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  saveLicenseHostOrderDeliveryOrder: (input) =>
    requestBrowserBridge('/license-host/order-delivery/order/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  generateLicenseHostOrderDeliveryCodes: (input) =>
    requestBrowserBridge('/license-host/order-delivery/codes/generate', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteLicenseHostOrderDeliveryCodes: (input) =>
    requestBrowserBridge('/license-host/order-delivery/codes/delete', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  fulfillLicenseHostOrderDeliveryOrder: (input) =>
    requestBrowserBridge('/license-host/order-delivery/order/fulfill', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  saveLicenseHostOrderDeliveryBot: (input) =>
    requestBrowserBridge('/license-host/order-delivery/bot/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
});

const createBrowserCloudLicenseHostBridge = (): LicenseHostViewBridge => ({
  mode: 'browser',
  getAppState: async () => {
    throw new Error('云端控制台当前不提供本地空间清单。');
  },
  importProfileTransferPackage: async () => {
    throw new Error('云端浏览器端当前不支持直接导入本地空间包。');
  },
  searchLicenseHostRecords: async (query): Promise<LicenseHostRecordsResult> => ({
    query,
    records: [],
    totalCount: 0,
  }),
  openPath: async () => ({
    ok: false,
    error: '云端浏览器端不支持打开本地路径。',
  }),
  minimizeWindow: async () => ({ maximized: false, visible: true }),
  toggleMaximizeWindow: async () => ({ maximized: false, visible: true }),
  closeWindow: async () => {
    window.close();
    return { maximized: false, visible: false };
  },
  getWindowState: async () => ({ maximized: false, visible: true }),
  onWindowStateChange: () => () => undefined,
  openLicenseHostWindow: async () => ({ ok: true as const }),
  restartLicenseHost: async () => {
    window.location.reload();
    return { ok: true as const };
  },
  getLicenseHostState: async (): Promise<LicenseHostState> => {
    const overview = await requestBrowserCloudJson<BrowserAdminOverviewPayload>('/api/admin/overview');
    return buildBrowserCloudHostState(overview);
  },
  issueLicenseCode: async (): Promise<LicenseHostActionResult> => {
    throw new Error('云端控制台当前不提供本地授权码签发，请改用本地主机入口。');
  },
  getLicenseHostRemoteDashboard: async (
    input,
  ): Promise<LicenseHostRemoteDashboardResult> => {
    const dashboard = await requestBrowserCloudJson<LicenseHostRemoteDashboard>(
      '/api/admin/license-host/browser/dashboard',
      {
        method: 'POST',
        body: JSON.stringify(input ?? {}),
      },
    );
    return {
      dashboard,
      message: '远程客户面板已刷新。',
    };
  },
  issueRemoteLicense: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>('/api/admin/license-host/browser/issue', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  setRemoteUpdateChannel: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/update-channel',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  revokeRemoteLicense: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>('/api/admin/license-host/browser/revoke', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  releaseRemoteAccountRestriction: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/release-account-restriction',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  resetRemotePassword: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/reset-password',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  resetRemoteSecondaryPassword: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/reset-secondary-password',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  issueRemoteProfileTransfer: async (): Promise<LicenseHostRemoteActionResult> => {
    throw new Error('云端浏览器端当前不支持直接发起空间包传送。');
  },
  markLicenseHostActivitiesRead: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/activities/read',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  markFeedbackRead: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/feedback/read',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  acknowledgeFeedback: (input) =>
    requestBrowserCloudJson<LicenseHostRemoteActionResult>(
      '/api/admin/license-host/browser/feedback/ack',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  getLicenseHostOrderDeliveryDashboard: async (): Promise<LicenseHostOrderDeliveryDashboardResult> => {
    const overview = await requestBrowserCloudJson<BrowserAdminOverviewPayload>('/api/admin/overview');
    return {
      topology: overview.topology,
      message: '授权码工作区已刷新。',
    };
  },
  saveLicenseHostOrderDeliveryTier: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>('/api/admin/xianyu/tier/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  saveLicenseHostOrderDeliveryTemplate: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>(
      '/api/admin/xianyu/template/save',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  saveLicenseHostOrderDeliveryOrder: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>('/api/admin/xianyu/order/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  generateLicenseHostOrderDeliveryCodes: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>(
      '/api/admin/xianyu/codes/generate',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  deleteLicenseHostOrderDeliveryCodes: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>(
      '/api/admin/xianyu/codes/delete',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  fulfillLicenseHostOrderDeliveryOrder: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>(
      '/api/admin/xianyu/order/fulfill',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  saveLicenseHostOrderDeliveryBot: (input) =>
    requestBrowserCloudJson<LicenseHostOrderDeliveryActionResult>('/api/admin/xianyu/bot/save', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
});

const resolveLicenseHostBridge = (): LicenseHostViewBridge | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (window.codexWorkspace) {
    return {
      mode: 'electron',
      ...window.codexWorkspace,
    };
  }

  const query = new URLSearchParams(window.location.search);
  if (query.get('view') === 'license-host') {
    return createBrowserLocalLicenseHostBridge();
  }

  if (window.location.pathname.startsWith('/root-console')) {
    return createBrowserCloudLicenseHostBridge();
  }

  return null;
};

const planLabelMap: Record<ProfileSummary['planType'], string> = {
  Unknown: '未识别',
  Free: '免费',
  Plus: 'PLUS',
  'Pro 5x': 'PRO 5X',
  'Pro 20x': 'PRO 20X',
  Team: 'TEAM',
  Business: 'BUSINESS',
};

const mapRecordChannelLabel = (record: LicenseHostIssuedRecord): string => {
  if (record.updateChannel === 'beta') {
    return record.allowEngineerAccess ? '高级内测通道' : '内测通道';
  }

  if (record.updateChannel === 'canary') {
    return '测试通道';
  }

  return '正式通道';
};

const buildIssuedRecordDeviceInfo = (record: LicenseHostIssuedRecord): string =>
  [
    `目标设备：${record.targetLabel}`,
    `设备编号：${record.targetDeviceId ?? '未记录'}`,
    `机器指纹摘要：${record.targetFingerprintSummary}`,
    `机器指纹哈希：${record.targetFingerprintHash ?? '旧记录未保存完整指纹'}`,
    `授权时长：${record.durationLabel}`,
    `卡片槽位：${record.slotLimitLabel}`,
    `更新通道：${mapRecordChannelLabel(record)}`,
    `工程师模式：${record.allowEngineerAccess ? '已开放' : '未开放'}`,
    `测试标识：${record.testerId ?? '未设置'}`,
    `签发时间：${record.createdAt}`,
    `备注：${record.note ?? '无'}`,
  ].join('\n');

const createEmptyHostState = (): LicenseHostState => ({
  initialized: false,
  hostId: 'HOST-UNSET',
  hostName: '授权控制台',
  hostCreatedAt: null,
  publicKeySummary: '未初始化',
  publicKeyPem: '',
  publicKeyPath: '',
  storagePath: '',
  currentDevice: {
    deviceId: 'DEV-UNKNOWN',
    fingerprintHash: '',
    fingerprintSummary: 'UNKN-OWNS-IGN',
    hostLabel: 'unknown',
    platformLabel: 'Unknown',
    machineName: 'Unknown',
  },
  lastIssuedAt: null,
  issuedCount: 0,
  supportedDurations: [],
  recentRecords: [],
  remoteAdminConfigured: false,
  remoteSourceUrl: null,
});

const createInitialForm = (): HostFormState => ({
  durationKey: 'permanent',
  customDurationDaysInput: '',
  targetLabel: '',
  deviceFingerprintHash: '',
  deviceId: '',
  slotLimitInput: '',
  betaAccess: false,
  advancedBetaAccess: false,
  note: '',
});

const createInitialRemoteForm = (): RemoteIssueFormState => ({
  durationKey: 'permanent',
  customDurationDaysInput: '',
  slotLimitInput: '',
  betaAccess: false,
  advancedBetaAccess: false,
  note: '',
  profileId: '',
  passwordInput: '',
});

const createInitialOrderDeliveryTierForm = (): OrderDeliveryTierFormState => ({
  id: '',
  title: '',
  tierKey: '',
  skuTitle: '',
  durationKey: 'one-month',
  customDurationDaysInput: '',
  slotLimitInput: '',
  licenseTypeLabel: '',
  tutorialUrl: '',
  noticeText: '',
  signatureText: '',
  autoDeliveryNote: '',
  enabled: true,
});

const createInitialOrderDeliveryTemplateForm = (): OrderDeliveryTemplateFormState => ({
  id: '',
  tierId: '',
  name: '',
  bodyTemplate: '',
  tutorialUrl: '',
  noticeText: '',
  signatureText: '',
  enabled: true,
  isDefault: true,
});

const createInitialOrderDeliveryOrderForm = (): OrderDeliveryOrderFormState => ({
  id: '',
  sourceOrderId: '',
  buyerId: '',
  buyerName: '',
  itemTitle: '',
  specName: '',
  specValue: '',
  quantityInput: '1',
  amountText: '',
  tierId: '',
  rawPayloadJson: '',
});

const createInitialOrderDeliveryCodeForm = (): OrderDeliveryCodeFormState => ({
  channel: 'stable',
  durationKey: 'one-month',
  customDurationDaysInput: '',
  slotLimitInput: String(getDefaultSlotLimitByDuration('one-month') ?? 4),
  quantityInput: '10',
});

const createInitialOrderDeliveryBotForm = (): OrderDeliveryBotFormState => ({
  id: '',
  clientName: '',
  endpointUrl: '',
  botKeyHint: '',
  status: 'idle',
  enabled: true,
  note: '',
});

const createEmptyOrderDeliverySnapshot = (): XianyuManagementSnapshot => ({
  tables: {
    productTiers: false,
    activationCodeInventory: false,
    salesOrders: false,
    deliveryTemplates: false,
    botClients: false,
    auditLogs: false,
    secretConfigured: false,
    ready: false,
  },
  summary: {
    totalTiers: 0,
    activeTiers: 0,
    totalTemplates: 0,
    unusedCodes: 0,
    deliveredCodes: 0,
    redeemedCodes: 0,
    pendingOrders: 0,
    fulfilledOrders: 0,
    enabledBots: 0,
    secretConfigured: false,
  },
  tiers: [],
  templates: [],
  orders: [],
  recentCodes: [],
  bots: [],
  auditLogs: [],
});

const createEmptyRemoteDashboard = (): LicenseHostRemoteDashboard => ({
  connected: false,
  checkedAt: null,
  sourceUrl: null,
  query: '',
  totalGroups: 0,
  onlineGroups: 0,
  recentGroups: 0,
  offlineGroups: 0,
  totalClients: 0,
  onlineClients: 0,
  recentClients: 0,
  offlineClients: 0,
  unreadLoginCount: 0,
  unreadFeedbackCount: 0,
  groups: [],
  clients: [],
  activities: [],
  loginEvents: [],
  feedbackEntries: [],
});

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const createEmptyUsageQuota = (): UsageQuota => ({
  percentUsed: 0,
  resetAt: null,
  source: 'unknown',
  label: null,
  configured: false,
});

const normalizeUsageQuota = (value: unknown): UsageQuota => {
  const fallback = createEmptyUsageQuota();
  if (!isObjectRecord(value)) {
    return fallback;
  }

  return {
    percentUsed: typeof value.percentUsed === 'number' ? value.percentUsed : fallback.percentUsed,
    resetAt: typeof value.resetAt === 'string' ? value.resetAt : null,
    source:
      value.source === 'manual' ||
      value.source === 'detected' ||
      value.source === 'live' ||
      value.source === 'unknown'
        ? value.source
        : fallback.source,
    label: typeof value.label === 'string' ? value.label : null,
    configured: typeof value.configured === 'boolean' ? value.configured : fallback.configured,
  };
};

const createEmptyLicenseSummary = (): LicenseSummary => ({
  status: 'inactive',
  licenseTypeLabel: '未授权',
  durationKey: null,
  expiresAt: null,
  permanent: false,
  slotLimit: null,
  slotLimitLabel: '未配置',
  source: 'local',
  sourceLabel: '本地',
  stageLabel: '未授权',
  remainingLabel: '未授权',
  activatedAt: null,
  lastValidatedAt: null,
  rollbackDetected: false,
  graceExpiresAt: null,
});

const normalizeLicenseSummary = (value: unknown): LicenseSummary => {
  const fallback = createEmptyLicenseSummary();
  if (!isObjectRecord(value)) {
    return fallback;
  }

  return {
    status:
      value.status === 'inactive' ||
      value.status === 'active' ||
      value.status === 'expired' ||
      value.status === 'grace'
        ? value.status
        : fallback.status,
    licenseTypeLabel:
      typeof value.licenseTypeLabel === 'string' ? value.licenseTypeLabel : fallback.licenseTypeLabel,
    durationKey:
      value.durationKey === 'one-day' ||
      value.durationKey === 'one-week' ||
      value.durationKey === 'half-month' ||
      value.durationKey === 'one-month' ||
      value.durationKey === 'three-months' ||
      value.durationKey === 'half-year' ||
      value.durationKey === 'one-year' ||
      value.durationKey === 'custom-days' ||
      value.durationKey === 'permanent' ||
      value.durationKey === 'invite-permanent' ||
      value.durationKey === null
        ? value.durationKey
        : fallback.durationKey,
    expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : null,
    permanent: typeof value.permanent === 'boolean' ? value.permanent : fallback.permanent,
    slotLimit:
      typeof value.slotLimit === 'number' || value.slotLimit === null ? value.slotLimit : fallback.slotLimit,
    slotLimitLabel: typeof value.slotLimitLabel === 'string' ? value.slotLimitLabel : fallback.slotLimitLabel,
    source:
      value.source === 'local' ||
      value.source === 'activation' ||
      value.source === 'invite' ||
      value.source === 'reward' ||
      value.source === 'trial'
        ? value.source
        : fallback.source,
    sourceLabel: typeof value.sourceLabel === 'string' ? value.sourceLabel : fallback.sourceLabel,
    stageLabel: typeof value.stageLabel === 'string' ? value.stageLabel : fallback.stageLabel,
    remainingLabel: typeof value.remainingLabel === 'string' ? value.remainingLabel : fallback.remainingLabel,
    activatedAt: typeof value.activatedAt === 'string' ? value.activatedAt : null,
    lastValidatedAt: typeof value.lastValidatedAt === 'string' ? value.lastValidatedAt : null,
    rollbackDetected:
      typeof value.rollbackDetected === 'boolean' ? value.rollbackDetected : fallback.rollbackDetected,
    graceExpiresAt: typeof value.graceExpiresAt === 'string' ? value.graceExpiresAt : null,
  };
};

const createUnmanagedRemoteUpdateChannel = (): LicenseHostRemoteUpdateChannelState => ({
  managed: false,
  channel: 'stable',
  allowPrerelease: false,
  allowEngineerAccess: false,
  testerId: null,
  updatedAt: null,
  updatedByHostId: null,
});

const normalizeRemoteUpdateChannel = (value: unknown): LicenseHostRemoteUpdateChannelState => {
  const fallback = createUnmanagedRemoteUpdateChannel();
  if (!isObjectRecord(value)) {
    return fallback;
  }

  return {
    managed: value.managed === true,
    channel:
      value.channel === 'beta' || value.channel === 'canary' ? value.channel : fallback.channel,
    allowPrerelease: value.allowPrerelease === true,
    allowEngineerAccess:
      (value.channel === 'beta' || value.channel === 'canary') &&
      value.allowEngineerAccess === true,
    testerId: typeof value.testerId === 'string' ? value.testerId : null,
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : null,
    updatedByHostId: typeof value.updatedByHostId === 'string' ? value.updatedByHostId : null,
  };
};

const withFeedbackActionTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMessage: string,
): Promise<T> => {
  let settled = false;
  return await new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error(timeoutMessage));
      }
    }, FEEDBACK_ACTION_TIMEOUT_MS);

    void promise.then(
      (value) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
};

const getAvatarFallbackText = (
  displayName?: string | null,
  email?: string | null,
  avatarText?: string | null,
): string => {
  const provided = typeof avatarText === 'string' ? avatarText.trim() : '';
  if (provided) {
    return provided.slice(0, 2).toUpperCase();
  }

  const source = (displayName?.trim() || email?.trim() || 'U').replace(/[^A-Za-z0-9\u4E00-\u9FFF]/g, '');
  return (source.slice(0, 2) || 'U').toUpperCase();
};

const orderDeliveryStatusLabel = (value?: string | null): string => {
  switch ((value || '').trim().toLowerCase()) {
    case 'pending':
      return '待处理';
    case 'fulfilled':
      return '已发货';
    case 'redeemed':
      return '已兑换';
    case 'failed':
      return '异常';
    case 'unused':
      return '未使用';
    case 'delivered':
      return '已分配';
    case 'online':
      return '在线';
    case 'warning':
      return '告警';
    case 'disabled':
      return '停用';
    case 'idle':
      return '待命';
    default:
      return value?.trim() || '未设置';
  }
};

const getOrderDeliveryCodeOwnerLabel = (
  code: XianyuManagementSnapshot['recentCodes'][number],
): string =>
  code.redeemedByDisplayId ||
  code.redeemedByEmail ||
  code.redeemedByDisplayName ||
  code.buyerName ||
  '未绑定账号';

const getOrderDeliveryCodeOwnerMeta = (
  code: XianyuManagementSnapshot['recentCodes'][number],
): string => {
  if (code.redeemedByDisplayId || code.redeemedByEmail || code.redeemedByDisplayName) {
    return '已绑定账号';
  }

  if (code.buyerName) {
    return '已分配对象';
  }

  return '待绑定';
};

const isOrderDeliveryCodeDeletable = (
  code: XianyuManagementSnapshot['recentCodes'][number],
): boolean => (code.status || '').trim().toLowerCase() === 'unused';

const orderDeliveryStatusTone = (value?: string | null): 'ready' | 'active' | 'disabled' => {
  switch ((value || '').trim().toLowerCase()) {
    case 'fulfilled':
    case 'redeemed':
    case 'delivered':
    case 'online':
      return 'active';
    case 'failed':
    case 'disabled':
      return 'disabled';
    default:
      return 'ready';
  }
};

const getOrderDeliveryCodeChannelLabel = (value: XianyuCodeChannel): string =>
  ORDER_DELIVERY_CODE_CHANNEL_OPTIONS.find((item) => item.value === value)?.label ?? '普通用户';

const orderDeliveryMatchesQuery = (keyword: string, ...values: Array<string | null | undefined>): boolean => {
  if (!keyword) {
    return true;
  }

  return values.some((value) => value?.toLowerCase().includes(keyword));
};

const normalizeRemoteProfileSecurityBinding = (
  value: unknown,
): LicenseHostRemoteProfileSnapshot['securityBinding'] => {
  if (value === null) {
    return null;
  }
  if (!isObjectRecord(value)) {
    return undefined;
  }

  const importSource =
    value.importSource === 'cloud-backup'
      ? 'cloud-backup'
      : value.importSource === 'local'
        ? 'local'
        : 'transfer';
  const boundDeviceId =
    typeof value.boundDeviceId === 'string' && value.boundDeviceId.trim().length > 0
      ? value.boundDeviceId
      : null;
  const boundFingerprintSummary =
    typeof value.boundFingerprintSummary === 'string' && value.boundFingerprintSummary.trim().length > 0
      ? value.boundFingerprintSummary
      : null;
  if (!boundDeviceId || !boundFingerprintSummary) {
    return null;
  }

    return {
      importSource,
      ownerUserId: typeof value.ownerUserId === 'string' ? value.ownerUserId : null,
      ownerUserEmail: typeof value.ownerUserEmail === 'string' ? value.ownerUserEmail : null,
      boundDeviceId,
      boundFingerprintSummary,
      senderHostLabel: typeof value.senderHostLabel === 'string' ? value.senderHostLabel : null,
      createdAt: typeof value.createdAt === 'string' && value.createdAt.trim().length > 0
        ? value.createdAt
        : new Date().toISOString(),
      integrityState:
        value.integrityState === 'tampered'
          ? 'tampered'
          : value.integrityState === 'verified'
            ? 'verified'
            : 'legacy',
    };
  };

const normalizeRemoteProfileSnapshots = (value: unknown): LicenseHostRemoteProfileSnapshot[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index): LicenseHostRemoteProfileSnapshot | null => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      const id =
        typeof entry.id === 'string' && entry.id.trim().length > 0
          ? entry.id
          : `remote-snapshot-${index + 1}`;
      const name =
        typeof entry.name === 'string' && entry.name.trim().length > 0
          ? entry.name
          : `未命名空间 ${index + 1}`;

      const snapshot: LicenseHostRemoteProfileSnapshot = {
        id,
        name,
        workspaceName: typeof entry.workspaceName === 'string' ? entry.workspaceName : null,
        notes: typeof entry.notes === 'string' ? entry.notes : '',
        planType:
          entry.planType === 'Unknown' ||
          entry.planType === 'Free' ||
          entry.planType === 'Plus' ||
          entry.planType === 'Pro 5x' ||
          entry.planType === 'Pro 20x' ||
          entry.planType === 'Team' ||
          entry.planType === 'Business'
            ? entry.planType
            : 'Unknown',
        manuallyDisabled: Boolean(entry.manuallyDisabled),
        isActive: Boolean(entry.isActive),
        authStatus:
          entry.authStatus === 'ok' ||
          entry.authStatus === 'missing' ||
          entry.authStatus === 'path-error'
            ? entry.authStatus
            : 'missing',
        overallRemainingPercent:
          typeof entry.overallRemainingPercent === 'number' ? entry.overallRemainingPercent : null,
        weeklyQuota: normalizeUsageQuota(entry.weeklyQuota),
        fiveHourQuota: normalizeUsageQuota(entry.fiveHourQuota),
        reviewQuota: normalizeUsageQuota(entry.reviewQuota),
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
        updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : '',
        usageUpdatedAt: typeof entry.usageUpdatedAt === 'string' ? entry.usageUpdatedAt : null,
        usageError: typeof entry.usageError === 'string' ? entry.usageError : null,
        lastActivatedAt: typeof entry.lastActivatedAt === 'string' ? entry.lastActivatedAt : null,
      };

      if (isObjectRecord(entry.securityBinding) || entry.securityBinding === null) {
        snapshot.securityBinding = normalizeRemoteProfileSecurityBinding(entry.securityBinding);
      }

      if (isObjectRecord(entry.transferPackage) || entry.transferPackage === null) {
        snapshot.transferPackage =
          entry.transferPackage as LicenseHostRemoteProfileSnapshot['transferPackage'];
      }

      return snapshot;
    })
    .filter((entry): entry is LicenseHostRemoteProfileSnapshot => entry !== null);
};

const normalizeRemoteAccessBadge = (value: unknown): RemoteAccountAccessBadge => {
  if (!isObjectRecord(value)) {
    return {
      kind: 'inactive',
      label: '未授权',
      emphasized: false,
    };
  }

  const kind =
    value.kind === 'blocked' ||
    value.kind === 'pending' ||
    value.kind === 'manual-grant' ||
    value.kind === 'member-auto' ||
    value.kind === 'trial' ||
    value.kind === 'invite' ||
    value.kind === 'reward' ||
    value.kind === 'inactive'
      ? value.kind
      : 'inactive';

  return {
    kind,
    label:
      typeof value.label === 'string' && value.label.trim().length > 0
        ? value.label
        : kind === 'pending'
          ? '待处理'
          : '未授权',
    emphasized: Boolean(value.emphasized),
  };
};

const normalizeFeedbackEntries = (value: unknown): FeedbackEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      const feedbackId =
        typeof entry.id === 'string' && entry.id.trim().length > 0 ? entry.id : `feedback-${index + 1}`;
      const senderDisplayName =
        typeof entry.senderDisplayName === 'string' ? entry.senderDisplayName : null;
      const senderEmail = typeof entry.senderEmail === 'string' ? entry.senderEmail : null;
      const senderDeviceId = typeof entry.senderDeviceId === 'string' ? entry.senderDeviceId : null;
      const acknowledgementMessage =
        typeof entry.acknowledgementMessage === 'string' ? entry.acknowledgementMessage : null;
      const messages = Array.isArray(entry.messages)
        ? entry.messages
            .map((message, messageIndex) => {
              if (!isObjectRecord(message)) {
                return null;
              }

              return {
                id:
                  typeof message.id === 'string' && message.id.trim().length > 0
                    ? message.id
                    : `${feedbackId}-message-${messageIndex + 1}`,
                feedbackId:
                  typeof message.feedbackId === 'string' && message.feedbackId.trim().length > 0
                    ? message.feedbackId
                    : feedbackId,
                sender: message.sender === 'host' ? 'host' : 'user',
                body: typeof message.body === 'string' ? message.body : '',
                createdAt: typeof message.createdAt === 'string' ? message.createdAt : '',
                senderDisplayName:
                  typeof message.senderDisplayName === 'string'
                    ? message.senderDisplayName
                    : message.sender === 'host'
                      ? FEEDBACK_SERVICE_DISPLAY_NAME
                      : senderDisplayName,
                senderEmail:
                  typeof message.senderEmail === 'string'
                    ? message.senderEmail
                    : message.sender === 'host'
                      ? null
                      : senderEmail,
                senderDeviceId:
                  typeof message.senderDeviceId === 'string'
                    ? message.senderDeviceId
                    : message.sender === 'host'
                      ? null
                      : senderDeviceId,
              } satisfies FeedbackConversationMessage;
            })
            .filter((message): message is FeedbackConversationMessage => message !== null)
        : [];
      const normalizedMessages =
        messages.length > 0
          ? messages
          : [
              {
                id: `${feedbackId}-user`,
                feedbackId,
                sender: 'user',
                body: typeof entry.body === 'string' ? entry.body : '',
                createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
                senderDisplayName,
                senderEmail,
                senderDeviceId,
              } satisfies FeedbackConversationMessage,
              ...(acknowledgementMessage
                ? ([
                    {
                      id: `${feedbackId}-host`,
                      feedbackId,
                      sender: 'host',
                      body: acknowledgementMessage,
                      createdAt:
                        typeof entry.acknowledgedAt === 'string'
                          ? entry.acknowledgedAt
                          : typeof entry.readAt === 'string'
                            ? entry.readAt
                            : typeof entry.createdAt === 'string'
                              ? entry.createdAt
                              : '',
                      senderDisplayName: FEEDBACK_SERVICE_DISPLAY_NAME,
                      senderEmail: null,
                      senderDeviceId: null,
                    } satisfies FeedbackConversationMessage,
                  ] as FeedbackConversationMessage[])
                : []),
            ];
      const latestMessage = normalizedMessages[normalizedMessages.length - 1] ?? null;

      return {
        id: feedbackId,
        body: typeof entry.body === 'string' ? entry.body : '',
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
        status:
          entry.status === 'pending' || entry.status === 'read' || entry.status === 'acknowledged'
            ? entry.status
            : 'pending',
        readAt: typeof entry.readAt === 'string' ? entry.readAt : null,
        userReadAt: typeof entry.userReadAt === 'string' ? entry.userReadAt : null,
        acknowledgedAt: typeof entry.acknowledgedAt === 'string' ? entry.acknowledgedAt : null,
        acknowledgementMessage,
        senderUserId: typeof entry.senderUserId === 'string' ? entry.senderUserId : null,
        senderDisplayName,
        senderEmail,
        senderDeviceId,
        senderFingerprintSummary:
          typeof entry.senderFingerprintSummary === 'string' ? entry.senderFingerprintSummary : null,
        senderAvatarText: typeof entry.senderAvatarText === 'string' ? entry.senderAvatarText : null,
        senderAvatarImageDataUrl:
          typeof entry.senderAvatarImageDataUrl === 'string'
            ? entry.senderAvatarImageDataUrl
            : null,
        previewText:
          typeof entry.previewText === 'string' && entry.previewText.trim().length > 0
            ? entry.previewText
            : latestMessage?.body ?? (typeof entry.body === 'string' ? entry.body : ''),
        latestMessageAt:
          typeof entry.latestMessageAt === 'string' && entry.latestMessageAt.trim().length > 0
            ? entry.latestMessageAt
            : latestMessage?.createdAt ?? (typeof entry.createdAt === 'string' ? entry.createdAt : ''),
        messages: normalizedMessages,
      } satisfies FeedbackEntry;
    })
    .filter((entry): entry is FeedbackEntry => entry !== null);
};

const normalizeRemoteActivities = (value: unknown): LicenseHostActivityRecord[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      return {
        id:
          typeof entry.id === 'string' && entry.id.trim().length > 0
            ? entry.id
            : `remote-activity-${index + 1}`,
        eventType:
          entry.eventType === 'register' ||
          entry.eventType === 'login' ||
          entry.eventType === 'logout' ||
          entry.eventType === 'feedback' ||
          entry.eventType === 'activation' ||
          entry.eventType === 'invite-request' ||
          entry.eventType === 'invite-redeem' ||
          entry.eventType === 'invite-claim' ||
          entry.eventType === 'reward-apply' ||
          entry.eventType === 'remote-license-issue' ||
          entry.eventType === 'remote-update-channel' ||
          entry.eventType === 'remote-license-revoke' ||
          entry.eventType === 'remote-account-restriction' ||
          entry.eventType === 'remote-account-restriction-release' ||
          entry.eventType === 'remote-password-reset' ||
          entry.eventType === 'remote-secondary-password-reset' ||
          entry.eventType === 'profile-transfer'
            ? entry.eventType
            : 'login',
        eventLabel: typeof entry.eventLabel === 'string' ? entry.eventLabel : '远程活动',
        summary: typeof entry.summary === 'string' ? entry.summary : '未记录活动摘要。',
        detail: typeof entry.detail === 'string' ? entry.detail : null,
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
        userId: typeof entry.userId === 'string' ? entry.userId : null,
        displayName: typeof entry.displayName === 'string' ? entry.displayName : null,
        email: typeof entry.email === 'string' ? entry.email : null,
        deviceId: typeof entry.deviceId === 'string' ? entry.deviceId : null,
        fingerprintSummary:
          typeof entry.fingerprintSummary === 'string' ? entry.fingerprintSummary : null,
        unread: entry.unread !== false,
        readAt: typeof entry.readAt === 'string' ? entry.readAt : null,
      } satisfies LicenseHostActivityRecord;
    })
    .filter((entry): entry is LicenseHostActivityRecord => entry !== null);
};

const normalizeRemoteLoginEvents = (value: unknown): LicenseHostLoginEvent[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      return {
        id:
          typeof entry.id === 'string' && entry.id.trim().length > 0
            ? entry.id
            : `remote-login-event-${index + 1}`,
        activityId:
          typeof entry.activityId === 'string' && entry.activityId.trim().length > 0
            ? entry.activityId
            : `remote-login-activity-${index + 1}`,
        eventType: entry.eventType === 'register' ? 'register' : 'login',
        eventLabel: typeof entry.eventLabel === 'string' ? entry.eventLabel : '新登录',
        summary: typeof entry.summary === 'string' ? entry.summary : '检测到新的远程登录事件。',
        detail: typeof entry.detail === 'string' ? entry.detail : null,
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
        userId: typeof entry.userId === 'string' ? entry.userId : null,
        displayName: typeof entry.displayName === 'string' ? entry.displayName : null,
        email: typeof entry.email === 'string' ? entry.email : null,
        deviceId: typeof entry.deviceId === 'string' ? entry.deviceId : null,
        fingerprintHash: typeof entry.fingerprintHash === 'string' ? entry.fingerprintHash : null,
        fingerprintSummary:
          typeof entry.fingerprintSummary === 'string' ? entry.fingerprintSummary : null,
        unread: entry.unread !== false,
        readAt: typeof entry.readAt === 'string' ? entry.readAt : null,
        accessBadge: normalizeRemoteAccessBadge(entry.accessBadge),
        groupId: typeof entry.groupId === 'string' ? entry.groupId : null,
        groupTitle: typeof entry.groupTitle === 'string' ? entry.groupTitle : null,
      } satisfies LicenseHostLoginEvent;
    })
    .filter((entry): entry is LicenseHostLoginEvent => entry !== null);
};

const normalizeRemoteClients = (value: unknown): LicenseHostRemoteClient[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      const deviceId =
        typeof entry.deviceId === 'string' && entry.deviceId.trim().length > 0
          ? entry.deviceId
          : `DEV-REMOTE-${index + 1}`;
      const fingerprintHash =
        typeof entry.fingerprintHash === 'string' && entry.fingerprintHash.trim().length > 0
          ? entry.fingerprintHash
          : deviceId;
      const profileSnapshots = normalizeRemoteProfileSnapshots(entry.profileSnapshots);

      return {
        deviceId,
        userId: typeof entry.userId === 'string' ? entry.userId : null,
        displayName: typeof entry.displayName === 'string' ? entry.displayName : null,
        email: typeof entry.email === 'string' ? entry.email : null,
        fingerprintHash,
        fingerprintSummary:
          typeof entry.fingerprintSummary === 'string' && entry.fingerprintSummary.trim().length > 0
            ? entry.fingerprintSummary
            : '未记录指纹',
        hostLabel: typeof entry.hostLabel === 'string' ? entry.hostLabel : 'unknown',
        platformLabel: typeof entry.platformLabel === 'string' ? entry.platformLabel : 'Unknown',
        machineName: typeof entry.machineName === 'string' ? entry.machineName : deviceId,
        clientVersion: typeof entry.clientVersion === 'string' ? entry.clientVersion : null,
        firstSeenAt: typeof entry.firstSeenAt === 'string' ? entry.firstSeenAt : '',
        lastSeenAt: typeof entry.lastSeenAt === 'string' ? entry.lastSeenAt : '',
        presence:
          entry.presence === 'online' || entry.presence === 'recent' || entry.presence === 'offline'
            ? entry.presence
            : 'offline',
        effectiveLicense: normalizeLicenseSummary(entry.effectiveLicense),
        accessBadge: normalizeRemoteAccessBadge(entry.accessBadge),
        accountRestrictionUntil:
          typeof entry.accountRestrictionUntil === 'string' ? entry.accountRestrictionUntil : null,
        accountRestrictionReason:
          typeof entry.accountRestrictionReason === 'string' ? entry.accountRestrictionReason : null,
        displayId: typeof entry.displayId === 'string' ? entry.displayId : null,
        licenseScope:
          entry.licenseScope === 'device' ||
          entry.licenseScope === 'account' ||
          entry.licenseScope === 'none'
            ? entry.licenseScope
            : 'none',
        canIssueRemotely: Boolean(entry.canIssueRemotely),
        remoteUpdateChannel: normalizeRemoteUpdateChannel(entry.remoteUpdateChannel),
        profileSnapshots,
        profileSnapshotCount:
          typeof entry.profileSnapshotCount === 'number'
            ? entry.profileSnapshotCount
            : profileSnapshots.length,
        profileSnapshotUpdatedAt:
          typeof entry.profileSnapshotUpdatedAt === 'string' ? entry.profileSnapshotUpdatedAt : null,
      } satisfies LicenseHostRemoteClient;
    })
    .filter((entry): entry is LicenseHostRemoteClient => entry !== null);
};

const normalizeRemoteClientGroups = (value: unknown): LicenseHostRemoteClientGroup[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isObjectRecord(entry)) {
        return null;
      }

      const devices = normalizeRemoteClients(entry.devices);
      const primaryClient =
        (isObjectRecord(entry.primaryClient)
          ? normalizeRemoteClients([entry.primaryClient])[0]
          : null) ?? devices[0] ?? null;

      if (!primaryClient) {
        return null;
      }

      return {
        id:
          typeof entry.id === 'string' && entry.id.trim().length > 0
            ? entry.id
            : primaryClient.fingerprintHash || `remote-group-${index + 1}`,
        title:
          typeof entry.title === 'string' && entry.title.trim().length > 0
            ? entry.title
            : getRemoteClientTitle(primaryClient),
        displayName: typeof entry.displayName === 'string' ? entry.displayName : primaryClient.displayName,
        email: typeof entry.email === 'string' ? entry.email : primaryClient.email,
        displayId: typeof entry.displayId === 'string' ? entry.displayId : primaryClient.displayId,
        userId: typeof entry.userId === 'string' ? entry.userId : primaryClient.userId,
        fingerprintHash:
          typeof entry.fingerprintHash === 'string' && entry.fingerprintHash.trim().length > 0
            ? entry.fingerprintHash
            : primaryClient.fingerprintHash,
        fingerprintSummary:
          typeof entry.fingerprintSummary === 'string' && entry.fingerprintSummary.trim().length > 0
            ? entry.fingerprintSummary
            : primaryClient.fingerprintSummary,
        deviceCount: typeof entry.deviceCount === 'number' ? entry.deviceCount : Math.max(devices.length, 1),
        unreadLoginCount:
          typeof entry.unreadLoginCount === 'number'
            ? entry.unreadLoginCount
            : normalizeRemoteActivities(entry.recentActivities).filter(
                (activity) =>
                  (activity.eventType === 'login' || activity.eventType === 'register') && activity.unread,
              ).length,
        latestLoginAt: typeof entry.latestLoginAt === 'string' ? entry.latestLoginAt : null,
        primaryClient,
        devices: devices.length > 0 ? devices : [primaryClient],
        recentActivities: normalizeRemoteActivities(entry.recentActivities),
      } satisfies LicenseHostRemoteClientGroup;
    })
    .filter((entry): entry is LicenseHostRemoteClientGroup => entry !== null);
};

const normalizeRemoteDashboard = (
  value: unknown,
  fallbackSourceUrl: string | null = null,
  fallbackQuery = '',
): LicenseHostRemoteDashboard => {
  const fallback = createEmptyRemoteDashboard();
  if (!isObjectRecord(value)) {
    return {
      ...fallback,
      sourceUrl: fallbackSourceUrl,
      query: fallbackQuery,
    };
  }

  const clients = normalizeRemoteClients(value.clients);
  const activities = normalizeRemoteActivities(value.activities);
  const loginEvents = normalizeRemoteLoginEvents(value.loginEvents);
  const groups = normalizeRemoteClientGroups(value.groups);
  const feedbackEntries = normalizeFeedbackEntries(value.feedbackEntries);
  const onlineClients = clients.filter((item) => item.presence === 'online').length;
  const recentClients = clients.filter((item) => item.presence === 'recent').length;
  const offlineClients = clients.filter((item) => item.presence === 'offline').length;
  const onlineGroups = groups.filter((item) => item.primaryClient.presence === 'online').length;
  const recentGroups = groups.filter((item) => item.primaryClient.presence === 'recent').length;
  const offlineGroups = groups.filter((item) => item.primaryClient.presence === 'offline').length;

  return {
    connected: Boolean(value.connected),
    checkedAt: typeof value.checkedAt === 'string' ? value.checkedAt : null,
    sourceUrl: typeof value.sourceUrl === 'string' ? value.sourceUrl : fallbackSourceUrl,
    query: typeof value.query === 'string' ? value.query : fallbackQuery,
    totalGroups: typeof value.totalGroups === 'number' ? value.totalGroups : groups.length,
    onlineGroups: typeof value.onlineGroups === 'number' ? value.onlineGroups : onlineGroups,
    recentGroups: typeof value.recentGroups === 'number' ? value.recentGroups : recentGroups,
    offlineGroups: typeof value.offlineGroups === 'number' ? value.offlineGroups : offlineGroups,
    totalClients: typeof value.totalClients === 'number' ? value.totalClients : clients.length,
    onlineClients: typeof value.onlineClients === 'number' ? value.onlineClients : onlineClients,
    recentClients: typeof value.recentClients === 'number' ? value.recentClients : recentClients,
    offlineClients: typeof value.offlineClients === 'number' ? value.offlineClients : offlineClients,
    unreadLoginCount:
      typeof value.unreadLoginCount === 'number'
        ? value.unreadLoginCount
        : loginEvents.length > 0
          ? loginEvents.filter((event) => event.unread).length
          : activities.filter(
              (activity) =>
                (activity.eventType === 'login' || activity.eventType === 'register') && activity.unread,
            ).length,
    unreadFeedbackCount:
      typeof value.unreadFeedbackCount === 'number'
        ? value.unreadFeedbackCount
        : feedbackEntries.filter((entry) => entry.status === 'pending').length,
    groups,
    clients,
    activities,
    loginEvents,
    feedbackEntries,
  };
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatFullDateTime = (value: string | null): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const escapeSpreadsheetHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const downloadSpreadsheetFile = (filename: string, html: string) => {
  const blob = new Blob(['\ufeff', html], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
};

const buildOrderDeliveryInventoryExportHtml = (params: {
  consoleUrl: string;
  exportedAt: string;
  query: string;
  scopeLabel: string;
  codes: XianyuManagementSnapshot['recentCodes'];
}): string => {
  const rows = params.codes
    .map((code, index) => {
      const ownerLabel = getOrderDeliveryCodeOwnerLabel(code);
      const ownerMeta = getOrderDeliveryCodeOwnerMeta(code);
      return `
        <tr>
          <td>${index + 1}</td>
          <td class="code-cell">${escapeSpreadsheetHtml(code.codeValue ?? code.codePreview)}</td>
          <td>${escapeSpreadsheetHtml(orderDeliveryStatusLabel(code.status))}</td>
          <td>${escapeSpreadsheetHtml(code.tierTitle ?? '未命名规格')}</td>
          <td>${escapeSpreadsheetHtml(code.batchId)}</td>
          <td>${escapeSpreadsheetHtml(ownerMeta)}</td>
          <td>${escapeSpreadsheetHtml(ownerLabel)}</td>
          <td>${escapeSpreadsheetHtml(code.orderId ?? '未记录')}</td>
          <td>${escapeSpreadsheetHtml(formatFullDateTime(code.createdAt))}</td>
          <td>${escapeSpreadsheetHtml(formatFullDateTime(code.deliveredAt))}</td>
          <td>${escapeSpreadsheetHtml(formatFullDateTime(code.redeemedAt))}</td>
        </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        font-family: "Microsoft YaHei", "Segoe UI", sans-serif;
        margin: 20px;
        color: #0f172a;
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      th,
      td {
        border: 1px solid #dbe3f0;
        padding: 8px 10px;
        vertical-align: top;
        text-align: left;
        font-size: 12px;
      }
      th {
        background: #eef4ff;
        font-weight: 700;
      }
      .title {
        background: #dbeafe;
        font-size: 18px;
        font-weight: 700;
      }
      .meta-label {
        width: 140px;
        background: #f8fafc;
        font-weight: 700;
      }
      .code-cell {
        font-family: "Consolas", "Courier New", monospace;
        white-space: nowrap;
        mso-number-format: "\\@";
      }
    </style>
  </head>
  <body>
    <table>
      <tr>
        <td class="title" colspan="11">AINIVOX 授权码库存导出</td>
      </tr>
      <tr>
        <td class="meta-label">控制台网址</td>
        <td colspan="10">${escapeSpreadsheetHtml(params.consoleUrl)}</td>
      </tr>
      <tr>
        <td class="meta-label">导出时间</td>
        <td colspan="10">${escapeSpreadsheetHtml(params.exportedAt)}</td>
      </tr>
      <tr>
        <td class="meta-label">导出范围</td>
        <td colspan="10">${escapeSpreadsheetHtml(params.scopeLabel)}</td>
      </tr>
      <tr>
        <td class="meta-label">搜索关键词</td>
        <td colspan="10">${escapeSpreadsheetHtml(params.query || '全部')}</td>
      </tr>
      <tr>
        <th>序号</th>
        <th>授权码</th>
        <th>状态</th>
        <th>规格</th>
        <th>批次</th>
        <th>归属状态</th>
        <th>账号信息</th>
        <th>订单编号</th>
        <th>生成时间</th>
        <th>分配时间</th>
        <th>绑定时间</th>
      </tr>
      ${rows}
    </table>
  </body>
</html>`;
};

const buildCompactPageItems = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_item, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage]);
  for (let offset = -1; offset <= 1; offset += 1) {
    const page = currentPage + offset;
    if (page > 1 && page < totalPages) {
      pages.add(page);
    }
  }

  if (currentPage <= 3) {
    [2, 3, 4, 5].forEach((page) => pages.add(page));
  }

  if (currentPage >= totalPages - 2) {
    [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1]
      .filter((page) => page > 1)
      .forEach((page) => pages.add(page));
  }

  const sortedPages = Array.from(pages).sort((left, right) => left - right);
  const items: Array<number | 'ellipsis'> = [];
  let previousPage = 0;
  sortedPages.forEach((page) => {
    if (previousPage && page - previousPage > 1) {
      items.push('ellipsis');
    }
    items.push(page);
    previousPage = page;
  });
  return items;
};

const getRemotePresenceLabel = (presence: LicenseHostRemoteClient['presence']): string => {
  if (presence === 'online') {
    return '在线';
  }
  if (presence === 'recent') {
    return '最近活跃';
  }
  return '离线';
};

const getRemoteClientTitle = (client: LicenseHostRemoteClient): string =>
  client.displayId || client.displayName || client.email || client.machineName || client.deviceId;

const getRemoteGroupAccountLabel = (group: LicenseHostRemoteClientGroup): string =>
  [group.displayId, group.email || group.displayName || null].filter(Boolean).join(' · ') ||
  '未绑定账号，先按机器指纹归组';

const stringMatchesRemoteQuery = (value: string | null | undefined, keyword: string): boolean =>
  typeof value === 'string' && value.toLowerCase().includes(keyword);

const remoteGroupMatchesQuery = (group: LicenseHostRemoteClientGroup, query: string): boolean => {
  const keyword = query.trim().toLowerCase();
  if (!keyword) {
    return true;
  }

  const primaryClient = group.primaryClient;
  const directFields = [
    group.title,
    group.displayName,
    group.email,
    group.displayId,
    group.userId,
    group.fingerprintSummary,
    group.fingerprintHash,
    getRemoteGroupAccountLabel(group),
    primaryClient.deviceId,
    primaryClient.displayId,
    primaryClient.displayName,
    primaryClient.email,
    primaryClient.userId,
    primaryClient.machineName,
    primaryClient.fingerprintSummary,
    primaryClient.fingerprintHash,
  ];

  if (directFields.some((value) => stringMatchesRemoteQuery(value, keyword))) {
    return true;
  }

  return group.devices.some((device) =>
    [
      device.deviceId,
      device.displayName,
      device.email,
      device.userId,
      device.machineName,
      device.fingerprintSummary,
      device.fingerprintHash,
    ].some((value) => stringMatchesRemoteQuery(value, keyword)),
  );
};

const getRemoteLoginEventAccountLabel = (event: LicenseHostLoginEvent): string =>
  event.email || event.displayName || event.deviceId || '未绑定账号';

const getFeedbackEntryActivityTimestamp = (
  entry: Pick<FeedbackEntry, 'latestMessageAt' | 'createdAt'>,
): number => {
  const activityAt = entry.latestMessageAt || entry.createdAt;
  const timestamp = Date.parse(activityAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const feedbackEntryMatchesQuery = (entry: FeedbackEntry, query: string): boolean => {
  const keyword = query.trim().toLowerCase();
  if (!keyword) {
    return true;
  }

  return [
    entry.senderDisplayName,
    entry.senderEmail,
    entry.senderDeviceId,
    entry.senderFingerprintSummary,
    entry.previewText,
    entry.body,
    ...entry.messages.map((message) => message.body),
  ].some((value) => stringMatchesRemoteQuery(value, keyword));
};

const getRemoteUpdateChannelLabel = (state: LicenseHostRemoteUpdateChannelState): string => {
  if (state.channel === 'beta') {
    return state.allowEngineerAccess ? '高级内测通道' : '内测通道';
  }
  if (state.channel === 'canary') {
    return '测试通道';
  }
  return '正式通道';
};

const getRemoteAccessBadgeTone = (badge: RemoteAccountAccessBadge): string => {
  switch (badge.kind) {
    case 'blocked':
      return 'pending';
    case 'pending':
      return 'pending';
    case 'manual-grant':
      return 'manual';
    case 'member-auto':
      return 'member';
    case 'trial':
      return 'trial';
    case 'invite':
      return 'invite';
    case 'reward':
      return 'reward';
    case 'inactive':
    default:
      return 'inactive';
  }
};

const remoteLoginEventMatchesGroup = (
  event: Pick<LicenseHostLoginEvent, 'groupId' | 'deviceId' | 'fingerprintHash' | 'userId'>,
  group: LicenseHostRemoteClientGroup,
): boolean => {
  if (event.groupId && event.groupId === group.id) {
    return true;
  }

  if (event.deviceId && group.devices.some((device) => device.deviceId === event.deviceId)) {
    return true;
  }

  if (event.fingerprintHash && group.devices.some((device) => device.fingerprintHash === event.fingerprintHash)) {
    return true;
  }

  return Boolean(event.userId && group.userId && event.userId === group.userId);
};

const feedbackEntryMatchesGroup = (
  entry: Pick<
    FeedbackEntry,
    'senderUserId' | 'senderEmail' | 'senderDeviceId' | 'senderFingerprintSummary'
  >,
  group: LicenseHostRemoteClientGroup,
): boolean => {
  if (entry.senderUserId && group.userId && entry.senderUserId === group.userId) {
    return true;
  }

  if (entry.senderEmail && group.email && entry.senderEmail === group.email) {
    return true;
  }

  if (entry.senderDeviceId && group.devices.some((device) => device.deviceId === entry.senderDeviceId)) {
    return true;
  }

  return Boolean(
    entry.senderFingerprintSummary && entry.senderFingerprintSummary === group.fingerprintSummary,
  );
};

const buildRemoteClientGroupsLocal = (
  clients: LicenseHostRemoteClient[],
  activities: LicenseHostActivityRecord[],
): LicenseHostRemoteClientGroup[] => {
  const grouped = new Map<string, LicenseHostRemoteClient[]>();

  for (const client of clients) {
    const key = client.fingerprintHash || `device:${client.deviceId}`;
    const bucket = grouped.get(key);
    if (bucket) {
      bucket.push(client);
      continue;
    }
    grouped.set(key, [client]);
  }

  return Array.from(grouped.entries())
    .map(([id, bucket]) => {
      const devices = [...bucket].sort((left, right) => right.lastSeenAt.localeCompare(left.lastSeenAt));
      const primaryClient = devices[0];
      const deviceIds = new Set(devices.map((item) => item.deviceId));
      const fingerprintSummaries = new Set(devices.map((item) => item.fingerprintSummary));
      const recentActivities = activities
        .filter(
          (activity) =>
            (activity.deviceId ? deviceIds.has(activity.deviceId) : false) ||
            (activity.fingerprintSummary ? fingerprintSummaries.has(activity.fingerprintSummary) : false),
        )
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
        .slice(0, 6);
      const loginActivities = recentActivities.filter(
        (activity) => activity.eventType === 'login' || activity.eventType === 'register',
      );

      return {
        id,
        title: getRemoteClientTitle(primaryClient),
        displayName: primaryClient.displayName,
        email: primaryClient.email,
        displayId: primaryClient.displayId,
        userId: primaryClient.userId,
        fingerprintHash: primaryClient.fingerprintHash,
        fingerprintSummary: primaryClient.fingerprintSummary,
        deviceCount: devices.length,
        unreadLoginCount: loginActivities.filter((activity) => activity.unread).length,
        latestLoginAt: loginActivities[0]?.createdAt ?? null,
        primaryClient,
        devices,
        recentActivities,
      };
    })
    .sort((left, right) => right.primaryClient.lastSeenAt.localeCompare(left.primaryClient.lastSeenAt));
};

const copyText = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text);
};

const getDefaultSlotLimitByDuration = (durationKey: HostLicenseDurationKey): number | null => {
  switch (durationKey) {
    case 'one-day':
      return 1;
    case 'one-week':
    case 'half-month':
      return 2;
    case 'one-month':
    case 'one-year':
    case 'half-year':
    case 'custom-days':
    case 'permanent':
    default:
      return 4;
  }
};

const formatHostSlotLimitLabel = (slotLimit: number | null): string =>
  slotLimit === null ? '无限卡片' : `${slotLimit} 张卡片`;

const MAX_SLOT_LIMIT = 25_000;
const REMOTE_CLIENT_QUERY_LIMIT = 40;
const REMOTE_ACTIVITY_QUERY_LIMIT = 40;
const REMOTE_GRANT_PRESETS_STORAGE_KEY = 'license-host-remote-grant-presets-v1';

interface RemoteGrantPreset {
  durationKey: HostLicenseDurationKey;
  customDurationDaysInput: string;
  slotLimitInput: string;
  betaAccess: boolean;
  advancedBetaAccess: boolean;
  note: string;
}

const sanitizeRemoteGrantPreset = (value: unknown): RemoteGrantPreset | null => {
  if (!isObjectRecord(value)) {
    return null;
  }

  return {
    durationKey:
      value.durationKey === 'one-day' ||
      value.durationKey === 'one-week' ||
      value.durationKey === 'half-month' ||
      value.durationKey === 'one-month' ||
      value.durationKey === 'half-year' ||
      value.durationKey === 'one-year' ||
      value.durationKey === 'custom-days' ||
      value.durationKey === 'permanent'
        ? value.durationKey
        : 'permanent',
    customDurationDaysInput:
      typeof value.customDurationDaysInput === 'string' ? value.customDurationDaysInput : '',
    slotLimitInput: typeof value.slotLimitInput === 'string' ? value.slotLimitInput : '',
    betaAccess: Boolean(value.betaAccess),
    advancedBetaAccess: Boolean(value.advancedBetaAccess),
    note: typeof value.note === 'string' ? value.note : '',
  };
};

const loadRemoteGrantPresetMap = (): Record<string, RemoteGrantPreset> => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(REMOTE_GRANT_PRESETS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, RemoteGrantPreset>>((accumulator, [key, value]) => {
      const preset = sanitizeRemoteGrantPreset(value);
      if (preset) {
        accumulator[key] = preset;
      }
      return accumulator;
    }, {});
  } catch {
    return {};
  }
};

const persistRemoteGrantPresetMap = (value: Record<string, RemoteGrantPreset>): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(REMOTE_GRANT_PRESETS_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore localStorage quota or privacy mode failures.
  }
};

const buildRemoteGrantPresetKey = (
  group: Pick<LicenseHostRemoteClientGroup, 'userId' | 'fingerprintHash'> | null | undefined,
): string | null => {
  if (!group) {
    return null;
  }
  if (group.userId) {
    return `user:${group.userId}`;
  }
  return group.fingerprintHash ? `fingerprint:${group.fingerprintHash}` : null;
};

const getPlanLabel = (planType: ProfileSummary['planType']): string => planLabelMap[planType] ?? planType;

const parseSlotLimitInput = (rawValue: string): number | null | undefined => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalized = trimmed.toLowerCase();
  if (
    trimmed === '无限' ||
    normalized === 'unlimited' ||
    normalized === 'inf' ||
    normalized === 'max' ||
    normalized === 'null'
  ) {
    return null;
  }

  if (!/^\d{1,5}$/.test(trimmed)) {
    throw new Error(`卡片槽位数仅支持 1-${MAX_SLOT_LIMIT}，或填写“无限”。`);
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (parsed < 1 || parsed > MAX_SLOT_LIMIT) {
    throw new Error(`卡片槽位数仅支持 1-${MAX_SLOT_LIMIT}，或填写“无限”。`);
  }

  return parsed;
};

const parseCustomDurationDaysInput = (
  durationKey: HostLicenseDurationKey,
  rawValue: string,
): number | undefined => {
  if (durationKey !== 'custom-days') {
    return undefined;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) {
    throw new Error('请选择“自定义天数”后，必须填写要下发的天数。');
  }

  if (!/^\d{1,4}$/.test(trimmed)) {
    throw new Error(`自定义天数仅支持 1-${MAX_CUSTOM_DURATION_DAYS} 天。`);
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (parsed < 1 || parsed > MAX_CUSTOM_DURATION_DAYS) {
    throw new Error(`自定义天数仅支持 1-${MAX_CUSTOM_DURATION_DAYS} 天。`);
  }

  return parsed;
};

const formatRemainingPercent = (value: number | null): string =>
  value === null ? '未识别' : `剩余 ${value}%`;

const getRemoteSnapshotTitle = (snapshot: LicenseHostRemoteProfileSnapshot): string =>
  snapshot.workspaceName || snapshot.name;

const getRemoteSnapshotStateMeta = (
  snapshot: LicenseHostRemoteProfileSnapshot,
): {
  label: string;
  tone: 'active' | 'ready' | 'disabled';
} => {
  if (snapshot.manuallyDisabled) {
    return {
      label: '已停用',
      tone: 'disabled',
    };
  }

  if (snapshot.isActive) {
    return {
      label: '正在使用',
      tone: 'active',
    };
  }

  return {
    label: '正常可用',
    tone: 'ready',
  };
};

type RemoteSnapshotSecurityTone = 'cloud' | 'success' | 'security' | 'warning';

interface RemoteSnapshotSecurityChip {
  key: string;
  label: string;
  tone: RemoteSnapshotSecurityTone;
}

const getRemoteSnapshotSecurityChips = (
  snapshot: LicenseHostRemoteProfileSnapshot,
): RemoteSnapshotSecurityChip[] => {
  const binding = snapshot.securityBinding;
  if (!binding) {
    return [];
  }

  const chips: RemoteSnapshotSecurityChip[] = [];
  if (binding.integrityState === 'tampered') {
    chips.push({ key: 'device-bound-alert', label: '安全异常', tone: 'warning' });
  }
  if (binding.importSource === 'cloud-backup') {
    chips.push({ key: 'cloud-backup', label: '云备份', tone: 'cloud' });
    chips.push({ key: 'cloud-restored', label: '恢复完成', tone: 'success' });
  } else if (binding.importSource === 'local') {
    chips.push({ key: 'local-device-bound', label: '本机绑定', tone: 'security' });
  } else {
    chips.push({ key: 'cross-device', label: '跨设备传卡', tone: 'cloud' });
  }
  chips.push({ key: 'device-bound', label: '设备绑定', tone: 'security' });
  return chips;
};

const getRemoteSnapshotSecurityDetail = (
  snapshot: LicenseHostRemoteProfileSnapshot,
): string => {
  const binding = snapshot.securityBinding;
  if (!binding) {
    return '';
  }

  const sourceLabel =
    binding.importSource === 'cloud-backup'
      ? binding.senderHostLabel
        ? `来源 ${binding.senderHostLabel} 的云备份`
        : '来源同账号云备份'
      : binding.importSource === 'local'
        ? '当前设备本机绑定'
      : binding.senderHostLabel
        ? `来源 ${binding.senderHostLabel} 的跨设备传卡`
        : '来源跨设备传卡';
  const ownerLabel = binding.ownerUserEmail ? `归属 ${binding.ownerUserEmail}` : '';
  const boundLabel = `绑定 ${binding.boundFingerprintSummary}`;
  const integrityLabel =
    binding.integrityState === 'tampered'
      ? '安全锁校验失败'
      : binding.integrityState === 'verified'
        ? ''
        : '安全锁待升级';
  return [sourceLabel, ownerLabel, boundLabel, integrityLabel].filter(Boolean).join(' / ');
};

const buildRemoteFingerprintInfo = (
  group: LicenseHostRemoteClientGroup,
  client: LicenseHostRemoteClient,
): string =>
  [
    `客户分组：${group.title}`,
    `当前账号：${getRemoteGroupAccountLabel(group)}`,
    `当前主设备：${client.deviceId}`,
    `机器指纹摘要：${group.fingerprintSummary}`,
    `机器指纹哈希：${group.fingerprintHash}`,
  ].join('\n');

const buildRemoteSnapshotImportUnavailableMessage = (
  client: LicenseHostRemoteClient,
  snapshot: LicenseHostRemoteProfileSnapshot,
): string => {
  const workspaceLabel = getRemoteSnapshotTitle(snapshot);
  const versionLabel = client.clientVersion?.trim() || null;

  if (versionLabel) {
    return `当前空间“${workspaceLabel}”来自客户端 ${versionLabel}，还没有回传完整卡片内容；请先让客户机升级到最新版后再提取。`;
  }

  return `当前空间“${workspaceLabel}”只回传了概况，客户机暂未提供完整卡片内容；请先升级客户机后再提取。`;
};

const buildRemoteSnapshotExport = (
  group: LicenseHostRemoteClientGroup,
  client: LicenseHostRemoteClient,
  snapshot: LicenseHostRemoteProfileSnapshot,
): string =>
  JSON.stringify(
    {
      sourceGroup: group.title,
      sourceAccount: getRemoteGroupAccountLabel(group),
      sourceDeviceId: client.deviceId,
      sourceFingerprintSummary: group.fingerprintSummary,
      snapshot,
    },
    null,
    2,
  );

const formatHostDurationLabel = (
  durationKey: HostLicenseDurationKey,
  customDurationDaysInput: string,
  supportedDurations: LicenseHostState['supportedDurations'],
): string => {
  if (durationKey === 'custom-days') {
    const trimmed = customDurationDaysInput.trim();
    return trimmed ? `${trimmed} 天` : '自定义天数';
  }

  return supportedDurations.find((item) => item.key === durationKey)?.label ?? '未识别';
};

const formatRemoteQuotaSnapshotLabel = (
  quota: LicenseHostRemoteProfileSnapshot['weeklyQuota'],
): string => {
  if (quota.configured === false && quota.source === 'unknown') {
    return '未配置';
  }

  return `剩余 ${Math.max(0, 100 - quota.percentUsed)}%`;
};

const formatRemoteAuthStatusLabel = (
  status: LicenseHostRemoteProfileSnapshot['authStatus'],
): string => {
  if (status === 'ok') {
    return '授权状态正常';
  }

  if (status === 'missing') {
    return '缺少授权文件';
  }

  return '授权路径异常';
};

const getMondayBasedTodayIndex = (): number => {
  const day = new Date().getDay();
  return day === 0 ? 6 : day - 1;
};

const getOverviewBarTone = (index: number): string => {
  if (index === getMondayBasedTodayIndex()) {
    return 'is-today';
  }

  return 'is-muted';
};

const buildOverviewWaveGeometry = (
  entries: ReadonlyArray<{ value: number }>,
): {
  areaPath: string;
  linePath: string;
  points: Array<{ x: number; y: number }>;
  ticks: Array<{ label: string; y: number }>;
  gridStartX: number;
  gridEndX: number;
  width: number;
  height: number;
} => {
  const width = 960;
  const height = 176;
  const topPadding = 18;
  const rightPadding = 18;
  const bottomPadding = 34;
  const leftPadding = 44;
  const usableWidth = width - leftPadding - rightPadding;
  const usableHeight = height - topPadding - bottomPadding;
  const rawMaxValue = Math.max(...entries.map((item) => item.value), 1);
  const maxValue = Math.max(10, Math.ceil(rawMaxValue / 10) * 10);
  const baseline = height - bottomPadding;
  const points = entries.map((item, index) => {
    const x =
      leftPadding + (entries.length > 1 ? (index / (entries.length - 1)) * usableWidth : usableWidth / 2);
    const y = topPadding + usableHeight - (item.value / maxValue) * usableHeight;
    return { x, y };
  });
  const ticks = [maxValue, Math.round(maxValue / 2), 0].map((value) => ({
    label: String(value),
    y: topPadding + usableHeight - (value / maxValue) * usableHeight,
  }));

  const linePath = points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    }
    const previous = points[index - 1];
    const midX = (previous.x + point.x) / 2;
    return `${path} C ${midX.toFixed(2)} ${previous.y.toFixed(2)}, ${midX.toFixed(2)} ${point.y.toFixed(2)}, ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
  }, '');
  const firstPoint = points[0] ?? { x: leftPadding, y: baseline };
  const lastPoint = points[points.length - 1] ?? { x: width - rightPadding, y: baseline };
  const areaPath = `${linePath} L ${lastPoint.x.toFixed(2)} ${baseline} L ${firstPoint.x.toFixed(2)} ${baseline} Z`;

  return {
    areaPath,
    linePath,
    points,
    ticks,
    gridStartX: leftPadding,
    gridEndX: width - rightPadding,
    width,
    height,
  };
};

export function LicenseHostView() {
  const bridge = useMemo(() => resolveLicenseHostBridge(), []);
  const isBrowserLocalHostView = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return new URLSearchParams(window.location.search).get('view') === 'license-host';
  }, []);
  const isBrowserCloudConsoleView = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.location.pathname.startsWith('/root-console');
  }, []);
  const [hostState, setHostState] = useState<LicenseHostState>(createEmptyHostState);
  const [form, setForm] = useState<HostFormState>(createInitialForm);
  const [remoteForm, setRemoteForm] = useState<RemoteIssueFormState>(createInitialRemoteForm);
  const [remoteGrantPresets, setRemoteGrantPresets] = useState<Record<string, RemoteGrantPreset>>(
    loadRemoteGrantPresetMap,
  );
  const [activeSection, setActiveSection] = useState<LicenseHostWorkspaceSection>('overview');
  const [generatedCode, setGeneratedCode] = useState('');
  const [busy, setBusy] = useState<'loading' | 'issuing' | null>('loading');
  const [remoteBusy, setRemoteBusy] = useState<
    | 'refresh'
    | 'set-update-channel'
    | 'issue-license'
    | 'issue-transfer'
    | 'revoke-license'
    | 'release-account-restriction'
    | 'reset-password'
    | 'reset-secondary-password'
    | 'import-profile'
    | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [hostLoginConfig] = useState<LicenseHostLocalLoginConfig>(loadLicenseHostLoginConfig);
  const [hostLoginSession, setHostLoginSession] = useState<LicenseHostLocalSession | null>(
    loadLicenseHostSession,
  );
  const [hostLoginUsername, setHostLoginUsername] = useState(
    () => loadLicenseHostRememberedUsername() || loadLicenseHostSession()?.username || '',
  );
  const [hostLoginPassword, setHostLoginPassword] = useState('');
  const [hostLoginRememberDevice, setHostLoginRememberDevice] = useState(() =>
    Boolean(loadLicenseHostRememberedUsername()),
  );
  const [hostLoginPasswordVisible, setHostLoginPasswordVisible] = useState(false);
  const [hostLoginBusy, setHostLoginBusy] = useState(false);
  const [hostLoginErrorMessage, setHostLoginErrorMessage] = useState<string | null>(null);
  const [recordSearch, setRecordSearch] = useState('');
  const [recordSearchBusy, setRecordSearchBusy] = useState(false);
  const [recordSearchResults, setRecordSearchResults] = useState<LicenseHostIssuedRecord[] | null>(
    null,
  );
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [remoteDashboard, setRemoteDashboard] = useState<LicenseHostRemoteDashboard>(
    createEmptyRemoteDashboard,
  );
  const [orderDeliveryDashboard, setOrderDeliveryDashboard] = useState<XianyuManagementSnapshot>(
    createEmptyOrderDeliverySnapshot,
  );
  const [orderDeliveryBusy, setOrderDeliveryBusy] = useState<
    'refresh' | 'tier' | 'template' | 'order' | 'codes' | 'codes-delete' | 'fulfill' | 'bot' | null
  >(null);
  const [orderDeliveryQuery, setOrderDeliveryQuery] = useState('');
  const [selectedOrderDeliveryCodeIds, setSelectedOrderDeliveryCodeIds] = useState<string[]>([]);
  const [orderDeliveryCodePage, setOrderDeliveryCodePage] = useState(1);
  const [orderDeliveryTierForm, setOrderDeliveryTierForm] = useState<OrderDeliveryTierFormState>(
    createInitialOrderDeliveryTierForm,
  );
  const [orderDeliveryTemplateForm, setOrderDeliveryTemplateForm] =
    useState<OrderDeliveryTemplateFormState>(createInitialOrderDeliveryTemplateForm);
  const [orderDeliveryOrderForm, setOrderDeliveryOrderForm] =
    useState<OrderDeliveryOrderFormState>(createInitialOrderDeliveryOrderForm);
  const [orderDeliveryCodeForm, setOrderDeliveryCodeForm] =
    useState<OrderDeliveryCodeFormState>(createInitialOrderDeliveryCodeForm);
  const [orderDeliveryBotForm, setOrderDeliveryBotForm] =
    useState<OrderDeliveryBotFormState>(createInitialOrderDeliveryBotForm);
  const [generatedOrderDeliveryCodes, setGeneratedOrderDeliveryCodes] = useState<string[]>([]);
  const [remoteProfiles, setRemoteProfiles] = useState<ProfileSummary[]>([]);
  const [remoteQuery, setRemoteQuery] = useState('');
  const [selectedRemoteGroupId, setSelectedRemoteGroupId] = useState<string | null>(null);
  const [selectedRemoteSnapshotId, setSelectedRemoteSnapshotId] = useState<string | null>(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [feedbackReplyDraft, setFeedbackReplyDraft] = useState('');
  const [optimisticFeedbackMessages, setOptimisticFeedbackMessages] = useState<
    OptimisticHostFeedbackMessage[]
  >([]);
  const [feedbackActionBusy, setFeedbackActionBusy] = useState<'read' | 'reply' | null>(null);
  const feedbackThreadRef = useRef<HTMLDivElement | null>(null);
  const feedbackNotificationAudioContextRef = useRef<AudioContext | null>(null);
  const observedIncomingFeedbackMessageIdsRef = useRef<Set<string>>(new Set());
  const observedIncomingFeedbackInitializedRef = useRef(false);
  const [quickGrantGroupId, setQuickGrantGroupId] = useState<string | null>(null);
  const remoteGroups = useMemo(
    () =>
      remoteDashboard.groups.length > 0
        ? remoteDashboard.groups
        : buildRemoteClientGroupsLocal(remoteDashboard.clients, remoteDashboard.activities),
    [remoteDashboard.groups, remoteDashboard.clients, remoteDashboard.activities],
  );
  const remoteGroupSummary = useMemo(
    () => ({
      total: remoteGroups.length,
      online: remoteGroups.filter((group) => group.primaryClient.presence === 'online').length,
      recent: remoteGroups.filter((group) => group.primaryClient.presence === 'recent').length,
      offline: remoteGroups.filter((group) => group.primaryClient.presence === 'offline').length,
    }),
    [remoteGroups],
  );
  const selectedFeedbackEntry = useMemo(
    () => remoteDashboard.feedbackEntries.find((entry) => entry.id === selectedFeedbackId) ?? null,
    [remoteDashboard.feedbackEntries, selectedFeedbackId],
  );
  const latestSelectedFeedbackUserMessageId = useMemo(() => {
    if (!selectedFeedbackEntry) {
      return null;
    }
    for (let index = selectedFeedbackEntry.messages.length - 1; index >= 0; index -= 1) {
      const message = selectedFeedbackEntry.messages[index];
      if (message.sender === 'user') {
        return message.id;
      }
    }
    return null;
  }, [selectedFeedbackEntry]);
  const latestSelectedFeedbackHostMessageId = useMemo(() => {
    if (!selectedFeedbackEntry) {
      return null;
    }
    for (let index = selectedFeedbackEntry.messages.length - 1; index >= 0; index -= 1) {
      const message = selectedFeedbackEntry.messages[index];
      if (message.sender === 'host') {
        return message.id;
      }
    }
    return null;
  }, [selectedFeedbackEntry]);
  const visibleSelectedFeedbackMessages = useMemo(() => {
    const wasHostMessageReadByUser = (messageCreatedAt: string): boolean => {
      const userReadAt = selectedFeedbackEntry?.userReadAt;
      if (!userReadAt) {
        return false;
      }

      const userReadAtMs = Date.parse(userReadAt);
      const messageCreatedAtMs = Date.parse(messageCreatedAt);
      if (Number.isNaN(userReadAtMs) || Number.isNaN(messageCreatedAtMs)) {
        return true;
      }

      return userReadAtMs >= messageCreatedAtMs;
    };

    const serverMessages = selectedFeedbackEntry?.messages.map((message) => ({
      id: message.id,
      sender: message.sender,
      body: message.body,
      createdAt: message.createdAt,
      deliveryState:
        message.sender === 'user'
          ? message.id === latestSelectedFeedbackUserMessageId
            ? selectedFeedbackEntry.status === 'pending'
              ? '未读'
              : selectedFeedbackEntry.status === 'read'
                ? '已读'
                : '已回复'
            : '已发送'
          : message.id === latestSelectedFeedbackHostMessageId
            ? wasHostMessageReadByUser(message.createdAt)
              ? '已读'
              : '未读'
            : '已发送',
      errorMessage: null,
    })) ?? [];

    const optimisticMessages = optimisticFeedbackMessages
      .filter((message) => message.feedbackId === selectedFeedbackEntry?.id)
      .map((message) => ({
        id: message.id,
        sender: 'host' as const,
        body: message.body,
        createdAt: message.createdAt,
        deliveryState: message.state === 'sending' ? '发送中' : '发送失败',
        errorMessage: message.errorMessage,
      }));

    return [...serverMessages, ...optimisticMessages].sort((left, right) => {
      const leftTime = Date.parse(left.createdAt);
      const rightTime = Date.parse(right.createdAt);
      if (Number.isNaN(leftTime) || Number.isNaN(rightTime) || leftTime === rightTime) {
        return left.id.localeCompare(right.id);
      }
      return leftTime - rightTime;
    });
  }, [
    latestSelectedFeedbackHostMessageId,
    latestSelectedFeedbackUserMessageId,
    optimisticFeedbackMessages,
    selectedFeedbackEntry,
  ]);
  const latestSelectedFeedbackMessageId =
    visibleSelectedFeedbackMessages[visibleSelectedFeedbackMessages.length - 1]?.id ?? null;
  const latestIncomingFeedbackMessages = useMemo(
    () =>
      remoteDashboard.feedbackEntries.reduce<
        Array<{
          id: string;
          body: string;
          entry: FeedbackEntry;
        }>
      >((items, entry) => {
        for (let index = entry.messages.length - 1; index >= 0; index -= 1) {
          const message = entry.messages[index];
          if (message.sender === 'user') {
            items.push({
              id: message.id,
              body: message.body,
              entry,
            });
            break;
          }
        }
        return items;
      }, []),
    [remoteDashboard.feedbackEntries],
  );

  useEffect(() => {
    if (!selectedFeedbackId) {
      return;
    }

    if (!remoteDashboard.feedbackEntries.some((entry) => entry.id === selectedFeedbackId)) {
      setSelectedFeedbackId(remoteDashboard.feedbackEntries[0]?.id ?? null);
      setFeedbackReplyDraft('');
    }
  }, [remoteDashboard.feedbackEntries, selectedFeedbackId]);

  useEffect(() => {
    if (!selectedFeedbackEntry) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const threadElement = feedbackThreadRef.current;
      if (threadElement) {
        threadElement.scrollTop = threadElement.scrollHeight;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedFeedbackEntry?.id, latestSelectedFeedbackMessageId]);

  useEffect(() => {
    const nextObservedIds = new Set(latestIncomingFeedbackMessages.map((item) => item.id));
    if (!observedIncomingFeedbackInitializedRef.current) {
      observedIncomingFeedbackMessageIdsRef.current = nextObservedIds;
      observedIncomingFeedbackInitializedRef.current = true;
      return;
    }

    const incomingMessages = latestIncomingFeedbackMessages.filter(
      (item) => !observedIncomingFeedbackMessageIdsRef.current.has(item.id),
    );
    if (incomingMessages.length === 0) {
      return;
    }

    observedIncomingFeedbackMessageIdsRef.current = nextObservedIds;

    incomingMessages.forEach((_item, index) => {
      window.setTimeout(() => {
        void playMessageDropletTone(feedbackNotificationAudioContextRef, window);
      }, index * 140);
    });

    if (!document.hasFocus()) {
      incomingMessages.forEach((item) => {
        void showDesktopNotification({
          title: `${item.entry.senderDisplayName ?? item.entry.senderEmail ?? '客户'} 发来新消息`,
          body: item.body,
          tag: `license-host-feedback-${item.id}`,
          onClick: async () => {
            if (bridge?.mode === 'electron' && typeof bridge.openLicenseHostWindow === 'function') {
              await bridge.openLicenseHostWindow();
            }
            handleOpenFeedbackConversation(item.entry);
          },
        });
      });
    }
  }, [bridge, latestIncomingFeedbackMessages]);

  useEffect(() => {
    return () => {
      const context = feedbackNotificationAudioContextRef.current;
      feedbackNotificationAudioContextRef.current = null;
      if (context) {
        void context.close().catch(() => {
          // Ignore notification-audio teardown failures during unmount.
        });
      }
    };
  }, []);

  const expireBrowserHostSession = useCallback(
    (message: string) => {
      if (isBrowserCloudConsoleView) {
        clearLicenseHostBrowserAuthToken();
      }
      clearLicenseHostSession();
      setHostLoginSession(null);
      setHostLoginPassword('');
      setHostLoginPasswordVisible(false);
      setHostLoginErrorMessage(message);
    },
    [isBrowserCloudConsoleView],
  );

  useEffect(() => {
    if (hostLoginSession) {
      if (!isLicenseHostSessionValid(hostLoginSession, hostLoginConfig.updatedAt)) {
        expireBrowserHostSession('控制台会话已失效，请重新输入密码登录。');
      }
    }
  }, [expireBrowserHostSession, hostLoginConfig.updatedAt, hostLoginSession]);

  useEffect(() => {
    if (!isBrowserCloudConsoleView || !hostLoginSession) {
      return;
    }

    const expireMessage = '会话闲置超过 2 小时，请重新输入密码登录。';
    let expiryTimer: number | null = null;

    const scheduleExpiry = (session: LicenseHostLocalSession) => {
      if (expiryTimer !== null) {
        window.clearTimeout(expiryTimer);
      }
      expiryTimer = window.setTimeout(
        () => expireBrowserHostSession(expireMessage),
        Math.max(0, getLicenseHostSessionExpiresAt(session) - Date.now()),
      );
    };

    const refreshSessionActivity = () => {
      setHostLoginSession((current) => {
        if (!current) {
          return current;
        }
        if (!isLicenseHostSessionValid(current, hostLoginConfig.updatedAt)) {
          window.setTimeout(() => expireBrowserHostSession(expireMessage), 0);
          return current;
        }
        const nextSession = touchLicenseHostSession(current);
        persistLicenseHostSession(nextSession);
        scheduleExpiry(nextSession);
        return nextSession;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSessionActivity();
      }
    };

    scheduleExpiry(hostLoginSession);
    window.addEventListener('pointerdown', refreshSessionActivity);
    window.addEventListener('keydown', refreshSessionActivity);
    window.addEventListener('focus', refreshSessionActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (expiryTimer !== null) {
        window.clearTimeout(expiryTimer);
      }
      window.removeEventListener('pointerdown', refreshSessionActivity);
      window.removeEventListener('keydown', refreshSessionActivity);
      window.removeEventListener('focus', refreshSessionActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    expireBrowserHostSession,
    hostLoginConfig.updatedAt,
    hostLoginSession,
    isBrowserCloudConsoleView,
  ]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const currentHostConsoleUnlocked = isLicenseHostSessionValid(
        hostLoginSession,
        hostLoginConfig.updatedAt,
      );
      if (isBrowserCloudConsoleView && !currentHostConsoleUnlocked) {
        if (!cancelled) {
          setBusy(null);
          setErrorMessage(null);
          setRemoteProfiles([]);
        }
        return;
      }
      if (isBrowserCloudConsoleView && currentHostConsoleUnlocked && !loadLicenseHostBrowserAuthToken()) {
        if (!cancelled) {
          clearLicenseHostSession();
          setHostLoginSession(null);
          setHostLoginErrorMessage('云端管理员会话已失效，请重新登录。');
          setBusy(null);
        }
        return;
      }
      if (
        !bridge ||
        typeof bridge.getLicenseHostState !== 'function' ||
        (!isBrowserCloudConsoleView && typeof bridge.getAppState !== 'function') ||
        typeof bridge.getLicenseHostRemoteDashboard !== 'function'
      ) {
        if (!cancelled) {
          setErrorMessage(
            isBrowserLocalHostView
              ? '当前浏览器监听还没有连上本地授权控制台桥接服务。请重新通过启动器打开“浏览器监听”，或先确认本地桥接已启动。'
              : isBrowserCloudConsoleView
                ? '当前云端控制台会话还没有完成初始化，请重新登录后再试。'
                : '当前主机窗口没有拿到 Electron 通信桥。请不要直接用浏览器打开这个页面，改用启动脚本或安装版里的主机入口重新打开。',
          );
          setBusy(null);
        }
        return;
      }

      try {
        const nextState = await bridge.getLicenseHostState();
        if (cancelled) {
          return;
        }

        setHostState(nextState);
        if (isBrowserCloudConsoleView) {
          setRemoteProfiles([]);
          setRemoteForm((current) => ({
            ...current,
            profileId: '',
          }));
          setErrorMessage(null);
          return;
        }

        const appState = await bridge.getAppState();
        if (cancelled) {
          return;
        }
        setRemoteProfiles(appState.profiles);
        setRemoteForm((current) => ({
          ...current,
          profileId: current.profileId || appState.profiles[0]?.id || '',
        }));
        setErrorMessage(null);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (
          isBrowserCloudConsoleView &&
          error instanceof Error &&
          /管理员会话|登录令牌|权限|失效/.test(error.message)
        ) {
          clearLicenseHostBrowserAuthToken();
          clearLicenseHostSession();
          setHostLoginSession(null);
        }
        setErrorMessage(error instanceof Error ? error.message : '授权主机初始化失败。');
      } finally {
        if (!cancelled) {
          setBusy(null);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [bridge, hostLoginConfig.updatedAt, hostLoginSession, isBrowserCloudConsoleView, isBrowserLocalHostView]);

  useEffect(() => {
    if (!toastMessage) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToastMessage(null);
    }, 2600);

    return () => window.clearTimeout(timer);
  }, [toastMessage]);

  useEffect(() => {
    persistRemoteGrantPresetMap(remoteGrantPresets);
  }, [remoteGrantPresets]);

  useEffect(() => {
    if (!selectedRemoteGroupId) {
      if (remoteGroups[0]?.id) {
        setSelectedRemoteGroupId(remoteGroups[0].id);
      }
      return;
    }

    const stillExists = remoteGroups.some(
      (group) => group.id === selectedRemoteGroupId,
    );
    if (!stillExists) {
      setSelectedRemoteGroupId(remoteGroups[0]?.id ?? null);
    }
  }, [remoteGroups, selectedRemoteGroupId]);

  useEffect(() => {
    if (!remoteForm.profileId && remoteProfiles[0]?.id) {
      setRemoteForm((current) => ({
        ...current,
        profileId: remoteProfiles[0]?.id ?? current.profileId,
      }));
    }
  }, [remoteForm.profileId, remoteProfiles]);

  useEffect(() => {
    const targetGroup =
      remoteGroups.find((group) => group.id === selectedRemoteGroupId) ?? remoteGroups[0] ?? null;
    const targetClient = targetGroup?.primaryClient ?? null;
    const targetSnapshots = targetClient?.profileSnapshots ?? [];
    const nextSnapshotId = targetSnapshots[0]?.id ?? null;
    if (!selectedRemoteSnapshotId) {
      setSelectedRemoteSnapshotId(nextSnapshotId);
      return;
    }

    const exists = targetSnapshots.some((snapshot) => snapshot.id === selectedRemoteSnapshotId);
    if (!exists) {
      setSelectedRemoteSnapshotId(nextSnapshotId);
    }
  }, [remoteGroups, selectedRemoteGroupId, selectedRemoteSnapshotId]);

  useEffect(() => {
    let cancelled = false;

    if (!recordSearch.trim()) {
      setRecordSearchResults(null);
      setRecordSearchBusy(false);
      return () => {
        cancelled = true;
      };
    }

    if (!bridge) {
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(() => {
      const activeBridge = bridge;
      if (!activeBridge) {
        return;
      }
      setRecordSearchBusy(true);
      void activeBridge
        .searchLicenseHostRecords(recordSearch)
        .then((result) => {
          if (!cancelled) {
            setRecordSearchResults(result.records);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            setErrorMessage(error instanceof Error ? error.message : '搜索签发记录失败。');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setRecordSearchBusy(false);
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bridge, recordSearch, hostState]);

  useEffect(() => {
    let cancelled = false;

    if (!hostState.remoteAdminConfigured) {
      setRemoteDashboard(createEmptyRemoteDashboard());
      return () => {
        cancelled = true;
      };
    }

    if (!bridge) {
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(() => {
      const activeBridge = bridge;
      if (!activeBridge) {
        return;
      }
      void activeBridge
        .getLicenseHostRemoteDashboard({
          query: '',
          clientLimit: REMOTE_CLIENT_QUERY_LIMIT,
          activityLimit: REMOTE_ACTIVITY_QUERY_LIMIT,
        })
        .then((result) => {
          if (!cancelled) {
            setRemoteDashboard(
              normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl),
            );
          }
        })
        .catch((error) => {
          if (!cancelled) {
            setErrorMessage(error instanceof Error ? error.message : '远程监控刷新失败。');
          }
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bridge, hostState.remoteAdminConfigured, hostState.remoteSourceUrl]);

  useEffect(() => {
    let cancelled = false;

    if (!hostState.remoteAdminConfigured) {
      setOrderDeliveryDashboard(createEmptyOrderDeliverySnapshot());
      return () => {
        cancelled = true;
      };
    }

    if (!bridge || (activeSection !== 'orders' && activeSection !== 'codes')) {
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(() => {
      setOrderDeliveryBusy('refresh');
      void bridge
        .getLicenseHostOrderDeliveryDashboard()
        .then((result) => {
          if (!cancelled) {
            setOrderDeliveryDashboard(result.topology);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            setErrorMessage(error instanceof Error ? error.message : '授权码工作区刷新失败。');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setOrderDeliveryBusy((current) => (current === 'refresh' ? null : current));
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeSection, bridge, hostState.remoteAdminConfigured]);

  const currentDuration = useMemo(
    () => hostState.supportedDurations.find((item) => item.key === form.durationKey) ?? null,
    [hostState.supportedDurations, form.durationKey],
  );
  const currentSlotLimitHint = useMemo(() => {
    try {
      const parsed = parseSlotLimitInput(form.slotLimitInput);
      if (parsed === undefined) {
        return `${formatHostSlotLimitLabel(getDefaultSlotLimitByDuration(form.durationKey))}（按当前时长默认）`;
      }
      return formatHostSlotLimitLabel(parsed);
    } catch {
      return '输入格式无效';
    }
  }, [form.durationKey, form.slotLimitInput]);
  const currentRemoteDurationLabel = useMemo(
    () =>
      formatHostDurationLabel(
        remoteForm.durationKey,
        remoteForm.customDurationDaysInput,
        hostState.supportedDurations,
      ),
    [hostState.supportedDurations, remoteForm.customDurationDaysInput, remoteForm.durationKey],
  );
  const currentRemoteSlotLimitHint = useMemo(() => {
    try {
      const parsed = parseSlotLimitInput(remoteForm.slotLimitInput);
      if (parsed === undefined) {
        return `${formatHostSlotLimitLabel(getDefaultSlotLimitByDuration(remoteForm.durationKey))}（按当前时长默认）`;
      }
      return formatHostSlotLimitLabel(parsed);
    } catch {
      return '输入格式无效';
    }
  }, [remoteForm.durationKey, remoteForm.slotLimitInput]);
  const visibleRecords = recordSearch.trim() ? recordSearchResults ?? [] : hostState.recentRecords;
  const filteredRemoteGroups = useMemo(
    () => remoteGroups.filter((group) => remoteGroupMatchesQuery(group, remoteQuery)),
    [remoteGroups, remoteQuery],
  );
  const selectedRemoteGroup = useMemo(
    () => remoteGroups.find((group) => group.id === selectedRemoteGroupId) ?? remoteGroups[0] ?? null,
    [remoteGroups, selectedRemoteGroupId],
  );
  const selectedRemoteLoginEvents = useMemo(
    () =>
      selectedRemoteGroup
        ? remoteDashboard.loginEvents.filter((event) => remoteLoginEventMatchesGroup(event, selectedRemoteGroup))
        : [],
    [remoteDashboard.loginEvents, selectedRemoteGroup],
  );
  const unreadRemoteLoginEvents = useMemo(
    () => remoteDashboard.loginEvents.filter((event) => event.unread),
    [remoteDashboard.loginEvents],
  );
  const visibleRemoteLoginEvents = useMemo(() => {
    const scopedEvents =
      selectedRemoteLoginEvents.length > 0 ? selectedRemoteLoginEvents : remoteDashboard.loginEvents;
    return scopedEvents.slice(0, 8);
  }, [remoteDashboard.loginEvents, selectedRemoteLoginEvents]);
  const feedbackGroupSummaries = useMemo(() => {
    const summaryByGroup = new Map<
      string,
      {
        entries: FeedbackEntry[];
        unreadCount: number;
        latestEntry: FeedbackEntry | null;
      }
    >();
    remoteGroups.forEach((group) => {
      summaryByGroup.set(group.id, {
        entries: [],
        unreadCount: 0,
        latestEntry: null,
      });
    });

    for (const entry of remoteDashboard.feedbackEntries) {
      const matchedGroup =
        remoteGroups.find((group) => feedbackEntryMatchesGroup(entry, group)) ?? null;
      if (!matchedGroup) {
        continue;
      }
      const currentSummary = summaryByGroup.get(matchedGroup.id);
      if (!currentSummary) {
        continue;
      }
      currentSummary.entries.push(entry);
      if (entry.status === 'pending') {
        currentSummary.unreadCount += 1;
      }
      if (
        !currentSummary.latestEntry ||
        getFeedbackEntryActivityTimestamp(entry) >
          getFeedbackEntryActivityTimestamp(currentSummary.latestEntry)
      ) {
        currentSummary.latestEntry = entry;
      }
    }

    summaryByGroup.forEach((summary) => {
      summary.entries.sort(
        (left, right) =>
          getFeedbackEntryActivityTimestamp(right) - getFeedbackEntryActivityTimestamp(left),
      );
    });

    return summaryByGroup;
  }, [remoteDashboard.feedbackEntries, remoteGroups]);
  const selectedRemoteFeedbackEntries = useMemo(() => {
    if (!selectedRemoteGroup) {
      return [];
    }

    return feedbackGroupSummaries.get(selectedRemoteGroup.id)?.entries ?? [];
  }, [feedbackGroupSummaries, selectedRemoteGroup]);
  const feedbackInboxEntries = useMemo(
    () =>
      remoteDashboard.feedbackEntries
        .map((entry) => ({
          entry,
          group: remoteGroups.find((group) => feedbackEntryMatchesGroup(entry, group)) ?? null,
        }))
        .filter(({ entry, group }) => {
          const keyword = remoteQuery.trim();
          if (!keyword) {
            return true;
          }
          return group ? remoteGroupMatchesQuery(group, keyword) : feedbackEntryMatchesQuery(entry, keyword);
        })
        .sort((left, right) => {
          if (left.entry.status === 'pending' && right.entry.status !== 'pending') {
            return -1;
          }
          if (left.entry.status !== 'pending' && right.entry.status === 'pending') {
            return 1;
          }
          return (
            getFeedbackEntryActivityTimestamp(right.entry) -
            getFeedbackEntryActivityTimestamp(left.entry)
          );
        }),
    [remoteDashboard.feedbackEntries, remoteGroups, remoteQuery],
  );
  useEffect(() => {
    if (selectedRemoteFeedbackEntries.length === 0) {
      if (selectedFeedbackId) {
        setSelectedFeedbackId(null);
        setFeedbackReplyDraft('');
      }
      return;
    }

    if (
      !selectedFeedbackId ||
      !selectedRemoteFeedbackEntries.some((entry) => entry.id === selectedFeedbackId)
    ) {
      setSelectedFeedbackId(selectedRemoteFeedbackEntries[0].id);
      setFeedbackReplyDraft('');
    }
  }, [selectedFeedbackId, selectedRemoteFeedbackEntries]);
  const quickGrantGroup = useMemo(
    () => remoteGroups.find((group) => group.id === quickGrantGroupId) ?? null,
    [quickGrantGroupId, remoteGroups],
  );
  const selectedRemoteClient = selectedRemoteGroup?.primaryClient ?? null;
  const selectedRemoteSnapshots = selectedRemoteClient?.profileSnapshots ?? [];
  const selectedRemoteSnapshot = useMemo(
    () =>
      selectedRemoteSnapshots.find((snapshot) => snapshot.id === selectedRemoteSnapshotId) ??
      selectedRemoteSnapshots[0] ??
      null,
    [selectedRemoteSnapshotId, selectedRemoteSnapshots],
  );
  const selectedRemoteSnapshotState = selectedRemoteSnapshot
    ? getRemoteSnapshotStateMeta(selectedRemoteSnapshot)
    : null;
  const buildSelectedFeedbackTarget = (): Omit<
    AcknowledgeFeedbackInput,
    'feedbackId' | 'message'
  > | null => {
    if (!selectedRemoteGroup || !selectedRemoteClient) {
      return null;
    }

    const userId = normalizeText(selectedRemoteGroup.userId ?? selectedRemoteClient.userId ?? '');
    const email = normalizeText(selectedRemoteGroup.email ?? selectedRemoteClient.email ?? '');
    if (!userId && !email) {
      return null;
    }

    return {
      userId: userId || null,
      email: email || null,
      displayName:
        normalizeText(selectedRemoteGroup.displayName) ||
        normalizeText(selectedRemoteClient.displayName) ||
        normalizeText(selectedRemoteGroup.title) ||
        null,
      deviceId: normalizeText(selectedRemoteClient.deviceId) || null,
      fingerprintSummary:
        normalizeText(selectedRemoteGroup.fingerprintSummary) ||
        normalizeText(selectedRemoteClient.fingerprintSummary) ||
        null,
    };
  };
  const selectedRemoteProfile = useMemo(
    () => remoteProfiles.find((profile) => profile.id === remoteForm.profileId) ?? null,
    [remoteProfiles, remoteForm.profileId],
  );
  const selectedRemoteUpdateChannel = selectedRemoteClient?.remoteUpdateChannel ??
    createUnmanagedRemoteUpdateChannel();
  useEffect(() => {
    const presetKey = buildRemoteGrantPresetKey(selectedRemoteGroup);
    const preset = presetKey ? remoteGrantPresets[presetKey] : null;
    if (!preset) {
      return;
    }

    setRemoteForm((current) => ({
      ...current,
      durationKey: preset.durationKey,
      customDurationDaysInput: preset.customDurationDaysInput,
      slotLimitInput: preset.slotLimitInput,
      betaAccess: preset.betaAccess,
      advancedBetaAccess: preset.advancedBetaAccess,
      note: preset.note || current.note,
    }));
  }, [remoteGrantPresets, selectedRemoteGroup?.fingerprintHash, selectedRemoteGroup?.userId]);
  useEffect(() => {
    if (!selectedRemoteClient) {
      return;
    }

    const nextBetaAccess = selectedRemoteClient.remoteUpdateChannel.channel !== 'stable';
    const nextAdvancedBetaAccess = selectedRemoteClient.remoteUpdateChannel.allowEngineerAccess;
    setRemoteForm((current) =>
      current.betaAccess === nextBetaAccess &&
      current.advancedBetaAccess === nextAdvancedBetaAccess
        ? current
        : {
            ...current,
            betaAccess: nextBetaAccess,
            advancedBetaAccess: nextAdvancedBetaAccess,
          },
    );
  }, [
    selectedRemoteClient?.deviceId,
    selectedRemoteClient?.remoteUpdateChannel.allowEngineerAccess,
    selectedRemoteClient?.remoteUpdateChannel.channel,
    selectedRemoteClient?.remoteUpdateChannel.managed,
    selectedRemoteClient?.remoteUpdateChannel.updatedAt,
  ]);
  const currentDurationLabel = useMemo(
    () => formatHostDurationLabel(form.durationKey, form.customDurationDaysInput, hostState.supportedDurations),
    [form.customDurationDaysInput, form.durationKey, hostState.supportedDurations],
  );
  const displayHostConsoleName = useMemo(() => {
    const trimmed = hostState.hostName?.trim();
    if (!trimmed || trimmed === '本地授权主机') {
      return '授权控制台';
    }
    return trimmed;
  }, [hostState.hostName]);
  const proactiveFeedbackTarget = buildSelectedFeedbackTarget();
  const activeFeedbackEntry =
    selectedFeedbackEntry ??
    selectedRemoteFeedbackEntries[0] ??
    (selectedRemoteGroup && proactiveFeedbackTarget
      ? ({
          id: '',
          body: '',
          createdAt: selectedRemoteClient?.lastSeenAt ?? new Date().toISOString(),
          status: 'acknowledged',
          readAt: null,
          userReadAt: null,
          acknowledgedAt: null,
          acknowledgementMessage: null,
          senderUserId: selectedRemoteGroup.userId ?? selectedRemoteClient?.userId ?? null,
          senderDisplayName:
            selectedRemoteGroup.displayName ?? selectedRemoteClient?.displayName ?? selectedRemoteGroup.title,
          senderEmail: selectedRemoteGroup.email ?? selectedRemoteClient?.email ?? null,
          senderDeviceId: selectedRemoteClient?.deviceId ?? null,
          senderFingerprintSummary:
            selectedRemoteGroup.fingerprintSummary ?? selectedRemoteClient?.fingerprintSummary ?? null,
          senderAvatarText: null,
          senderAvatarImageDataUrl: null,
          previewText: '',
          latestMessageAt: selectedRemoteClient?.lastSeenAt ?? new Date().toISOString(),
          messages: [],
        } satisfies FeedbackEntry)
      : null);
  const hasActiveFeedbackThread = Boolean(activeFeedbackEntry?.id);
  const featuredFeedbackEntry = activeFeedbackEntry;
  const hostConsoleUnlocked = isLicenseHostSessionValid(hostLoginSession, hostLoginConfig.updatedAt);
  const hostConsoleAccount =
    hostLoginSession?.username ||
    loadLicenseHostRememberedUsername() ||
    LICENSE_HOST_DEFAULT_LOGIN_USERNAME;
  const activeSectionCopy = LICENSE_HOST_SECTION_COPY[activeSection];
  const restrictedGroupCount = useMemo(
    () => remoteGroups.filter((group) => Boolean(group.primaryClient.accountRestrictionUntil)).length,
    [remoteGroups],
  );
  const overviewKpis = useMemo(
    () => [
      {
        label: '活跃客户',
        value: String(remoteGroupSummary.online + remoteGroupSummary.recent),
        note: `在线 ${remoteGroupSummary.online} / 最近活跃 ${remoteGroupSummary.recent}`,
        tone: 'blue',
        icon: Users,
      },
      {
        label: '授权签发',
        value: String(hostState.issuedCount),
        note: hostState.lastIssuedAt ? `最近签发 ${formatDateTime(hostState.lastIssuedAt)}` : '还没有签发记录',
        tone: 'indigo',
        icon: KeyRound,
      },
      {
        label: '待处理会话',
        value: String(remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount),
        note: `登录 ${remoteDashboard.unreadLoginCount} / 反馈 ${remoteDashboard.unreadFeedbackCount}`,
        tone: 'amber',
        icon: BellRing,
      },
      {
        label: '异常限制',
        value: String(restrictedGroupCount),
        note: restrictedGroupCount > 0 ? '需要人工复核的客户分组' : '当前没有异常限制',
        tone: restrictedGroupCount > 0 ? 'red' : 'green',
        icon: AlertTriangle,
      },
    ],
    [hostState.issuedCount, hostState.lastIssuedAt, remoteDashboard.unreadFeedbackCount, remoteDashboard.unreadLoginCount, remoteGroupSummary.online, remoteGroupSummary.recent, restrictedGroupCount],
  );
  const overviewReminders = useMemo(
    () =>
      [
        remoteDashboard.unreadLoginCount > 0
          ? {
              title: '有新的登录提醒等待处理',
              detail: `${remoteDashboard.unreadLoginCount} 条登录事件还没有处理，可直接进入客户与授权。`,
            }
          : null,
        remoteDashboard.unreadFeedbackCount > 0
          ? {
              title: '有客户反馈待回执',
              detail: `${remoteDashboard.unreadFeedbackCount} 个反馈会话未完成回执或回复。`,
            }
          : null,
        restrictedGroupCount > 0
          ? {
              title: '存在异常限制中的客户',
              detail: `${restrictedGroupCount} 个客户分组处于限制状态，可在客户详情里直接解除。`,
            }
          : null,
      ].filter((item): item is { title: string; detail: string } => Boolean(item)),
    [remoteDashboard.unreadFeedbackCount, remoteDashboard.unreadLoginCount, restrictedGroupCount],
  );
  const systemStatusItems = useMemo(
    () => [
      {
        label: '远程管理',
        value: hostState.remoteAdminConfigured ? '已接入' : '未接入',
        note: hostState.remoteAdminConfigured ? '同步入口已就绪' : '等待同步入口',
      },
      {
        label: '控制台会话',
        value: hostConsoleUnlocked ? '已解锁' : '未解锁',
        note: hostLoginSession?.unlockedAt ? `进入时间 ${formatDateTime(hostLoginSession.unlockedAt)}` : '等待主机登录',
      },
      {
        label: '当前设备',
        value: '本机',
        note: '设备会话正常',
      },
    ],
    [hostConsoleUnlocked, hostLoginSession?.unlockedAt, hostState.remoteAdminConfigured],
  );
  const hostConsolePendingCount =
    remoteDashboard.unreadLoginCount +
    remoteDashboard.unreadFeedbackCount +
    orderDeliveryDashboard.summary.pendingOrders;
  const hostConsoleStatusLabel = remoteDashboard.connected
    ? '系统在线'
    : hostState.remoteAdminConfigured
      ? '等待同步'
      : '本地模式';
  const hostConsoleAvatarText = getAvatarFallbackText(hostConsoleAccount);
  const workspaceSearchDisabled = activeSection === 'overview';
  const workspaceSearchValue =
    activeSection === 'customers' || activeSection === 'feedback'
      ? remoteQuery
      : activeSection === 'orders' || activeSection === 'codes'
        ? orderDeliveryQuery
      : activeSection === 'settings'
        ? recordSearch
        : '';
  const workspaceSearchPlaceholder =
    activeSection === 'customers'
      ? '搜索客户账号 / 设备编号 / 指纹'
      : activeSection === 'orders'
        ? '搜索订单号 / 买家 / 模板 / 通道'
      : activeSection === 'codes'
        ? '搜索授权码 / 批次 / 账号'
      : activeSection === 'feedback'
        ? '搜索客户账号 / 设备编号 / 收信箱内容'
      : activeSection === 'settings'
        ? '搜索签发记录 / 设备 ID / 备注'
        : '运营总览';
  const workspaceSecondaryLabel =
    activeSection === 'customers'
      ? selectedRemoteGroup
        ? getRemoteGroupAccountLabel(selectedRemoteGroup)
        : `${remoteGroups.length} 个客户分组`
      : activeSection === 'orders'
        ? `待分配 ${orderDeliveryDashboard.summary.pendingOrders} / 已分配 ${orderDeliveryDashboard.summary.fulfilledOrders}`
      : activeSection === 'codes'
        ? `库存 ${orderDeliveryDashboard.summary.unusedCodes} / 已绑定 ${orderDeliveryDashboard.summary.redeemedCodes} / 本次 ${generatedOrderDeliveryCodes.length}`
      : activeSection === 'feedback'
        ? `${feedbackInboxEntries.length} 个会话 / 未读 ${remoteDashboard.unreadFeedbackCount}`
      : activeSection === 'settings'
        ? `${visibleRecords.length} 条签发记录`
        : `${remoteGroupSummary.total} 个客户分组`;
  const remoteGrantPresetCount = Object.keys(remoteGrantPresets).length;
  const orderDeliveryKeyword = orderDeliveryQuery.trim().toLowerCase();
  const orderDeliveryCodeDurationLabel = useMemo(
    () =>
      formatHostDurationLabel(
        orderDeliveryCodeForm.durationKey,
        orderDeliveryCodeForm.customDurationDaysInput,
        hostState.supportedDurations,
      ),
    [
      hostState.supportedDurations,
      orderDeliveryCodeForm.customDurationDaysInput,
      orderDeliveryCodeForm.durationKey,
    ],
  );
  const orderDeliveryCodeChannelLabel = useMemo(
    () => getOrderDeliveryCodeChannelLabel(orderDeliveryCodeForm.channel),
    [orderDeliveryCodeForm.channel],
  );
  const orderDeliveryCodeSlotLabel = useMemo(() => {
    try {
      const parsedSlotLimit = parseSlotLimitInput(orderDeliveryCodeForm.slotLimitInput);
      const resolvedSlotLimit =
        parsedSlotLimit === undefined
          ? getDefaultSlotLimitByDuration(orderDeliveryCodeForm.durationKey)
          : parsedSlotLimit;
      return formatHostSlotLimitLabel(resolvedSlotLimit ?? null);
    } catch {
      return '卡槽未设置';
    }
  }, [orderDeliveryCodeForm.durationKey, orderDeliveryCodeForm.slotLimitInput]);
  const visibleOrderDeliveryTiers = useMemo(
    () =>
      orderDeliveryDashboard.tiers.filter((tier) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          tier.title,
          tier.tierKey,
          tier.skuTitle,
          tier.licenseTypeLabel,
          tier.autoDeliveryNote,
        ),
      ),
    [orderDeliveryDashboard.tiers, orderDeliveryKeyword],
  );
  const visibleOrderDeliveryTemplates = useMemo(
    () =>
      orderDeliveryDashboard.templates.filter((template) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          template.name,
          template.tierTitle,
          template.bodyTemplate,
          template.noticeText,
        ),
      ),
    [orderDeliveryDashboard.templates, orderDeliveryKeyword],
  );
  const visibleOrderDeliveryOrders = useMemo(
    () =>
      orderDeliveryDashboard.orders.filter((order) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          order.sourceOrderId,
          order.buyerName,
          order.buyerId,
          order.itemTitle,
          order.specValue,
          order.tierTitle,
          order.deliveryText,
        ),
      ),
    [orderDeliveryDashboard.orders, orderDeliveryKeyword],
  );
  const visibleOrderDeliveryCodes = useMemo(
    () =>
      orderDeliveryDashboard.recentCodes.filter((code) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          code.codeValue,
          code.codePreview,
          code.batchId,
          code.tierTitle,
          code.buyerName,
          code.redeemedByDisplayId,
          code.redeemedByDisplayName,
          code.redeemedByEmail,
        ),
      ),
    [orderDeliveryDashboard.recentCodes, orderDeliveryKeyword],
  );
  const selectedOrderDeliveryCodeIdSet = useMemo(
    () => new Set(selectedOrderDeliveryCodeIds),
    [selectedOrderDeliveryCodeIds],
  );
  const selectableOrderDeliveryCodeIds = useMemo(
    () => visibleOrderDeliveryCodes.filter((code) => isOrderDeliveryCodeDeletable(code)).map((code) => code.id),
    [visibleOrderDeliveryCodes],
  );
  const selectableOrderDeliveryCodeIdSet = useMemo(
    () => new Set(selectableOrderDeliveryCodeIds),
    [selectableOrderDeliveryCodeIds],
  );
  const orderDeliveryCodeTotalPages = useMemo(
    () => Math.max(1, Math.ceil(visibleOrderDeliveryCodes.length / ORDER_DELIVERY_CODE_PAGE_SIZE)),
    [visibleOrderDeliveryCodes.length],
  );
  const activeOrderDeliveryCodePage = Math.max(
    1,
    Math.min(orderDeliveryCodePage, orderDeliveryCodeTotalPages),
  );
  const pagedOrderDeliveryCodes = useMemo(() => {
    const startIndex = (activeOrderDeliveryCodePage - 1) * ORDER_DELIVERY_CODE_PAGE_SIZE;
    return visibleOrderDeliveryCodes.slice(startIndex, startIndex + ORDER_DELIVERY_CODE_PAGE_SIZE);
  }, [activeOrderDeliveryCodePage, visibleOrderDeliveryCodes]);
  const selectableCurrentPageOrderDeliveryCodeIds = useMemo(
    () =>
      pagedOrderDeliveryCodes.filter((code) => isOrderDeliveryCodeDeletable(code)).map((code) => code.id),
    [pagedOrderDeliveryCodes],
  );
  const selectedVisibleOrderDeliveryCodeCount = useMemo(
    () => selectedOrderDeliveryCodeIds.filter((id) => selectableOrderDeliveryCodeIdSet.has(id)).length,
    [selectableOrderDeliveryCodeIdSet, selectedOrderDeliveryCodeIds],
  );
  const allCurrentPageOrderDeliveryCodesSelected =
    selectableCurrentPageOrderDeliveryCodeIds.length > 0 &&
    selectableCurrentPageOrderDeliveryCodeIds.every((id) => selectedOrderDeliveryCodeIdSet.has(id));
  const allVisibleOrderDeliveryCodesSelected =
    selectableOrderDeliveryCodeIds.length > 0 &&
    selectableOrderDeliveryCodeIds.every((id) => selectedOrderDeliveryCodeIdSet.has(id));
  const orderDeliveryCodePageItems = useMemo(
    () => buildCompactPageItems(activeOrderDeliveryCodePage, orderDeliveryCodeTotalPages),
    [activeOrderDeliveryCodePage, orderDeliveryCodeTotalPages],
  );
  useEffect(() => {
    setOrderDeliveryCodePage(1);
    setSelectedOrderDeliveryCodeIds([]);
  }, [orderDeliveryKeyword]);

  useEffect(() => {
    const visibleDeletableCodeIdSet = new Set(
      visibleOrderDeliveryCodes.filter((code) => isOrderDeliveryCodeDeletable(code)).map((code) => code.id),
    );
    setSelectedOrderDeliveryCodeIds((current) =>
      current.filter((codeId) => visibleDeletableCodeIdSet.has(codeId)),
    );
  }, [visibleOrderDeliveryCodes]);

  useEffect(() => {
    setOrderDeliveryCodePage((current) => Math.min(Math.max(1, current), orderDeliveryCodeTotalPages));
  }, [orderDeliveryCodeTotalPages]);

  const visibleOrderDeliveryBots = useMemo(
    () =>
      orderDeliveryDashboard.bots.filter((bot) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          bot.clientName,
          bot.endpointUrl,
          bot.botKeyHint,
          bot.note,
        ),
      ),
    [orderDeliveryDashboard.bots, orderDeliveryKeyword],
  );
  const visibleOrderDeliveryAuditLogs = useMemo(
    () =>
      orderDeliveryDashboard.auditLogs.filter((item) =>
        orderDeliveryMatchesQuery(
          orderDeliveryKeyword,
          item.summary,
          item.detail,
          item.scope,
          item.action,
        ),
      ),
    [orderDeliveryDashboard.auditLogs, orderDeliveryKeyword],
  );

  const applyCurrentDevice = () => {
    setForm((current) => ({
      ...current,
      targetLabel: current.targetLabel || `主机本机 / ${hostState.currentDevice.deviceId}`,
      deviceFingerprintHash: hostState.currentDevice.fingerprintHash,
      deviceId: hostState.currentDevice.deviceId,
    }));
  };

  const handleSubmitHostLogin = async () => {
    const normalizedUsername = normalizeLicenseHostLoginUsername(hostLoginUsername);
    const normalizedPassword = hostLoginPassword.trim();

    if (!normalizedUsername) {
      setHostLoginErrorMessage('请输入企业账号。');
      return;
    }

    if (!normalizedPassword) {
      setHostLoginErrorMessage('请输入密码。');
      return;
    }

    setHostLoginBusy(true);
    setHostLoginErrorMessage(null);
    try {
      if (!isLicenseHostAllowedUsername(normalizedUsername) || normalizedPassword !== LICENSE_HOST_LOGIN_PASSWORD) {
        throw new Error('账号或密码不正确。');
      }
      if (isBrowserCloudConsoleView) {
        const loginResult = await requestBrowserCloudJson<BrowserAuthLoginResult>(
          '/api/admin/auth/login',
          {
            method: 'POST',
            body: JSON.stringify({
              email: resolveLicenseHostBrowserAdminEmail(normalizedUsername),
              password: normalizedPassword,
              currentVersion: LICENSE_HOST_BROWSER_APP_VERSION,
            }),
          },
          { requiresAuth: false },
        );
        if (!loginResult.sessionToken) {
          throw new Error('云端管理员登录成功，但没有返回有效会话。');
        }
        persistLicenseHostBrowserAuthToken(loginResult.sessionToken);
        await requestBrowserCloudJson<BrowserAdminOverviewPayload>('/api/admin/overview');
      }

      const nextSession = buildLicenseHostSession(normalizedUsername, hostLoginConfig.updatedAt);
      persistLicenseHostRememberedUsername(normalizedUsername, hostLoginRememberDevice);
      persistLicenseHostSession(nextSession);
      setHostLoginSession(nextSession);
      setHostLoginUsername(normalizedUsername);
      setHostLoginPassword('');
      setHostLoginPasswordVisible(false);
    } catch (error) {
      if (isBrowserCloudConsoleView) {
        clearLicenseHostBrowserAuthToken();
      }
      clearLicenseHostSession();
      setHostLoginErrorMessage(error instanceof Error ? error.message : '登录失败。');
    } finally {
      setHostLoginBusy(false);
    }
  };

  const handleHostLogout = () => {
    if (isBrowserCloudConsoleView) {
      clearLicenseHostBrowserAuthToken();
    }
    clearLicenseHostSession();
    setHostLoginSession(null);
    setHostLoginPassword('');
    setHostLoginPasswordVisible(false);
    persistLicenseHostRememberedUsername(
      hostLoginSession?.username ?? hostLoginUsername,
      hostLoginRememberDevice,
    );
    setHostLoginErrorMessage(null);
  };

  const handleCopy = async (text: string, successText: string) => {
    try {
      await copyText(text);
      setToastMessage(successText);
    } catch {
      setErrorMessage('复制失败，请检查系统剪贴板权限。');
    }
  };

  const handleHostRememberDeviceChange = (rememberDevice: boolean) => {
    setHostLoginRememberDevice(rememberDevice);
    persistLicenseHostRememberedUsername(hostLoginSession?.username ?? hostLoginUsername, rememberDevice);
    setToastMessage(rememberDevice ? '已记住控制台账号。' : '已取消记住控制台账号。');
  };

  const handleResetHostIssueForm = () => {
    setForm(createInitialForm());
    setGeneratedCode('');
    setToastMessage('本地签发表单已重置。');
  };

  const handleClearRemoteGrantPresets = () => {
    setRemoteGrantPresets({});
    setToastMessage('快速授权预设已清空。');
  };

  const handleIssue = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    try {
      const slotLimit = parseSlotLimitInput(form.slotLimitInput);
      const customDurationDays = parseCustomDurationDaysInput(
        form.durationKey,
        form.customDurationDaysInput,
      );
      const payload: IssueLicenseCodeInput = {
        durationKey: form.durationKey,
        customDurationDays,
        targetLabel: form.targetLabel,
        deviceFingerprintHash: form.deviceFingerprintHash,
        deviceId: form.deviceId || null,
        slotLimit,
        betaAccess: form.betaAccess,
        advancedBetaAccess: form.advancedBetaAccess,
        note: form.note || '',
      };
      setBusy('issuing');
      setErrorMessage(null);
      const result = await bridge.issueLicenseCode(payload);
      setHostState(result.state);
      setGeneratedCode(result.generatedCode ?? '');
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '生成授权码失败。');
    } finally {
      setBusy(null);
    }
  };

  const handleCopyRecordCode = async (record: LicenseHostIssuedRecord) => {
    if (!record.generatedCode) {
      setErrorMessage('这条旧签发记录没有保存完整授权码，当前版本只能查看预览，无法重新复制。');
      return;
    }

    await handleCopy(record.generatedCode, '该条授权码已复制。');
  };

  const handleCopyRecordDeviceInfo = async (record: LicenseHostIssuedRecord) => {
    await handleCopy(buildIssuedRecordDeviceInfo(record), '设备信息已复制。');
  };

  const refreshRemoteDashboard = async (silent = false) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    try {
      setRemoteBusy('refresh');
      const result = await bridge.getLicenseHostRemoteDashboard({
        query: '',
        clientLimit: REMOTE_CLIENT_QUERY_LIMIT,
        activityLimit: REMOTE_ACTIVITY_QUERY_LIMIT,
      });
      setRemoteDashboard(normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl));
      if (!silent) {
        setToastMessage(result.message);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '远程监控刷新失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const applyOrderDeliveryActionResult = (result: LicenseHostOrderDeliveryActionResult) => {
    setOrderDeliveryDashboard(result.topology);
    if (Array.isArray(result.generatedCodes)) {
      setGeneratedOrderDeliveryCodes(result.generatedCodes);
    }
    if (result.deliveryText) {
      void handleCopy(result.deliveryText, '发货文案已复制。');
    }
    setToastMessage(result.message);
  };

  const refreshOrderDeliveryDashboard = async (silent = false) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      setOrderDeliveryBusy('refresh');
      const result = await bridge.getLicenseHostOrderDeliveryDashboard();
      setOrderDeliveryDashboard(result.topology);
      if (!silent) {
        setToastMessage(result.message);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '授权码工作区刷新失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleSaveOrderDeliveryTier = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      const slotLimit = parseSlotLimitInput(orderDeliveryTierForm.slotLimitInput);
      const customDurationDays = parseCustomDurationDaysInput(
        orderDeliveryTierForm.durationKey,
        orderDeliveryTierForm.customDurationDaysInput,
      );
      setOrderDeliveryBusy('tier');
      const result = await bridge.saveLicenseHostOrderDeliveryTier({
        id: orderDeliveryTierForm.id || null,
        title: orderDeliveryTierForm.title,
        tierKey: orderDeliveryTierForm.tierKey || null,
        skuTitle: orderDeliveryTierForm.skuTitle || null,
        durationKey: orderDeliveryTierForm.durationKey,
        customDurationDays,
        slotLimit,
        licenseTypeLabel: orderDeliveryTierForm.licenseTypeLabel || null,
        tutorialUrl: orderDeliveryTierForm.tutorialUrl || null,
        noticeText: orderDeliveryTierForm.noticeText || null,
        signatureText: orderDeliveryTierForm.signatureText || null,
        autoDeliveryNote: orderDeliveryTierForm.autoDeliveryNote || null,
        enabled: orderDeliveryTierForm.enabled,
      } satisfies SaveXianyuTierInput);
      applyOrderDeliveryActionResult(result);
      setOrderDeliveryTierForm(createInitialOrderDeliveryTierForm());
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '保存档位失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleSaveOrderDeliveryTemplate = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      setOrderDeliveryBusy('template');
      const result = await bridge.saveLicenseHostOrderDeliveryTemplate({
        id: orderDeliveryTemplateForm.id || null,
        tierId: orderDeliveryTemplateForm.tierId || null,
        channel: 'xianyu',
        name: orderDeliveryTemplateForm.name,
        bodyTemplate: orderDeliveryTemplateForm.bodyTemplate,
        tutorialUrl: orderDeliveryTemplateForm.tutorialUrl || null,
        noticeText: orderDeliveryTemplateForm.noticeText || null,
        signatureText: orderDeliveryTemplateForm.signatureText || null,
        enabled: orderDeliveryTemplateForm.enabled,
        isDefault: orderDeliveryTemplateForm.isDefault,
      } satisfies SaveXianyuTemplateInput);
      applyOrderDeliveryActionResult(result);
      setOrderDeliveryTemplateForm(createInitialOrderDeliveryTemplateForm());
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '保存模板失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleSaveOrderDeliveryOrder = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      const quantity = Number.parseInt(orderDeliveryOrderForm.quantityInput || '1', 10);
      setOrderDeliveryBusy('order');
      const result = await bridge.saveLicenseHostOrderDeliveryOrder({
        id: orderDeliveryOrderForm.id || null,
        sourceOrderId: orderDeliveryOrderForm.sourceOrderId,
        buyerId: orderDeliveryOrderForm.buyerId || null,
        buyerName: orderDeliveryOrderForm.buyerName || null,
        itemTitle: orderDeliveryOrderForm.itemTitle,
        specName: orderDeliveryOrderForm.specName || null,
        specValue: orderDeliveryOrderForm.specValue || null,
        quantity: Number.isFinite(quantity) ? quantity : 1,
        amountText: orderDeliveryOrderForm.amountText || null,
        tierId: orderDeliveryOrderForm.tierId || null,
        rawPayloadJson: orderDeliveryOrderForm.rawPayloadJson || null,
      } satisfies SaveXianyuOrderInput);
      applyOrderDeliveryActionResult(result);
      setOrderDeliveryOrderForm(createInitialOrderDeliveryOrderForm());
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '保存订单失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleGenerateOrderDeliveryCodes = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      const customDurationDays = parseCustomDurationDaysInput(
        orderDeliveryCodeForm.durationKey,
        orderDeliveryCodeForm.customDurationDaysInput,
      );
      const parsedSlotLimit = parseSlotLimitInput(orderDeliveryCodeForm.slotLimitInput);
      const resolvedSlotLimit =
        parsedSlotLimit === undefined
          ? getDefaultSlotLimitByDuration(orderDeliveryCodeForm.durationKey)
          : parsedSlotLimit;
      const quantity = Math.max(
        1,
        Math.min(1000, Number.parseInt(orderDeliveryCodeForm.quantityInput || '10', 10) || 10),
      );
      setOrderDeliveryBusy('codes');
      const result = await bridge.generateLicenseHostOrderDeliveryCodes({
        channel: orderDeliveryCodeForm.channel,
        durationKey: orderDeliveryCodeForm.durationKey,
        customDurationDays: customDurationDays ?? null,
        slotLimit: resolvedSlotLimit ?? null,
        quantity,
        note: null,
      } satisfies GenerateXianyuCodesInput);
      applyOrderDeliveryActionResult(result);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '生成授权码失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const toggleOrderDeliveryCodeSelection = (codeId: string, checked: boolean) => {
    setSelectedOrderDeliveryCodeIds((current) => {
      if (checked) {
        return current.includes(codeId) ? current : [...current, codeId];
      }
      return current.filter((item) => item !== codeId);
    });
  };

  const selectOrderDeliveryCodes = (codeIds: string[]) => {
    setSelectedOrderDeliveryCodeIds((current) => Array.from(new Set([...current, ...codeIds])));
  };

  const handleDeleteOrderDeliveryCodes = async (codeIds: string[]) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    const uniqueIds = Array.from(new Set(codeIds.map((item) => item.trim()).filter(Boolean)));
    if (uniqueIds.length === 0) {
      setErrorMessage('请先选择要删除的未使用授权码。');
      return;
    }

    const confirmed = window.confirm(
      uniqueIds.length === 1
        ? '确定删除这 1 个未使用授权码吗？已分配或已绑定的记录不会被删除。'
        : `确定删除选中的 ${uniqueIds.length} 个未使用授权码吗？已分配或已绑定的记录不会被删除。`,
    );
    if (!confirmed) {
      return;
    }

    try {
      setOrderDeliveryBusy('codes-delete');
      const result = await bridge.deleteLicenseHostOrderDeliveryCodes({
        codeIds: uniqueIds,
      } satisfies DeleteXianyuInventoryCodesInput);
      applyOrderDeliveryActionResult(result);
      setSelectedOrderDeliveryCodeIds((current) =>
        current.filter((item) => !uniqueIds.includes(item)),
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '删除授权码失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleExportOrderDeliveryCodes = () => {
    const exportCodes =
      selectedVisibleOrderDeliveryCodeCount > 0
        ? visibleOrderDeliveryCodes.filter((code) => selectedOrderDeliveryCodeIdSet.has(code.id))
        : visibleOrderDeliveryCodes;

    if (exportCodes.length === 0) {
      setErrorMessage('当前没有可导出的授权码。');
      return;
    }

    try {
      const now = new Date();
      const fileStamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
        now.getDate(),
      ).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(
        now.getSeconds(),
      ).padStart(2, '0')}`;
      const consoleUrl =
        bridge?.mode === 'browser'
          ? `${window.location.origin}${window.location.pathname}`
          : hostState.remoteSourceUrl || LICENSE_HOST_BROWSER_API_URL;
      const scopeLabel =
        selectedVisibleOrderDeliveryCodeCount > 0
          ? `已选 ${exportCodes.length} 条`
          : orderDeliveryKeyword
            ? `筛选结果 ${exportCodes.length} 条`
            : `全部库存 ${exportCodes.length} 条`;
      const html = buildOrderDeliveryInventoryExportHtml({
        consoleUrl,
        exportedAt: formatFullDateTime(now.toISOString()),
        query: orderDeliveryQuery.trim(),
        scopeLabel,
        codes: exportCodes,
      });
      downloadSpreadsheetFile(`AINIVOX-授权码库存-${fileStamp}.xls`, html);
      setToastMessage(`已导出 ${exportCodes.length} 条授权码。`);
    } catch {
      setErrorMessage('导出 Excel 失败，请稍后重试。');
    }
  };

  const handleFulfillOrderDeliveryOrder = async (orderId: string) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      setOrderDeliveryBusy('fulfill');
      const result = await bridge.fulfillLicenseHostOrderDeliveryOrder({
        orderId,
        templateId: null,
      } satisfies FulfillXianyuOrderInput);
      applyOrderDeliveryActionResult(result);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '生成分配文案失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleSaveOrderDeliveryBot = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    try {
      setOrderDeliveryBusy('bot');
      const result = await bridge.saveLicenseHostOrderDeliveryBot({
        id: orderDeliveryBotForm.id || null,
        clientName: orderDeliveryBotForm.clientName,
        endpointUrl: orderDeliveryBotForm.endpointUrl || null,
        botKeyHint: orderDeliveryBotForm.botKeyHint || null,
        status: orderDeliveryBotForm.status || 'idle',
        enabled: orderDeliveryBotForm.enabled,
        note: orderDeliveryBotForm.note || null,
      } satisfies SaveXianyuBotClientInput);
      applyOrderDeliveryActionResult(result);
      setOrderDeliveryBotForm(createInitialOrderDeliveryBotForm());
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '保存通道失败。');
    } finally {
      setOrderDeliveryBusy(null);
    }
  };

  const handleLoadOrderDeliveryTier = (tierId: string) => {
    const target = orderDeliveryDashboard.tiers.find((item) => item.id === tierId);
    if (!target) {
      return;
    }
    setOrderDeliveryTierForm({
      id: target.id,
      title: target.title,
      tierKey: target.tierKey,
      skuTitle: target.skuTitle ?? '',
      durationKey: target.durationKey,
      customDurationDaysInput: target.customDurationDays ? String(target.customDurationDays) : '',
      slotLimitInput: target.slotLimit === null ? '无限' : target.slotLimit?.toString() ?? '',
      licenseTypeLabel: target.licenseTypeLabel,
      tutorialUrl: target.tutorialUrl ?? '',
      noticeText: target.noticeText ?? '',
      signatureText: target.signatureText ?? '',
      autoDeliveryNote: target.autoDeliveryNote ?? '',
      enabled: target.enabled,
    });
  };

  const handleLoadOrderDeliveryTemplate = (templateId: string) => {
    const target = orderDeliveryDashboard.templates.find((item) => item.id === templateId);
    if (!target) {
      return;
    }
    setOrderDeliveryTemplateForm({
      id: target.id,
      tierId: target.tierId ?? '',
      name: target.name,
      bodyTemplate: target.bodyTemplate,
      tutorialUrl: target.tutorialUrl ?? '',
      noticeText: target.noticeText ?? '',
      signatureText: target.signatureText ?? '',
      enabled: target.enabled,
      isDefault: target.isDefault,
    });
  };

  const handleLoadOrderDeliveryOrder = (orderId: string) => {
    const target = orderDeliveryDashboard.orders.find((item) => item.id === orderId);
    if (!target) {
      return;
    }
    setOrderDeliveryOrderForm({
      id: target.id,
      sourceOrderId: target.sourceOrderId,
      buyerId: target.buyerId ?? '',
      buyerName: target.buyerName ?? '',
      itemTitle: target.itemTitle,
      specName: target.specName ?? '',
      specValue: target.specValue ?? '',
      quantityInput: String(target.quantity),
      amountText: target.amountText ?? '',
      tierId: target.tierId ?? '',
      rawPayloadJson: '',
    });
  };

  const handleLoadOrderDeliveryBot = (botId: string) => {
    const target = orderDeliveryDashboard.bots.find((item) => item.id === botId);
    if (!target) {
      return;
    }
    setOrderDeliveryBotForm({
      id: target.id,
      clientName: target.clientName,
      endpointUrl: target.endpointUrl ?? '',
      botKeyHint: target.botKeyHint ?? '',
      status: target.status,
      enabled: target.enabled,
      note: target.note ?? '',
    });
  };

  const getRemoteActionTarget = (groupOverride?: LicenseHostRemoteClientGroup | null) => {
    const targetGroup = groupOverride ?? selectedRemoteGroup;
    const targetClient = targetGroup?.primaryClient ?? null;
    return {
      targetGroup,
      targetClient,
    };
  };

  const handlePersistRemoteGrantPreset = (group: LicenseHostRemoteClientGroup | null | undefined) => {
    const presetKey = buildRemoteGrantPresetKey(group);
    if (!presetKey) {
      return;
    }

    const nextPreset: RemoteGrantPreset = {
      durationKey: remoteForm.durationKey,
      customDurationDaysInput: remoteForm.customDurationDaysInput,
      slotLimitInput: remoteForm.slotLimitInput,
      betaAccess: remoteForm.betaAccess,
      advancedBetaAccess: remoteForm.advancedBetaAccess,
      note: remoteForm.note,
    };
    setRemoteGrantPresets((current) => ({
      ...current,
      [presetKey]: nextPreset,
    }));
  };

  const handleMarkActivitiesRead = async (activityIds: string[]) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    const normalizedIds = Array.from(new Set(activityIds.filter(Boolean)));
    if (normalizedIds.length === 0) {
      return;
    }

    try {
      const result = await bridge.markLicenseHostActivitiesRead({ activityIds: normalizedIds });
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '更新远程活动已读状态失败。');
    }
  };

  const getUnreadRemoteLoginActivityIds = (group: LicenseHostRemoteClientGroup): string[] =>
    remoteDashboard.loginEvents
      .filter((event) => event.unread && remoteLoginEventMatchesGroup(event, group))
      .map((event) => event.activityId)
      .filter((activityId): activityId is string => Boolean(activityId));

  const handleOpenFeedbackConversation = (entry: FeedbackEntry) => {
    const matchedGroup =
      remoteGroups.find((group) => feedbackEntryMatchesGroup(entry, group)) ?? null;
    if (matchedGroup) {
      setSelectedRemoteGroupId(matchedGroup.id);
    }
    setSelectedFeedbackId(entry.id);
    setFeedbackReplyDraft('');
    if (entry.status === 'pending') {
      void handleMarkFeedbackRead(entry.id, true);
    }
  };

  const handleSelectCustomerGroup = (group: LicenseHostRemoteClientGroup) => {
    setSelectedRemoteGroupId(group.id);
  };

  const handleSelectFeedbackGroup = (group: LicenseHostRemoteClientGroup) => {
    setSelectedRemoteGroupId(group.id);
    const nextFeedbackEntry = feedbackGroupSummaries.get(group.id)?.entries[0] ?? null;
    if (nextFeedbackEntry) {
      handleOpenFeedbackConversation(nextFeedbackEntry);
      return;
    }
    setSelectedFeedbackId(null);
    setFeedbackReplyDraft('');
  };

  const handleMarkFeedbackRead = async (feedbackId: string, silent = false) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    if (!feedbackId) {
      return;
    }

    try {
      setFeedbackActionBusy('read');
      const result = await withFeedbackActionTimeout(
        bridge.markFeedbackRead({ feedbackId } satisfies MarkFeedbackReadInput),
        '同步客户消息状态超时。',
      );
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      if (!silent) {
        setToastMessage('已把当前反馈标记为已读。');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '更新反馈已读状态失败。');
    } finally {
      setFeedbackActionBusy((current) => (current === 'read' ? null : current));
    }
  };

  const handleAcknowledgeFeedback = async (feedbackId?: string | null, message?: string) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    const nextMessage = message?.trim() ? message.trim() : undefined;
    const feedbackTarget = !feedbackId ? buildSelectedFeedbackTarget() : null;
    if (!feedbackId && !feedbackTarget) {
      setErrorMessage('当前客户缺少可用账号标识，暂时无法主动发起会话。');
      return;
    }

    const canUseOptimistic = Boolean(feedbackId && nextMessage);
    const optimisticId = `feedback-host-local-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    if (canUseOptimistic && feedbackId && nextMessage) {
      setOptimisticFeedbackMessages((current) => [
        ...current,
        {
          id: optimisticId,
          feedbackId,
          body: nextMessage,
          createdAt: new Date().toISOString(),
          state: 'sending',
          errorMessage: null,
        },
      ]);
    }

    try {
      setFeedbackActionBusy('reply');
      const result = await withFeedbackActionTimeout(
        bridge.acknowledgeFeedback({
          feedbackId: feedbackId ?? null,
          ...(feedbackTarget ?? {}),
          message: nextMessage,
        } satisfies AcknowledgeFeedbackInput),
        '发送客服回复超时。',
      );
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      if (canUseOptimistic) {
        setOptimisticFeedbackMessages((current) =>
          current.filter((entry) => entry.id !== optimisticId),
        );
      }
      if (!feedbackId || selectedFeedbackId === feedbackId) {
        setFeedbackReplyDraft('');
      }
      setToastMessage(result.message);
    } catch (error) {
      if (canUseOptimistic) {
        const errorMessage = error instanceof Error ? error.message : '发送客服回复失败。';
        setOptimisticFeedbackMessages((current) =>
          current.map((entry) =>
            entry.id === optimisticId
              ? {
                  ...entry,
                  state: 'failed',
                  errorMessage,
                }
              : entry,
          ),
        );
      }
      setErrorMessage(error instanceof Error ? error.message : '发送客服回复失败。');
    } finally {
      setFeedbackActionBusy((current) => (current === 'reply' ? null : current));
    }
  };

  const handleFeedbackReplyKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    if (
      feedbackActionBusy === 'reply' ||
      !feedbackReplyDraft.trim() ||
      (!selectedFeedbackEntry && !buildSelectedFeedbackTarget())
    ) {
      return;
    }

    void handleAcknowledgeFeedback(selectedFeedbackEntry?.id ?? null, feedbackReplyDraft);
  };

  const handleOpenQuickGrant = (group: LicenseHostRemoteClientGroup) => {
    setSelectedRemoteGroupId(group.id);
    setQuickGrantGroupId(group.id);
    const unreadLoginIds = getUnreadRemoteLoginActivityIds(group);
    if (unreadLoginIds.length > 0) {
      void handleMarkActivitiesRead(unreadLoginIds);
    }
  };

  const handleOpenQuickGrantFromLoginEvent = (event: LicenseHostLoginEvent) => {
    const targetGroup =
      remoteGroups.find((group) => group.id === event.groupId) ??
      remoteGroups.find((group) => remoteLoginEventMatchesGroup(event, group)) ??
      null;
    if (!targetGroup) {
      setErrorMessage('当前登录事件还没有匹配到客户分组，请先刷新远程列表。');
      return;
    }

    setSelectedRemoteGroupId(targetGroup.id);
    setQuickGrantGroupId(targetGroup.id);
    if (event.unread && event.activityId) {
      void handleMarkActivitiesRead([event.activityId]);
    }
  };

  const handleRemoteIssueLicense = async (groupOverride?: LicenseHostRemoteClientGroup | null) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }

    try {
      const slotLimit = parseSlotLimitInput(remoteForm.slotLimitInput);
      const customDurationDays = parseCustomDurationDaysInput(
        remoteForm.durationKey,
        remoteForm.customDurationDaysInput,
      );
      const payload: IssueRemoteLicenseInput = {
        durationKey: remoteForm.durationKey,
        customDurationDays,
        targetLabel: targetGroup?.title ?? getRemoteClientTitle(targetClient),
        deviceFingerprintHash: targetClient.fingerprintHash,
        deviceId: targetClient.deviceId,
        slotLimit,
        betaAccess: remoteForm.betaAccess,
        advancedBetaAccess: remoteForm.advancedBetaAccess,
        note: remoteForm.note || '',
      };
      if (targetGroup?.id) {
        setSelectedRemoteGroupId(targetGroup.id);
      }
      setRemoteBusy('issue-license');
      setErrorMessage(null);
      const result = await bridge.issueRemoteLicense(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      handlePersistRemoteGrantPreset(targetGroup);
      setQuickGrantGroupId(null);
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '远程授权下发失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteSetUpdateChannel = async (
    nextBetaAccess: boolean,
    nextAdvancedBetaAccess = remoteForm.advancedBetaAccess,
    groupOverride?: LicenseHostRemoteClientGroup | null,
  ) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }

    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }

    const previousRemoteAccess = {
      betaAccess: remoteForm.betaAccess,
      advancedBetaAccess: remoteForm.advancedBetaAccess,
    };
    setRemoteForm((current) => ({
      ...current,
      betaAccess: nextBetaAccess,
      advancedBetaAccess: nextBetaAccess ? nextAdvancedBetaAccess : false,
    }));

    try {
      const payload: SetRemoteUpdateChannelInput = {
        targetLabel: targetGroup?.title ?? getRemoteClientTitle(targetClient),
        deviceFingerprintHash: targetClient.fingerprintHash,
        deviceId: targetClient.deviceId,
        updateChannel: nextBetaAccess ? 'beta' : 'stable',
        allowEngineerAccess: nextBetaAccess && nextAdvancedBetaAccess,
        note: remoteForm.note || '',
      };
      if (targetGroup?.id) {
        setSelectedRemoteGroupId(targetGroup.id);
      }
      setRemoteBusy('set-update-channel');
      setErrorMessage(null);
      const result = await bridge.setRemoteUpdateChannel(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
    } catch (error) {
      setRemoteForm((current) => ({
        ...current,
        betaAccess: previousRemoteAccess.betaAccess,
        advancedBetaAccess: previousRemoteAccess.advancedBetaAccess,
      }));
      setErrorMessage(error instanceof Error ? error.message : '切换远程更新通道失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteRevokeLicense = async (groupOverride?: LicenseHostRemoteClientGroup | null) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }

    try {
      const payload: RevokeRemoteLicenseInput = {
        targetLabel: targetGroup?.title ?? getRemoteClientTitle(targetClient),
        deviceFingerprintHash: targetClient.fingerprintHash,
        deviceId: targetClient.deviceId,
        note: remoteForm.note || '',
      };
      if (targetGroup?.id) {
        setSelectedRemoteGroupId(targetGroup.id);
      }
      setRemoteBusy('revoke-license');
      setErrorMessage(null);
      const result = await bridge.revokeRemoteLicense(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '移除远程授权失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteReleaseAccountRestriction = async (
    groupOverride?: LicenseHostRemoteClientGroup | null,
  ) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户端设备。');
      return;
    }

    try {
      const payload: ReleaseRemoteAccountRestrictionInput = {
        targetLabel: targetGroup?.title ?? getRemoteClientTitle(targetClient),
        deviceFingerprintHash: targetClient.fingerprintHash,
        deviceId: targetClient.deviceId,
        note: remoteForm.note || '',
      };
      if (targetGroup?.id) {
        setSelectedRemoteGroupId(targetGroup.id);
      }
      setRemoteBusy('release-account-restriction');
      setErrorMessage(null);
      const result = await bridge.releaseRemoteAccountRestriction(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '解除当前设备异常限制失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteResetPassword = async (groupOverride?: LicenseHostRemoteClientGroup | null) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetGroup || !targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }

    try {
      const nextPassword = remoteForm.passwordInput.trim();
      if (nextPassword.length < 6) {
        throw new Error('请先填写至少 6 位的新密码。');
      }

      const payload: ResetRemoteAccountPasswordInput = {
        userId: targetGroup.userId,
        email: targetGroup.email,
        displayName: targetGroup.displayName,
        nextPassword,
        note: remoteForm.note || '',
      };
      setRemoteBusy('reset-password');
      setErrorMessage(null);
      const result = await bridge.resetRemotePassword(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
      setRemoteForm((current) => ({ ...current, passwordInput: '' }));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '重置远程账号密码失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteResetSecondaryPassword = async (
    groupOverride?: LicenseHostRemoteClientGroup | null,
  ) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetGroup || !targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }

    try {
      const payload: ResetRemoteSecondaryPasswordInput = {
        userId: targetGroup.userId,
        email: targetGroup.email,
        displayName: targetGroup.displayName,
        note: remoteForm.note || '',
      };
      setRemoteBusy('reset-secondary-password');
      setErrorMessage(null);
      const result = await bridge.resetRemoteSecondaryPassword(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '重置远程二次密码失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleCopyRemoteFingerprint = async () => {
    if (!selectedRemoteGroup || !selectedRemoteClient) {
      return;
    }

    await handleCopy(
      buildRemoteFingerprintInfo(selectedRemoteGroup, selectedRemoteClient),
      '客户完整指纹信息已复制。',
    );
  };

  const handleCopyRemoteSnapshot = async () => {
    if (!selectedRemoteGroup || !selectedRemoteClient || !selectedRemoteSnapshot) {
      setErrorMessage('当前没有可复制的客户端空间快照。');
      return;
    }

    await handleCopy(
      buildRemoteSnapshotExport(selectedRemoteGroup, selectedRemoteClient, selectedRemoteSnapshot),
      '客户端空间快照已复制。',
    );
  };

  const handleImportRemoteSnapshot = async () => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    if (!selectedRemoteGroup || !selectedRemoteClient || !selectedRemoteSnapshot) {
      setErrorMessage('当前没有可提取的客户端空间卡片。');
      return;
    }
    if (!selectedRemoteSnapshot.transferPackage) {
      setErrorMessage(
        buildRemoteSnapshotImportUnavailableMessage(selectedRemoteClient, selectedRemoteSnapshot),
      );
      return;
    }

    try {
      setRemoteBusy('import-profile');
      setErrorMessage(null);
      const result = await bridge.importProfileTransferPackage(
        selectedRemoteSnapshot.transferPackage,
        {
          senderHostLabel: `${selectedRemoteGroup.title} / 客户端回传`,
        },
      );
      setRemoteProfiles(result.state.profiles);
      setRemoteForm((current) => ({
        ...current,
        profileId: result.importedProfileId || current.profileId,
      }));
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '提取客户端空间卡片失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  const handleRemoteIssueTransfer = async (groupOverride?: LicenseHostRemoteClientGroup | null) => {
    if (!bridge) {
      setErrorMessage('授权控制台桥接尚未就绪，请重新打开当前主机入口。');
      return;
    }
    const { targetGroup, targetClient } = getRemoteActionTarget(groupOverride);
    if (!targetClient) {
      setErrorMessage('请先从远程列表里选中一个客户设备。');
      return;
    }
    if (!remoteForm.profileId) {
      setErrorMessage('请先选择要传送的空间卡片。');
      return;
    }

    try {
      const payload: IssueRemoteProfileTransferInput = {
        profileId: remoteForm.profileId,
        targetLabel: targetGroup?.title ?? getRemoteClientTitle(targetClient),
        deviceFingerprintHash: targetClient.fingerprintHash,
        deviceId: targetClient.deviceId,
        note: remoteForm.note || '',
      };
      if (targetGroup?.id) {
        setSelectedRemoteGroupId(targetGroup.id);
      }
      setRemoteBusy('issue-transfer');
      setErrorMessage(null);
      const result = await bridge.issueRemoteProfileTransfer(payload);
      setRemoteDashboard(
        normalizeRemoteDashboard(result.dashboard, hostState.remoteSourceUrl, remoteQuery.trim()),
      );
      setToastMessage(result.message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '空间卡片传送失败。');
    } finally {
      setRemoteBusy(null);
    }
  };

  useEffect(() => {
    if (!hostConsoleUnlocked || !hostState.remoteAdminConfigured) {
      return;
    }

    const timer = window.setInterval(() => {
      void refreshRemoteDashboard(true);
    }, 20_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [bridge, hostConsoleUnlocked, hostState.remoteAdminConfigured]);

  const handleOpenExternalLink = async (targetUrl: string, fallbackMessage: string) => {
    try {
      if (bridge?.openExternal) {
        const result = await bridge.openExternal(targetUrl);
        if (!result.ok) {
          setErrorMessage(result.error ?? fallbackMessage);
        }
        return;
      }

      if (typeof window !== 'undefined') {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : fallbackMessage);
    }
  };

  const activeTrendMax = Math.max(...LICENSE_HOST_WORKSPACE_MOCK.activeTrend.map((item) => item.value), 1);
  const licenseTrendMax = Math.max(...LICENSE_HOST_WORKSPACE_MOCK.licenseTrend.map((item) => item.value), 1);

  const renderOverviewSection = () => {
    const licenseUsageTotal = LICENSE_HOST_WORKSPACE_MOCK.licenseTrend.reduce(
      (sum, item) => sum + item.value,
      0,
    );
    const waveGeometry = buildOverviewWaveGeometry(LICENSE_HOST_WORKSPACE_MOCK.flowTrend);

    return (
      <div className="license-host-erp__view license-host-erp__view--overview">
        <section className="license-host-erp__kpi-grid">
          {overviewKpis.map((item) => {
            const Icon = item.icon;
            const progressWidth =
              item.tone === 'blue'
                ? '78%'
                : item.tone === 'indigo'
                  ? '62%'
                  : item.tone === 'amber'
                    ? hostConsolePendingCount > 0
                      ? '46%'
                      : '18%'
                    : restrictedGroupCount > 0
                      ? '34%'
                      : '20%';

            return (
              <article key={item.label} className={`license-host-erp__kpi-card is-${item.tone}`}>
                <div className="license-host-erp__kpi-head">
                  <div className="license-host-erp__kpi-icon">
                    <Icon size={18} />
                  </div>
                  <span>{item.label}</span>
                </div>
                <div className="license-host-erp__kpi-copy">
                  <strong>{item.value}</strong>
                  <small>{item.note}</small>
                </div>
                <div className="license-host-erp__kpi-progress">
                  <span style={{ width: progressWidth }} />
                </div>
              </article>
            );
          })}
        </section>

        <div className="license-host-erp__overview-main">
          <section className="license-host-erp__panel license-host-erp__panel--chart">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>用户活跃趋势</strong>
                <span>当前保持 mock 结构，后续可以直接替换成真实趋势源。</span>
              </div>
              <LineChart size={16} />
            </div>
            <div className="license-host-wave">
              <svg
                className="license-host-wave__svg"
                viewBox={`0 0 ${waveGeometry.width} ${waveGeometry.height}`}
                role="img"
                aria-label="实时活跃波形"
              >
                <defs>
                  <linearGradient id="licenseHostWaveArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {waveGeometry.ticks.map((tick) => (
                  <g key={tick.label}>
                    <line
                      className="license-host-wave__grid"
                      x1={waveGeometry.gridStartX}
                      x2={waveGeometry.gridEndX}
                      y1={tick.y}
                      y2={tick.y}
                    />
                    <text className="license-host-wave__tick" x="10" y={tick.y + 4}>
                      {tick.label}
                    </text>
                  </g>
                ))}
                <path className="license-host-wave__area" d={waveGeometry.areaPath} />
                <path className="license-host-wave__line" d={waveGeometry.linePath} />
                {waveGeometry.points.map((point, index) => (
                  <text
                    key={LICENSE_HOST_WORKSPACE_MOCK.flowTrend[index].label}
                    className="license-host-wave__axis-label"
                    x={point.x}
                    y={waveGeometry.height - 10}
                  >
                    {LICENSE_HOST_WORKSPACE_MOCK.flowTrend[index].label}
                  </text>
                ))}
              </svg>
            </div>

            <div className="license-host-chart license-host-chart--wide">
              {LICENSE_HOST_WORKSPACE_MOCK.activeTrend.map((item, index) => (
                <div key={item.label} className="license-host-chart__item">
                  <div className="license-host-chart__track">
                    <span
                      className={`license-host-chart__bar ${getOverviewBarTone(index)}`}
                      style={{
                        animationDelay: `${index * 55}ms`,
                        height: `${(item.value / activeTrendMax) * 100}%`,
                      }}
                    />
                  </div>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="license-host-erp__overview-side">
            <section className="license-host-erp__panel">
              <div className="license-host-erp__panel-head">
                <div>
                  <strong>授权消耗趋势</strong>
                  <span>维持可替换的真实图表位，当前由前端 mock 数据承接。</span>
                </div>
                <BarChart3 size={16} />
              </div>
              <div className="license-host-erp__trend-stack">
                {LICENSE_HOST_WORKSPACE_MOCK.licenseTrend.map((item, index) => (
                  <article key={item.label} className="license-host-erp__trend-item">
                    <div className="license-host-erp__trend-copy">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                    <div className="license-host-erp__trend-track">
                      <span
                        className="license-host-erp__trend-fill"
                        style={{
                          animationDelay: `${index * 55}ms`,
                          width: `${(item.value / licenseTrendMax) * 100}%`,
                        }}
                      />
                    </div>
                  </article>
                ))}
              </div>
              <div className="license-host-erp__trend-summary">
                <span>本周累计</span>
                <strong>{licenseUsageTotal}</strong>
              </div>
            </section>

            <section className="license-host-erp__panel">
              <div className="license-host-erp__panel-head">
                <div>
                  <strong>系统状态摘要</strong>
                  <span>主机、远程连接和当前设备状态统一收口。</span>
                </div>
                <ServerCog size={16} />
              </div>
              <div className="license-host-erp__status-list">
                {systemStatusItems.map((item) => (
                  <article key={item.label} className="license-host-erp__status-item">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                    <small>{item.note}</small>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </div>

        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>待处理提醒</strong>
              <span>登录提醒、反馈回执和异常限制统一进入同一工作台。</span>
            </div>
            <BellRing size={16} />
          </div>
          {overviewReminders.length > 0 ? (
            <div className="license-host-erp__notice-list">
              {overviewReminders.map((item) => (
                <article key={item.title} className="license-host-erp__notice">
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </article>
              ))}
            </div>
          ) : (
            <div className="license-host__empty">当前没有需要优先处理的提醒。</div>
          )}
        </section>
      </div>
    );
  };

  const renderCustomerListPanel = (mode: 'customers' | 'feedback') => (
    <section className="license-host-erp__panel">
      <div className="license-host-erp__panel-head">
        <div>
          <strong>客户列表</strong>
        </div>
        <span className="license-host-erp__badge">{filteredRemoteGroups.length}</span>
      </div>
      <div className="license-host-erp__panel-body license-host-erp__panel-body--scroll">
        {filteredRemoteGroups.length > 0 ? (
          <div className="license-host__remote-client-list license-host-erp__customers-list">
            {filteredRemoteGroups.map((group) => {
              const feedbackSummary = feedbackGroupSummaries.get(group.id);
              const unreadFeedbackCount = feedbackSummary?.unreadCount ?? 0;
              const latestFeedbackEntry = feedbackSummary?.latestEntry ?? null;
              const hasUnreadFeedback = unreadFeedbackCount > 0;
              return (
                <article
                  key={group.id}
                  className={`license-host__remote-client-card${
                    selectedRemoteGroup?.id === group.id ? ' is-selected' : ''
                  }${hasUnreadFeedback ? ' has-feedback-unread' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (mode === 'feedback') {
                      handleSelectFeedbackGroup(group);
                      return;
                    }
                    handleSelectCustomerGroup(group);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (mode === 'feedback') {
                        handleSelectFeedbackGroup(group);
                        return;
                      }
                      handleSelectCustomerGroup(group);
                    }
                  }}
                >
                  <div className="license-host__remote-client-head">
                    <div className="license-host-erp__client-card-primary">
                      <div className="license-host__remote-client-title">
                        <span className={`license-host__presence-dot is-${group.primaryClient.presence}`} />
                        <strong>{group.title}</strong>
                        <span
                          className={`license-host__remote-access-badge is-${getRemoteAccessBadgeTone(group.primaryClient.accessBadge)}`}
                        >
                          {group.primaryClient.accessBadge.label}
                        </span>
                      </div>
                      <div className="license-host-erp__client-card-account">
                        {getRemoteGroupAccountLabel(group)}
                      </div>
                    </div>
                    <div className="license-host-erp__client-card-head-meta">
                      <span className="license-host__remote-client-presence">
                        {getRemotePresenceLabel(group.primaryClient.presence)}
                      </span>
                      {hasUnreadFeedback ? (
                        <span className="license-host-erp__dot-badge" aria-label={`${unreadFeedbackCount} 条未读消息`}>
                          {unreadFeedbackCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="license-host-erp__client-card-meta">
                    <span>{group.deviceCount} 台设备</span>
                    <span>
                      最近登录 {group.latestLoginAt ? formatDateTime(group.latestLoginAt) : '未记录'}
                    </span>
                  </div>
                  {latestFeedbackEntry ? (
                    <div className="license-host-erp__client-card-message">
                      {hasUnreadFeedback ? '新消息' : '最近会话'} ·{' '}
                      {latestFeedbackEntry.previewText || latestFeedbackEntry.body || '客户发送了一条新反馈。'}
                    </div>
                  ) : null}
                  <div className="license-host__remote-client-foot license-host-erp__client-card-foot">
                    <span className="license-host-erp__client-card-license">
                      {group.primaryClient.effectiveLicense.remainingLabel}
                    </span>
                    {mode === 'customers' && group.unreadLoginCount > 0 ? (
                      <button
                        type="button"
                        className="sidebar-button license-host-erp__quick-grant"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleOpenQuickGrant(group);
                        }}
                      >
                        快速授权
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="license-host__empty">当前搜索条件下还没有找到客户分组。</div>
        )}
      </div>
    </section>
  );

  const renderFeedbackThreadPanel = ({
    title = '客服聊天',
    idleHint = '从左侧客户列表或右侧收信箱进入对应会话。',
    emptyHint = '当前客户还没有反馈会话，或先从右侧收信箱打开一个对话。',
  }: {
    title?: string;
    idleHint?: string;
    emptyHint?: string;
  } = {}) => (
    <section className="license-host-erp__panel license-host-erp__panel--chat-main">
      <div className="license-host-erp__panel-head">
        <div>
          <strong>{title}</strong>
          <span>
            {activeFeedbackEntry
              ? `${
                  activeFeedbackEntry.senderDisplayName ??
                  activeFeedbackEntry.senderEmail ??
                  '匿名客户'
                } · ${activeFeedbackEntry.messages.length} 条消息`
              : idleHint}
          </span>
        </div>
        <div className="license-host-erp__chat-head-actions">
          {hasActiveFeedbackThread ? (
            <span className={`license-host__feedback-status is-${activeFeedbackEntry.status}`}>
              {activeFeedbackEntry.status === 'acknowledged'
                ? '客服已回复'
                : activeFeedbackEntry.status === 'read'
                  ? '客服已读'
                  : '待客服查看'}
            </span>
          ) : null}
          {selectedRemoteGroup ? (
            <span className="license-host-erp__badge license-host-erp__badge--muted">
              当前客户 {selectedRemoteFeedbackEntries.length}
            </span>
          ) : null}
        </div>
      </div>

      <div className="license-host-erp__panel-body">
        {activeFeedbackEntry ? (
          <>
            <div className="license-host-erp__chat-meta">
              <strong>
                {activeFeedbackEntry.senderDisplayName ??
                  activeFeedbackEntry.senderEmail ??
                  '匿名客户'}
              </strong>
              <span>
                {activeFeedbackEntry.senderDeviceId ?? '未记录设备'} / 首次反馈于{' '}
                {formatDateTime(activeFeedbackEntry.createdAt)}
              </span>
            </div>

            <div ref={feedbackThreadRef} className="license-host__feedback-thread">
              {visibleSelectedFeedbackMessages.length > 0 ? (
                visibleSelectedFeedbackMessages.map((message) => (
                  <article key={message.id} className={`license-host__feedback-message-row is-${message.sender}`}>
                    <span
                      className={`license-host__feedback-avatar${
                        message.sender === 'host' ? ' is-host' : ''
                      }${
                        message.sender === 'user' && activeFeedbackEntry.senderAvatarImageDataUrl
                          ? ' has-image'
                          : ''
                      }`}
                      aria-hidden="true"
                    >
                      {message.sender === 'host' ? (
                        <img src={appIconSrc} alt="" />
                      ) : activeFeedbackEntry.senderAvatarImageDataUrl ? (
                        <img src={activeFeedbackEntry.senderAvatarImageDataUrl} alt="" />
                      ) : (
                        <span>
                          {getAvatarFallbackText(
                            activeFeedbackEntry.senderDisplayName,
                            activeFeedbackEntry.senderEmail,
                            activeFeedbackEntry.senderAvatarText,
                          )}
                        </span>
                      )}
                    </span>
                    <div className={`license-host__feedback-message is-${message.sender}`}>
                      <div className="license-host__feedback-message-head">
                        <strong>
                          {message.sender === 'host'
                            ? FEEDBACK_SERVICE_DISPLAY_NAME
                            : activeFeedbackEntry.senderDisplayName ??
                              activeFeedbackEntry.senderEmail ??
                              '客户'}
                        </strong>
                        <span>{formatDateTime(message.createdAt)}</span>
                      </div>
                      <div className="license-host__feedback-message-body">{message.body}</div>
                      <div className="license-host__feedback-message-foot">
                        <span
                          className={`license-host__feedback-message-status${
                            message.deliveryState === '发送中'
                              ? ' is-sending'
                              : message.deliveryState === '发送失败'
                                ? ' is-failed'
                                : ''
                          }`}
                          title={message.errorMessage ?? undefined}
                        >
                          {message.deliveryState}
                        </span>
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <div className="license-host__feedback-empty">当前会话还没有可显示的历史消息。</div>
              )}
            </div>

            <div className="license-host__feedback-composer">
              <textarea
                className="license-host__textarea license-host__feedback-textarea"
                value={feedbackReplyDraft}
                onChange={(event) => setFeedbackReplyDraft(event.target.value)}
                onKeyDown={handleFeedbackReplyKeyDown}
                placeholder="支持 Enter 发送，Shift + Enter 换行。"
              />
              <div className="license-host__record-actions">
                {hasActiveFeedbackThread && activeFeedbackEntry.status === 'pending' ? (
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    disabled={feedbackActionBusy !== null}
                    onClick={() => {
                      void handleMarkFeedbackRead(activeFeedbackEntry.id);
                    }}
                  >
                    <Mail size={14} />
                    {feedbackActionBusy === 'read' ? '处理中...' : '标记已读'}
                  </button>
                ) : null}
                {hasActiveFeedbackThread ? (
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    disabled={feedbackActionBusy === 'reply'}
                    onClick={() => {
                      void handleAcknowledgeFeedback(activeFeedbackEntry.id);
                    }}
                  >
                    <CheckCheck size={14} />
                    {feedbackActionBusy === 'reply' ? '发送中...' : '回执已收到'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="sidebar-button"
                  disabled={
                    feedbackActionBusy === 'reply' ||
                    !feedbackReplyDraft.trim() ||
                    (!hasActiveFeedbackThread && !proactiveFeedbackTarget)
                  }
                  onClick={() => {
                    void handleAcknowledgeFeedback(activeFeedbackEntry.id || null, feedbackReplyDraft);
                  }}
                >
                  <MessageSquare size={14} />
                  {feedbackActionBusy === 'reply' ? '发送中...' : '发送回复'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="license-host__empty">{emptyHint}</div>
        )}
      </div>
    </section>
  );

  const renderCustomerActionsPanel = () => {
    if (!selectedRemoteGroup || !selectedRemoteClient) {
      return (
        <section className="license-host-erp__panel">
          <div className="license-host__empty">先从左侧选一个客户分组。</div>
        </section>
      );
    }

    return (
      <section className="license-host-erp__panel">
        <div className="license-host-erp__customer-top">
          <div className="license-host-erp__customer-overview">
            <div className="license-host-erp__customer-hero">
              <div className="license-host-erp__customer-identity">
                <div className="license-host-erp__customer-avatar" aria-hidden="true">
                  {getAvatarFallbackText(selectedRemoteGroup.title, selectedRemoteGroup.email, null)}
                </div>
                <div className="license-host-erp__customer-copy">
              <div className="license-host-erp__customer-title-row">
                <strong>{selectedRemoteGroup.title}</strong>
                <span
                  className={`license-host__remote-access-badge is-${getRemoteAccessBadgeTone(
                    selectedRemoteClient.accessBadge,
                  )}`}
                >
                  {selectedRemoteClient.accessBadge.label}
                </span>
                <span className="license-host__remote-chip">{selectedRemoteClient.effectiveLicense.remainingLabel}</span>
              </div>
              <p className="license-host-erp__customer-meta">
                {getRemoteGroupAccountLabel(selectedRemoteGroup)} / {selectedRemoteGroup.deviceCount} 台设备 /
                最近登录{' '}
                {selectedRemoteGroup.latestLoginAt
                  ? formatDateTime(selectedRemoteGroup.latestLoginAt)
                  : '未记录'}
              </p>
            </div>
          </div>
        </div>

        <div className="license-host-erp__summary-grid license-host-erp__summary-grid--customer">
            <article className="license-host-erp__summary-card">
              <span>当前授权</span>
              <strong>{selectedRemoteClient.effectiveLicense.licenseTypeLabel}</strong>
              <small>{selectedRemoteClient.machineName || selectedRemoteClient.deviceId}</small>
            </article>
              <article className="license-host-erp__summary-card">
                <span>更新通道</span>
                <strong>{getRemoteUpdateChannelLabel(selectedRemoteUpdateChannel)}</strong>
                <small>
                  {selectedRemoteUpdateChannel.updatedAt
                    ? formatDateTime(selectedRemoteUpdateChannel.updatedAt)
                    : '未由主机接管'}
                </small>
              </article>
              <article className="license-host-erp__summary-card">
                <span>客户端版本</span>
                <strong>{selectedRemoteClient.clientVersion ?? '未上报'}</strong>
                <small>
                  {selectedRemoteClient.hostLabel} / {selectedRemoteClient.platformLabel}
                </small>
              </article>
            <article className="license-host-erp__summary-card">
              <span>异常限制</span>
              <strong>{selectedRemoteClient.accountRestrictionUntil ? '限制中' : '正常'}</strong>
              <small>
                {selectedRemoteClient.accountRestrictionUntil
                    ? formatDateTime(selectedRemoteClient.accountRestrictionUntil)
                    : '当前设备可正常登录与注册'}
                </small>
              </article>
            </div>

            {renderCustomerAssetsPanel(true)}
          </div>

          <aside className="license-host-erp__action-panel">
            <label className="license-host__field">
              <span>远程授权时长</span>
              <div className="license-host__duration-grid license-host-erp__duration-grid--compact">
                {hostState.supportedDurations.map((option) => (
                  <button
                    key={`remote-${option.key}`}
                    type="button"
                    className={`license-host__duration-chip license-host__duration-chip--compact${
                      remoteForm.durationKey === option.key ? ' is-active' : ''
                    }`}
                    onClick={() =>
                      setRemoteForm((current) => ({
                        ...current,
                        durationKey: option.key,
                      }))
                    }
                  >
                    <strong>{option.label}</strong>
                  </button>
                ))}
              </div>
            </label>

            {remoteForm.durationKey === 'custom-days' ? (
              <label className="license-host__field">
                <span>自定义天数</span>
                <input
                  className="license-input"
                  value={remoteForm.customDurationDaysInput}
                  onChange={(event) =>
                    setRemoteForm((current) => ({
                      ...current,
                      customDurationDaysInput: event.target.value,
                    }))
                  }
                  placeholder={`输入 1-${MAX_CUSTOM_DURATION_DAYS} 天`}
                />
              </label>
            ) : null}

            <div className="license-host__remote-field-grid license-host-erp__action-grid">
              <label className="license-host__field">
                <span>卡片槽位数</span>
                <input
                  className="license-input"
                  value={remoteForm.slotLimitInput}
                  onChange={(event) =>
                    setRemoteForm((current) => ({
                      ...current,
                      slotLimitInput: event.target.value,
                    }))
                  }
                  placeholder={`输入 1-${MAX_SLOT_LIMIT} 或 无限`}
                />
              </label>
              <label className="license-host__field">
                <span>重置后的密码</span>
                <input
                  className="license-input"
                  value={remoteForm.passwordInput}
                  onChange={(event) =>
                    setRemoteForm((current) => ({
                      ...current,
                      passwordInput: event.target.value,
                    }))
                  }
                  placeholder="至少 6 位"
                />
              </label>
            </div>

            <label className="license-host__field license-host-erp__action-note-field">
              <span>备注</span>
              <textarea
                className="license-host__textarea license-host__textarea--compact"
                value={remoteForm.note}
                onChange={(event) =>
                  setRemoteForm((current) => ({
                    ...current,
                    note: event.target.value,
                  }))
                }
                placeholder="客户名、用途、导入提醒"
              />
            </label>

            <div className="license-host-erp__action-footer">
              <div className="license-host-erp__action-switches">
                <div className="license-host__remote-switch-row">
                  <div className="license-host__toggle-copy">
                    <strong>内测通道</strong>
                  </div>
                  <label className="settings-switch" aria-label="切换远程内测通道">
                    <input
                      className="settings-switch__input"
                      type="checkbox"
                      checked={remoteForm.betaAccess}
                      disabled={remoteBusy !== null}
                      onChange={(event) => {
                        void handleRemoteSetUpdateChannel(
                          event.target.checked,
                          event.target.checked ? remoteForm.advancedBetaAccess : false,
                        );
                      }}
                    />
                    <span className="settings-switch__track" />
                    <span className="settings-switch__thumb" />
                  </label>
                </div>

                <div className="license-host__remote-switch-row">
                  <div className="license-host__toggle-copy">
                    <strong>高级内测</strong>
                  </div>
                  <label className="settings-switch" aria-label="切换高级内测">
                    <input
                      className="settings-switch__input"
                      type="checkbox"
                      checked={remoteForm.advancedBetaAccess}
                      disabled={remoteBusy !== null}
                      onChange={(event) => {
                        void handleRemoteSetUpdateChannel(
                          event.target.checked ? true : remoteForm.betaAccess,
                          event.target.checked,
                        );
                      }}
                    />
                    <span className="settings-switch__track" />
                    <span className="settings-switch__thumb" />
                  </label>
                </div>
              </div>

              <div className="license-host__quick-actions license-host__quick-actions--dense license-host-erp__action-buttons">
                <button
                  type="button"
                  className="sidebar-button"
                  onClick={() => {
                    void handleRemoteIssueLicense();
                  }}
                  disabled={remoteBusy !== null || !selectedRemoteClient.canIssueRemotely}
                >
                  <KeyRound size={14} />
                  {remoteBusy === 'issue-license' ? '下发中...' : '下发授权'}
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    void handleRemoteRevokeLicense();
                  }}
                  disabled={remoteBusy !== null}
                >
                  <ShieldOff size={14} />
                  {remoteBusy === 'revoke-license' ? '移除中...' : '移除授权'}
                </button>
                {selectedRemoteClient.accountRestrictionUntil ? (
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => {
                      void handleRemoteReleaseAccountRestriction();
                    }}
                    disabled={remoteBusy !== null}
                  >
                    <ShieldCheck size={14} />
                    {remoteBusy === 'release-account-restriction' ? '解除中...' : '解除异常'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    void handleRemoteResetPassword();
                  }}
                  disabled={remoteBusy !== null}
                >
                  <KeyRound size={14} />
                  {remoteBusy === 'reset-password' ? '重置中...' : '重置密码'}
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    void handleRemoteResetSecondaryPassword();
                  }}
                  disabled={remoteBusy !== null}
                >
                  <KeyRound size={14} />
                  {remoteBusy === 'reset-secondary-password' ? '重置中...' : '重置二次密码'}
                </button>
              </div>
            </div>

            {!selectedRemoteClient.canIssueRemotely ? (
              <div className="license-host-erp__action-note license-host__empty">
                这个客户设备还没有绑定在线账号。
              </div>
            ) : null}
          </aside>
        </div>
      </section>
    );
  };

  const renderCustomerAssetsPanel = (embedded = false) => {
    if (!selectedRemoteGroup || !selectedRemoteClient) {
      return (
        <section
          className={`${embedded ? 'license-host-erp__embedded-panel' : 'license-host-erp__panel'} license-host-erp__space-panel`}
        >
          <div className="license-host__empty">先从左侧选一个客户分组。</div>
        </section>
      );
    }

    return (
      <section
        className={`${embedded ? 'license-host-erp__embedded-panel' : 'license-host-erp__panel'} license-host-erp__space-panel`}
      >
        <div className="license-host-erp__panel-head">
          <div>
            <strong>空间卡片</strong>
          </div>
        </div>

        <div className="license-host-erp__panel-body license-host-erp__space-panel-body">
          <div className="license-host-erp__space-layout">
            <div className="license-host__remote-snapshot-list license-host-erp__space-card-list">
              {selectedRemoteSnapshots.length > 0 ? (
                selectedRemoteSnapshots.map((snapshot) => {
                  const snapshotState = getRemoteSnapshotStateMeta(snapshot);
                  return (
                    <article
                      key={snapshot.id}
                      className={`license-host__remote-snapshot-card${
                        selectedRemoteSnapshot?.id === snapshot.id ? ' is-selected' : ''
                      } is-${snapshotState.tone}-state`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedRemoteSnapshotId(snapshot.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedRemoteSnapshotId(snapshot.id);
                        }
                      }}
                    >
                      <div className="license-host__remote-snapshot-row">
                        <strong>{getRemoteSnapshotTitle(snapshot)}</strong>
                        <span>{snapshotState.label}</span>
                      </div>
                      <div className="license-host__remote-snapshot-row">
                        <span>{snapshot.workspaceName ?? snapshot.name}</span>
                        <span>{formatRemainingPercent(snapshot.overallRemainingPercent)}</span>
                      </div>
                    </article>
                  );
                })
              ) : (
                <div className="license-host__empty">当前还没有回调空间数据。</div>
              )}
            </div>

            <div className="license-host-erp__space-preview">
              <div className="license-host-erp__space-preview-top">
                <div className="license-host-erp__space-preview-main">
                  <div className="license-host-erp__space-preview-head">
                    <strong>
                      {selectedRemoteSnapshot ? getRemoteSnapshotTitle(selectedRemoteSnapshot) : '还没有选中空间'}
                    </strong>
                    <span>
                      {selectedRemoteClient.profileSnapshotUpdatedAt
                        ? formatDateTime(selectedRemoteClient.profileSnapshotUpdatedAt)
                        : '等待回调'}
                    </span>
                  </div>
                  {selectedRemoteSnapshot ? (
                    <>
                      <div className="license-host__remote-device-meta">
                        {selectedRemoteSnapshotState ? (
                          <span
                            className={`license-host__remote-chip license-host__remote-chip--${selectedRemoteSnapshotState.tone}`}
                          >
                            {selectedRemoteSnapshotState.label}
                          </span>
                        ) : null}
                        {getRemoteSnapshotSecurityChips(selectedRemoteSnapshot).map((chip) => (
                          <span
                            key={chip.key}
                            className={`license-host__remote-chip license-host__remote-chip--${chip.tone}`}
                          >
                            {chip.label}
                          </span>
                        ))}
                        <span className="license-host__remote-chip">
                          {getPlanLabel(selectedRemoteSnapshot.planType)}
                        </span>
                        <span>{formatRemoteAuthStatusLabel(selectedRemoteSnapshot.authStatus)}</span>
                      </div>
                      {getRemoteSnapshotSecurityDetail(selectedRemoteSnapshot) ? (
                        <div className="license-host__remote-snapshot-copy">
                          {getRemoteSnapshotSecurityDetail(selectedRemoteSnapshot)}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="license-host__empty">请选择一个空间卡片查看详情。</div>
                  )}
                </div>

                <label className="license-host__field license-host-erp__space-note-field license-host-erp__space-note-field--top">
                  <span>备注</span>
                  <textarea
                    className="license-host__textarea license-host__textarea--compact"
                    value={remoteForm.note}
                    onChange={(event) =>
                      setRemoteForm((current) => ({
                        ...current,
                        note: event.target.value,
                      }))
                    }
                    placeholder="可写客户名、用途、导入提醒"
                  />
                </label>
              </div>

              <div className="license-host__remote-field-grid license-host-erp__space-form-grid">
                <label className="license-host__field license-host-erp__space-select-field">
                  <span>要传送的本地空间</span>
                  <select
                    className="license-input"
                    value={remoteForm.profileId}
                    onChange={(event) =>
                      setRemoteForm((current) => ({
                        ...current,
                        profileId: event.target.value,
                      }))
                    }
                  >
                    <option value="">请选择空间卡片</option>
                    {remoteProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.workspaceName || profile.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="license-host__quick-actions license-host__quick-actions--dense license-host-erp__space-actions">
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => {
                      void handleRemoteIssueTransfer();
                    }}
                    disabled={remoteBusy !== null || !selectedRemoteClient.canIssueRemotely || !selectedRemoteProfile}
                  >
                    <Sparkles size={14} />
                    {remoteBusy === 'issue-transfer' ? '正在传送空间卡片' : '传送空间卡片'}
                  </button>
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => {
                      void handleImportRemoteSnapshot();
                    }}
                    disabled={remoteBusy !== null || !selectedRemoteSnapshot}
                  >
                    <Sparkles size={14} />
                    {remoteBusy === 'import-profile' ? '正在提取回主机' : '提取客户端卡片'}
                  </button>
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => {
                      void handleCopyRemoteSnapshot();
                    }}
                    disabled={remoteBusy !== null || !selectedRemoteSnapshot}
                  >
                    <Copy size={14} />
                    复制回调
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderCustomerContextAside = () => (
    <aside className="license-host-erp__customers-context">
      <section className="license-host-erp__panel license-host-erp__panel--events">
        <div className="license-host-erp__panel-head">
          <div>
            <strong>登录事件</strong>
          </div>
          <Wifi size={16} />
        </div>
        <div className="license-host-erp__panel-body license-host-erp__panel-body--scroll">
          {visibleRemoteLoginEvents.length > 0 ? (
            <div className="license-host__remote-activity-list">
              {visibleRemoteLoginEvents.map((event) => {
                const canQuickGrant = remoteGroups.some((group) => remoteLoginEventMatchesGroup(event, group));
                return (
                  <article
                    key={event.id}
                    className={`license-host__remote-activity-card${event.unread ? ' is-unread' : ''}`}
                  >
                    <div className="license-host__remote-device-row">
                      <div className="license-host__remote-client-title">
                        <strong>{event.groupTitle ?? getRemoteLoginEventAccountLabel(event)}</strong>
                        <span
                          className={`license-host__remote-access-badge is-${getRemoteAccessBadgeTone(event.accessBadge)}`}
                        >
                          {event.accessBadge.label}
                        </span>
                      </div>
                      <span>{formatDateTime(event.createdAt)}</span>
                    </div>
                    <div className="license-host__record-note">{event.summary}</div>
                    {event.detail ? (
                      <div className="license-host__remote-device-meta">
                        <span>{event.detail}</span>
                      </div>
                    ) : null}
                    <div className="license-host__record-actions">
                      <button
                        type="button"
                        className="sidebar-button license-host-erp__quick-grant"
                        disabled={!canQuickGrant}
                        onClick={() => handleOpenQuickGrantFromLoginEvent(event)}
                      >
                        快速授权
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="license-host__empty">当前还没有新的登录事件。</div>
          )}
        </div>
      </section>

      <section className="license-host-erp__panel license-host-erp__panel--activity">
        <div className="license-host-erp__panel-head">
          <div>
            <strong>最近活动</strong>
          </div>
          <ServerCog size={16} />
        </div>
        <div className="license-host-erp__panel-body license-host-erp__panel-body--scroll">
          {selectedRemoteGroup?.recentActivities.length ? (
            <div className="license-host__remote-activity-list">
              {selectedRemoteGroup.recentActivities.map((activity) => (
                <article
                  key={activity.id}
                  className={`license-host__remote-activity-card${activity.unread ? ' is-unread' : ''}`}
                >
                  <div className="license-host__remote-device-row">
                    <strong>{activity.eventLabel}</strong>
                    <span>{formatDateTime(activity.createdAt)}</span>
                  </div>
                  <div className="license-host__record-note">{activity.summary}</div>
                  {activity.detail ? (
                    <div className="license-host__remote-device-meta">
                      <span>{activity.detail}</span>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          ) : (
            <div className="license-host__empty">当前客户还没有更多最近活动。</div>
          )}
        </div>
      </section>
    </aside>
  );

  const renderFeedbackInboxAside = () => (
    <aside className="license-host-erp__customers-context license-host-erp__customers-context--feedback">
      <section className="license-host-erp__panel license-host-erp__panel--inbox">
        <div className="license-host-erp__panel-head">
          <div>
            <strong>收信箱</strong>
            <span>新消息会在这里保留提醒和缩略信息，点击直接跳到对应聊天。</span>
          </div>
          {remoteDashboard.unreadFeedbackCount > 0 ? (
            <span className="license-host-erp__badge license-host-erp__badge--danger">
              {remoteDashboard.unreadFeedbackCount}
            </span>
          ) : null}
        </div>
        <div className="license-host-erp__panel-body license-host-erp__panel-body--scroll">
          {feedbackInboxEntries.length > 0 ? (
            <div className="license-host-erp__inbox-list">
              {feedbackInboxEntries.map(({ entry, group }) => (
                <article
                  key={entry.id}
                  className={`license-host__feedback-entry license-host-erp__inbox-entry${
                    activeFeedbackEntry?.id === entry.id ? ' is-selected' : ''
                  }${entry.status === 'pending' ? ' is-unread' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenFeedbackConversation(entry)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOpenFeedbackConversation(entry);
                    }
                  }}
                >
                  <div className="license-host__record-row">
                    <strong>{group?.title ?? entry.senderDisplayName ?? entry.senderEmail ?? '匿名反馈'}</strong>
                    <span>{formatDateTime(entry.latestMessageAt || entry.createdAt)}</span>
                  </div>
                  <div className="license-host-erp__inbox-entry-account">
                    {group ? getRemoteGroupAccountLabel(group) : entry.senderDeviceId ?? '未归组会话'}
                  </div>
                  <div className="license-host__record-note">
                    {entry.previewText || entry.body || '客户发送了一条新反馈。'}
                  </div>
                  <div className="license-host__record-row">
                    <span>{entry.senderDeviceId ?? '未记录设备'}</span>
                    <span className={`license-host__feedback-status is-${entry.status}`}>
                      {entry.status === 'acknowledged'
                        ? '已回执'
                        : entry.status === 'read'
                          ? '已读'
                          : '未读'}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="license-host__empty">当前还没有客户消息进入收信箱。</div>
          )}
        </div>
      </section>
    </aside>
  );

  const renderCustomersSection = () => {
    if (!hostState.remoteAdminConfigured) {
      return (
        <div className="license-host-erp__view">
          <section className="license-host-erp__panel">
            <div className="license-host__empty">当前还没有配置远程授权服务地址。</div>
          </section>
        </div>
      );
    }

    return (
      <div className="license-host-erp__view license-host-erp__view--customers">
        <div className="license-host-erp__customers">
          {renderCustomerListPanel('customers')}
          <div className="license-host-erp__customers-main">
            {renderCustomerActionsPanel()}
            <div className="license-host-erp__customers-main-grid">
              {renderFeedbackThreadPanel({
                title: '客户聊天',
                idleHint: '选择左侧客户后，可在这里继续处理这个客户的聊天记录。',
                emptyHint: '当前客户还没有聊天记录。',
              })}
            </div>
          </div>
          {renderCustomerContextAside()}
        </div>
      </div>
    );
  };

  const renderCodeGenerationSection = () => {
    if (!hostState.remoteAdminConfigured) {
      return (
        <div className="license-host-erp__view">
          <section className="license-host-erp__panel">
            <div className="license-host__empty">当前还没有配置远程授权服务地址。</div>
          </section>
        </div>
      );
    }

    return (
      <div className="license-host-erp__view license-host-erp__view--codegen">
        <div className="license-host-order__metric-grid">
          {[
            ['库存授权码', orderDeliveryDashboard.summary.unusedCodes],
            ['已分配', orderDeliveryDashboard.summary.deliveredCodes],
            ['已绑定', orderDeliveryDashboard.summary.redeemedCodes],
            ['本次生成', generatedOrderDeliveryCodes.length],
          ].map(([label, value]) => (
            <article key={label} className="license-host-order__metric-card">
              <span>{label}</span>
              <strong>{value}</strong>
            </article>
          ))}
        </div>

        <div className="license-host-codegen__grid">
          <section className="license-host-erp__panel license-host-erp__panel--scroll">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>生成授权码</strong>
              </div>
              <QrCode size={16} />
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>通道</span>
                <select
                  className="license-input"
                  value={orderDeliveryCodeForm.channel}
                  onChange={(event) =>
                    setOrderDeliveryCodeForm((current) => ({
                      ...current,
                      channel: event.target.value as XianyuCodeChannel,
                    }))
                  }
                >
                  {ORDER_DELIVERY_CODE_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="license-host__field">
                <span>生成数量</span>
                <input
                  className="license-input"
                  value={orderDeliveryCodeForm.quantityInput}
                  onChange={(event) =>
                    setOrderDeliveryCodeForm((current) => ({
                      ...current,
                      quantityInput: event.target.value,
                    }))
                  }
                  placeholder="1-1000"
                />
              </label>
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>授权时长</span>
                <select
                  className="license-input"
                  value={orderDeliveryCodeForm.durationKey}
                  onChange={(event) =>
                    setOrderDeliveryCodeForm((current) => ({
                      ...current,
                      durationKey: event.target.value as HostLicenseDurationKey,
                      customDurationDaysInput:
                        event.target.value === 'custom-days'
                          ? current.customDurationDaysInput
                          : '',
                      slotLimitInput: String(
                        getDefaultSlotLimitByDuration(
                          event.target.value as HostLicenseDurationKey,
                        ) ?? 4,
                      ),
                    }))
                  }
                >
                  {hostState.supportedDurations.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="license-host__field">
                <span>卡槽位数</span>
                <input
                  className="license-input"
                  value={orderDeliveryCodeForm.slotLimitInput}
                  onChange={(event) =>
                    setOrderDeliveryCodeForm((current) => ({
                      ...current,
                      slotLimitInput: event.target.value,
                    }))
                  }
                  placeholder="例如：4 或 无限"
                />
              </label>
            </div>
            {orderDeliveryCodeForm.durationKey === 'custom-days' ? (
              <label className="license-host__field">
                <span>自定义天数</span>
                <input
                  className="license-input"
                  value={orderDeliveryCodeForm.customDurationDaysInput}
                  onChange={(event) =>
                    setOrderDeliveryCodeForm((current) => ({
                      ...current,
                      customDurationDaysInput: event.target.value,
                    }))
                  }
                  placeholder={`输入 1-${MAX_CUSTOM_DURATION_DAYS} 天`}
                />
              </label>
            ) : null}
            <div className="license-host-codegen__summary">
              <span>{orderDeliveryCodeChannelLabel}</span>
              <span>{orderDeliveryCodeDurationLabel}</span>
              <span>{orderDeliveryCodeSlotLabel}</span>
            </div>
            <div className="license-host__quick-actions license-host__quick-actions--dense">
              <button
                type="button"
                className="sidebar-button"
                disabled={orderDeliveryBusy === 'codes' || orderDeliveryBusy === 'codes-delete'}
                onClick={() => {
                  void handleGenerateOrderDeliveryCodes();
                }}
              >
                <RefreshCw size={14} className={orderDeliveryBusy === 'codes' ? 'spin' : ''} />
                {orderDeliveryBusy === 'codes' ? '生成中...' : '生成授权码'}
              </button>
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                disabled={generatedOrderDeliveryCodes.length === 0}
                onClick={() => {
                  void handleCopy(generatedOrderDeliveryCodes.join('\n'), '本次授权码已复制。');
                }}
              >
                <Copy size={14} />
                复制本次
              </button>
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                disabled={orderDeliveryBusy === 'refresh' || orderDeliveryBusy === 'codes-delete'}
                onClick={() => {
                  void refreshOrderDeliveryDashboard();
                }}
              >
                <RefreshCw size={14} className={orderDeliveryBusy === 'refresh' ? 'spin' : ''} />
                刷新库存
              </button>
            </div>
            {generatedOrderDeliveryCodes.length > 0 ? (
              <div className="license-host-codegen__summary license-host-codegen__summary--inline">
                <span>最近生成 {generatedOrderDeliveryCodes.length} 个</span>
              </div>
            ) : null}
          </section>

          <section className="license-host-erp__panel license-host-codegen__inventory-panel">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>库存</strong>
              </div>
              <QrCode size={16} />
            </div>
            <div className="license-host-codegen__inventory-toolbar">
              <div className="license-host-codegen__inventory-toolbar-copy">
                <span>共 {visibleOrderDeliveryCodes.length} 条</span>
                <span>已选 {selectedVisibleOrderDeliveryCodeCount} 条</span>
                <span>仅未使用可删除</span>
              </div>
              <div className="license-host__quick-actions license-host__quick-actions--dense license-host-codegen__inventory-toolbar-actions">
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={
                    orderDeliveryBusy === 'codes-delete' ||
                    selectableCurrentPageOrderDeliveryCodeIds.length === 0 ||
                    allCurrentPageOrderDeliveryCodesSelected
                  }
                  onClick={() => {
                    selectOrderDeliveryCodes(selectableCurrentPageOrderDeliveryCodeIds);
                  }}
                >
                  全选本页
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={
                    orderDeliveryBusy === 'codes-delete' ||
                    selectableOrderDeliveryCodeIds.length === 0 ||
                    allVisibleOrderDeliveryCodesSelected
                  }
                  onClick={() => {
                    selectOrderDeliveryCodes(selectableOrderDeliveryCodeIds);
                  }}
                >
                  全选结果
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={selectedOrderDeliveryCodeIds.length === 0}
                  onClick={() => {
                    setSelectedOrderDeliveryCodeIds([]);
                  }}
                >
                  取消选择
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  disabled={visibleOrderDeliveryCodes.length === 0 || orderDeliveryBusy === 'codes-delete'}
                  onClick={handleExportOrderDeliveryCodes}
                >
                  <Download size={14} />
                  导出 Excel
                </button>
                <button
                  type="button"
                  className="sidebar-button license-host-codegen__danger-button"
                  disabled={selectedOrderDeliveryCodeIds.length === 0 || orderDeliveryBusy === 'codes-delete'}
                  onClick={() => {
                    void handleDeleteOrderDeliveryCodes(selectedOrderDeliveryCodeIds);
                  }}
                >
                  <Trash2 size={14} />
                  {orderDeliveryBusy === 'codes-delete' ? '删除中...' : '批量删除'}
                </button>
              </div>
            </div>
            <div className="license-host-codegen__inventory-body">
              <div className="license-host-codegen__inventory-list">
                {pagedOrderDeliveryCodes.length > 0 ? (
                  pagedOrderDeliveryCodes.map((item) => {
                    const canDelete = isOrderDeliveryCodeDeletable(item);
                    return (
                      <article key={item.id} className="license-host-codegen__inventory-item">
                        <div className="license-host-codegen__inventory-head">
                          <label className="license-host-codegen__inventory-select" aria-label={`选择授权码 ${item.codePreview}`}>
                            <input
                              type="checkbox"
                              checked={selectedOrderDeliveryCodeIdSet.has(item.id)}
                              disabled={!canDelete || orderDeliveryBusy === 'codes-delete'}
                              onChange={(event) => {
                                toggleOrderDeliveryCodeSelection(item.id, event.target.checked);
                              }}
                            />
                          </label>
                          <div className="license-host-codegen__inventory-content">
                            <div className="license-host__record-row">
                              {item.codeValue ? (
                                <button
                                  type="button"
                                  className="license-host-codegen__copy-link"
                                  onClick={() => {
                                    void handleCopy(item.codeValue ?? '', '授权码已复制。');
                                  }}
                                >
                                  {item.codeValue}
                                </button>
                              ) : (
                                <span className="license-host-codegen__copy-link is-muted">{item.codePreview}</span>
                              )}
                              <div className="license-host__record-actions">
                                <span className={`license-host-order__status is-${orderDeliveryStatusTone(item.status)}`}>
                                  {orderDeliveryStatusLabel(item.status)}
                                </span>
                                {canDelete ? (
                                  <button
                                    type="button"
                                    className="sidebar-button sidebar-button--secondary license-host-codegen__row-action"
                                    disabled={orderDeliveryBusy === 'codes-delete'}
                                    onClick={() => {
                                      void handleDeleteOrderDeliveryCodes([item.id]);
                                    }}
                                  >
                                    <Trash2 size={14} />
                                    删除
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            <div className="license-host-codegen__inventory-meta">
                              <span>{item.tierTitle ?? '未命名规格'}</span>
                              <span>批次 {item.batchId}</span>
                              <span>{`${getOrderDeliveryCodeOwnerMeta(item)} ${getOrderDeliveryCodeOwnerLabel(item)}`}</span>
                              {item.orderId ? <span>订单 {item.orderId}</span> : null}
                              <span>{formatDateTime(item.redeemedAt ?? item.deliveredAt ?? item.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="license-host__empty">
                    {orderDeliveryKeyword ? '当前筛选下没有授权码。' : '当前还没有库存授权码。'}
                  </div>
                )}
              </div>
            </div>
            {visibleOrderDeliveryCodes.length > 0 ? (
              <div className="license-host-codegen__inventory-pagination">
                <span>{`第 ${activeOrderDeliveryCodePage} / ${orderDeliveryCodeTotalPages} 页`}</span>
                <div className="license-host-codegen__inventory-pagination-actions">
                  <button
                    type="button"
                    className="license-host-codegen__page-button"
                    disabled={activeOrderDeliveryCodePage <= 1}
                    onClick={() => {
                      setOrderDeliveryCodePage((current) => Math.max(1, current - 1));
                    }}
                  >
                    上一页
                  </button>
                  {orderDeliveryCodePageItems.map((item, index) =>
                    item === 'ellipsis' ? (
                      <span
                        key={`ellipsis-${activeOrderDeliveryCodePage}-${index}`}
                        className="license-host-codegen__page-ellipsis"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`license-host-codegen__page-button${
                          item === activeOrderDeliveryCodePage ? ' is-active' : ''
                        }`}
                        onClick={() => {
                          setOrderDeliveryCodePage(item);
                        }}
                      >
                        {item}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    className="license-host-codegen__page-button"
                    disabled={activeOrderDeliveryCodePage >= orderDeliveryCodeTotalPages}
                    onClick={() => {
                      setOrderDeliveryCodePage((current) =>
                        Math.min(orderDeliveryCodeTotalPages, current + 1),
                      );
                    }}
                  >
                    下一页
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    );
  };

  const renderOrdersSection = () => {
    if (!hostState.remoteAdminConfigured) {
      return (
        <div className="license-host-erp__view">
          <section className="license-host-erp__panel">
            <div className="license-host__empty">当前还没有配置远程授权服务地址。</div>
          </section>
        </div>
      );
    }

    return (
      <div className="license-host-erp__view license-host-erp__view--orders">
        <div className="license-host-order__metric-grid">
          {[
            ['待分配', orderDeliveryDashboard.summary.pendingOrders],
            ['已分配', orderDeliveryDashboard.summary.fulfilledOrders],
            ['模板', orderDeliveryDashboard.summary.totalTemplates],
            ['通道', orderDeliveryDashboard.summary.enabledBots],
          ].map(([label, value]) => (
            <article key={label} className="license-host-order__metric-card">
              <span>{label}</span>
              <strong>{value}</strong>
            </article>
          ))}
        </div>

        <div className="license-host-order__grid">
          <section className="license-host-erp__panel license-host-erp__panel--scroll">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>工作区状态</strong>
                <span>这里保留订单、模板、自动发货通道和审计记录。</span>
              </div>
              <BellRing size={16} />
            </div>
            <div className="license-host-erp__settings-list">
              <article className="license-host-erp__settings-item">
                <div>
                  <strong>远程管理源</strong>
                  <span>{hostState.remoteSourceUrl ?? '未配置'}</span>
                </div>
                <small>{orderDeliveryDashboard.tables.ready ? '数据表就绪' : '等待迁移'}</small>
              </article>
              <article className="license-host-erp__settings-item">
                <div>
                  <strong>模板状态</strong>
                  <span>{orderDeliveryDashboard.summary.totalTemplates} 套模板，支持默认模板和按档位套用。</span>
                </div>
                <small>{visibleOrderDeliveryTemplates.length} 条当前筛选结果</small>
              </article>
              <article className="license-host-erp__settings-item">
                <div>
                  <strong>自动发货通道</strong>
                  <span>{orderDeliveryDashboard.summary.enabledBots} 条通道已启用，人工核单流程继续保留。</span>
                </div>
                <small>{visibleOrderDeliveryBots.length} 条当前筛选结果</small>
              </article>
            </div>
            <div className="license-host__quick-actions license-host__quick-actions--dense">
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                disabled={orderDeliveryBusy === 'refresh'}
                onClick={() => {
                  void refreshOrderDeliveryDashboard();
                }}
              >
                <RefreshCw size={14} />
                {orderDeliveryBusy === 'refresh' ? '刷新中...' : '刷新订单发货'}
              </button>
            </div>
          </section>

          <section className="license-host-erp__panel license-host-erp__panel--scroll">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>发货模板</strong>
                <span>默认模板会在生成发货文案时自动套用。</span>
              </div>
              <Mail size={16} />
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>模板名称</span>
                <input className="license-input" value={orderDeliveryTemplateForm.name} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, name: event.target.value }))} placeholder="例如：永久授权模板" />
              </label>
              <label className="license-host__field">
                <span>绑定档位</span>
                <select className="license-input" value={orderDeliveryTemplateForm.tierId} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, tierId: event.target.value }))}>
                  <option value="">通用模板</option>
                  {orderDeliveryDashboard.tiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="license-host__field">
              <span>模板正文</span>
              <textarea className="license-input" value={orderDeliveryTemplateForm.bodyTemplate} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, bodyTemplate: event.target.value }))} placeholder="卖家、订单号、授权码、教程链接等模板变量占位" />
            </label>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>教程链接</span>
                <input className="license-input" value={orderDeliveryTemplateForm.tutorialUrl} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, tutorialUrl: event.target.value }))} placeholder="https://..." />
              </label>
              <label className="license-host__field">
                <span>签名结尾</span>
                <input className="license-input" value={orderDeliveryTemplateForm.signatureText} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, signatureText: event.target.value }))} placeholder="感谢支持..." />
              </label>
            </div>
            <label className="license-host__field">
              <span>补充提醒</span>
              <textarea className="license-input" value={orderDeliveryTemplateForm.noticeText} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, noticeText: event.target.value }))} placeholder="发货后提醒、续费说明、异常处理提示" />
            </label>
            <div className="license-host-order__check-row">
              <label className="license-host-order__check">
                <input type="checkbox" checked={orderDeliveryTemplateForm.enabled} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, enabled: event.target.checked }))} />
                <span>启用模板</span>
              </label>
              <label className="license-host-order__check">
                <input type="checkbox" checked={orderDeliveryTemplateForm.isDefault} onChange={(event) => setOrderDeliveryTemplateForm((current) => ({ ...current, isDefault: event.target.checked }))} />
                <span>设为默认</span>
              </label>
            </div>
            <div className="license-host__quick-actions license-host__quick-actions--dense">
              <button type="button" className="sidebar-button" disabled={orderDeliveryBusy === 'template'} onClick={() => { void handleSaveOrderDeliveryTemplate(); }}>
                {orderDeliveryBusy === 'template' ? '保存中...' : '保存模板'}
              </button>
              <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => setOrderDeliveryTemplateForm(createInitialOrderDeliveryTemplateForm())}>
                重置
              </button>
            </div>
            <div className="license-host-order__list">
              {visibleOrderDeliveryTemplates.length > 0 ? visibleOrderDeliveryTemplates.map((template) => (
                <article key={template.id} className="license-host-erp__record-card">
                  <div className="license-host__record-row">
                    <strong>{template.name}</strong>
                    <span className={`license-host-order__status is-${orderDeliveryStatusTone(template.enabled ? 'online' : 'disabled')}`}>{template.isDefault ? '默认模板' : template.enabled ? '启用中' : '已停用'}</span>
                  </div>
                  <div className="license-host__record-note">{template.tierTitle ?? '通用模板'} / {template.bodyTemplate.slice(0, 90) || '暂无正文'}</div>
                  <div className="license-host__record-row">
                    <span>{formatDateTime(template.updatedAt)}</span>
                    <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => handleLoadOrderDeliveryTemplate(template.id)}>
                      载入编辑
                    </button>
                  </div>
                </article>
              )) : <div className="license-host__empty">当前还没有发货模板。</div>}
            </div>
          </section>

          <section className="license-host-erp__panel license-host-erp__panel--scroll">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>订单登记</strong>
                <span>登记外部订单或流水后，可直接占用库存授权码并生成发送文案。</span>
              </div>
              <BellRing size={16} />
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>外部单号</span>
                <input className="license-input" value={orderDeliveryOrderForm.sourceOrderId} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, sourceOrderId: event.target.value }))} placeholder="订单号 / 流水号" />
              </label>
              <label className="license-host__field">
                <span>买家名称</span>
                <input className="license-input" value={orderDeliveryOrderForm.buyerName} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, buyerName: event.target.value }))} placeholder="买家备注名 / 账号名" />
              </label>
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>商品标题</span>
                <input className="license-input" value={orderDeliveryOrderForm.itemTitle} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, itemTitle: event.target.value }))} placeholder="商品标题" />
              </label>
              <label className="license-host__field">
                <span>绑定档位</span>
                <select className="license-input" value={orderDeliveryOrderForm.tierId} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, tierId: event.target.value }))}>
                  <option value="">未绑定档位</option>
                  {orderDeliveryDashboard.tiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>规格值</span>
                <input className="license-input" value={orderDeliveryOrderForm.specValue} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, specValue: event.target.value }))} placeholder="例如：永久 / 4 卡" />
              </label>
              <label className="license-host__field">
                <span>金额</span>
                <input className="license-input" value={orderDeliveryOrderForm.amountText} onChange={(event) => setOrderDeliveryOrderForm((current) => ({ ...current, amountText: event.target.value }))} placeholder="¥198" />
              </label>
            </div>
            <div className="license-host__quick-actions license-host__quick-actions--dense">
              <button type="button" className="sidebar-button" disabled={orderDeliveryBusy === 'order'} onClick={() => { void handleSaveOrderDeliveryOrder(); }}>
                {orderDeliveryBusy === 'order' ? '保存中...' : '保存订单'}
              </button>
              <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => setOrderDeliveryOrderForm(createInitialOrderDeliveryOrderForm())}>
                重置
              </button>
            </div>
            <div className="license-host-order__list">
              {visibleOrderDeliveryOrders.length > 0 ? visibleOrderDeliveryOrders.map((order) => (
                <article key={order.id} className="license-host-erp__record-card">
                  <div className="license-host__record-row">
                    <strong>{order.sourceOrderId}</strong>
                    <span className={`license-host-order__status is-${orderDeliveryStatusTone(order.status)}`}>{orderDeliveryStatusLabel(order.status)}</span>
                  </div>
                  <div className="license-host__record-note">{order.itemTitle} / {order.buyerName ?? order.buyerId ?? '未记录对象'} / {order.tierTitle ?? '未绑定档位'}</div>
                  {order.deliveryText ? <div className="license-host__record-note">{order.deliveryText.slice(0, 120)}</div> : null}
                  <div className="license-host__record-row">
                    <span>{formatDateTime(order.updatedAt)}</span>
                    <div className="license-host__record-actions">
                      <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => handleLoadOrderDeliveryOrder(order.id)}>
                        载入编辑
                      </button>
                      <button type="button" className="sidebar-button" disabled={orderDeliveryBusy === 'fulfill'} onClick={() => { void handleFulfillOrderDeliveryOrder(order.id); }}>
                        {orderDeliveryBusy === 'fulfill' ? '生成中...' : '生成发货'}
                      </button>
                      {order.deliveryText ? (
                        <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => { void handleCopy(order.deliveryText ?? '', '发货文案已复制。'); }}>
                          <Copy size={14} />
                          复制文案
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              )) : <div className="license-host__empty">当前还没有登记订单。</div>}
            </div>
          </section>

          <section className="license-host-erp__panel license-host-erp__panel--scroll">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>自动发货通道</strong>
                <span>保留自动发货能力，同时不影响人工核单和手动发货。</span>
              </div>
              <ServerCog size={16} />
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>通道名称</span>
                <input className="license-input" value={orderDeliveryBotForm.clientName} onChange={(event) => setOrderDeliveryBotForm((current) => ({ ...current, clientName: event.target.value }))} placeholder="自动发码通道" />
              </label>
              <label className="license-host__field">
                <span>状态</span>
                <select className="license-input" value={orderDeliveryBotForm.status} onChange={(event) => setOrderDeliveryBotForm((current) => ({ ...current, status: event.target.value }))}>
                  <option value="idle">待命</option>
                  <option value="online">在线</option>
                  <option value="warning">告警</option>
                  <option value="disabled">停用</option>
                </select>
              </label>
            </div>
            <div className="license-host__remote-field-grid">
              <label className="license-host__field">
                <span>回调地址</span>
                <input className="license-input" value={orderDeliveryBotForm.endpointUrl} onChange={(event) => setOrderDeliveryBotForm((current) => ({ ...current, endpointUrl: event.target.value }))} placeholder="https://bot.ainivox.cn/..." />
              </label>
              <label className="license-host__field">
                <span>bot key 提示</span>
                <input className="license-input" value={orderDeliveryBotForm.botKeyHint} onChange={(event) => setOrderDeliveryBotForm((current) => ({ ...current, botKeyHint: event.target.value }))} placeholder="只展示提示，不保存完整密钥" />
              </label>
            </div>
            <label className="license-host-order__check">
              <input type="checkbox" checked={orderDeliveryBotForm.enabled} onChange={(event) => setOrderDeliveryBotForm((current) => ({ ...current, enabled: event.target.checked }))} />
              <span>启用通道</span>
            </label>
            <div className="license-host__quick-actions license-host__quick-actions--dense">
              <button type="button" className="sidebar-button" disabled={orderDeliveryBusy === 'bot'} onClick={() => { void handleSaveOrderDeliveryBot(); }}>
                {orderDeliveryBusy === 'bot' ? '保存中...' : '保存通道'}
              </button>
              <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => setOrderDeliveryBotForm(createInitialOrderDeliveryBotForm())}>
                重置
              </button>
            </div>
            <div className="license-host-order__list">
              {visibleOrderDeliveryBots.length > 0 ? visibleOrderDeliveryBots.map((bot) => (
                <article key={bot.id} className="license-host-erp__record-card">
                  <div className="license-host__record-row">
                    <strong>{bot.clientName}</strong>
                    <span className={`license-host-order__status is-${orderDeliveryStatusTone(bot.enabled ? bot.status : 'disabled')}`}>{orderDeliveryStatusLabel(bot.enabled ? bot.status : 'disabled')}</span>
                  </div>
                  <div className="license-host__record-note">{bot.endpointUrl ?? '未配置回调地址'}</div>
                  <div className="license-host__record-row">
                    <span>{formatDateTime(bot.updatedAt)}</span>
                    <button type="button" className="sidebar-button sidebar-button--secondary" onClick={() => handleLoadOrderDeliveryBot(bot.id)}>
                      载入编辑
                    </button>
                  </div>
                </article>
              )) : <div className="license-host__empty">当前还没有通道配置。</div>}
            </div>
          </section>

          <section className="license-host-erp__panel license-host-erp__panel--scroll license-host-order__panel--wide">
            <div className="license-host-erp__panel-head">
              <div>
                <strong>审计流</strong>
                <span>保留订单发货、占用库存和绑定账号相关的审计记录。</span>
              </div>
              <LineChart size={16} />
            </div>
            <div className="license-host-order__list">
              {visibleOrderDeliveryAuditLogs.length > 0 ? visibleOrderDeliveryAuditLogs.map((item) => (
                <article key={item.id} className="license-host-erp__record-card">
                  <div className="license-host__record-row">
                    <strong>{item.summary}</strong>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                  <div className="license-host__record-note">{item.scope} / {item.action}</div>
                  <div className="license-host__record-note">{item.detail ?? '无额外说明'}</div>
                </article>
              )) : <div className="license-host__empty">当前还没有审计记录。</div>}
            </div>
          </section>
        </div>
      </div>
    );
  };

  const renderFeedbackSection = () => {
    if (!hostState.remoteAdminConfigured) {
      return (
        <div className="license-host-erp__view">
          <section className="license-host-erp__panel">
            <div className="license-host__empty">当前还没有配置远程授权服务地址。</div>
          </section>
        </div>
      );
    }

    return (
      <div className="license-host-erp__view license-host-erp__view--customers">
        <div className="license-host-erp__customers license-host-erp__customers--feedback">
          {renderCustomerListPanel('feedback')}
          <div className="license-host-erp__customers-main license-host-erp__customers-main--feedback">
            <div className="license-host-erp__customers-main-grid">{renderFeedbackThreadPanel()}</div>
          </div>
          {renderFeedbackInboxAside()}
        </div>
      </div>
    );
  };

  const renderSettingsSection = () => (
    <div className="license-host-erp__view license-host-erp__view--settings">
      <div className="license-host-erp__settings-grid">
        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>本地授权签发</strong>
              <span>保留现有签发逻辑，只重组为后台设置工作区。</span>
            </div>
            <KeyRound size={16} />
          </div>

          <label className="license-host__field">
            <span>授权时长</span>
            <div className="license-host__duration-grid">
              {hostState.supportedDurations.map((option) => (
                <button
                  key={`host-${option.key}`}
                  type="button"
                  className={`license-host__duration-chip license-host__duration-chip--compact${
                    form.durationKey === option.key ? ' is-active' : ''
                  }`}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      durationKey: option.key,
                    }))
                  }
                >
                  <strong>{option.label}</strong>
                </button>
              ))}
            </div>
          </label>

          {form.durationKey === 'custom-days' ? (
            <label className="license-host__field">
              <span>自定义天数</span>
              <input
                className="license-input"
                value={form.customDurationDaysInput}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    customDurationDaysInput: event.target.value,
                  }))
                }
                placeholder={`输入 1-${MAX_CUSTOM_DURATION_DAYS} 天`}
              />
            </label>
          ) : null}

          <div className="license-host__remote-field-grid">
            <label className="license-host__field">
              <span>目标名称</span>
              <input
                className="license-input"
                value={form.targetLabel}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    targetLabel: event.target.value,
                  }))
                }
                placeholder="客户名 / 设备名 / 备注名"
              />
            </label>
            <label className="license-host__field">
              <span>设备 ID</span>
              <input
                className="license-input"
                value={form.deviceId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    deviceId: event.target.value,
                  }))
                }
                placeholder="可留空"
              />
            </label>
          </div>

          <div className="license-host__remote-field-grid">
            <label className="license-host__field license-host__field--wide">
              <span>设备指纹哈希</span>
              <input
                className="license-input"
                value={form.deviceFingerprintHash}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    deviceFingerprintHash: event.target.value,
                  }))
                }
                placeholder="粘贴指纹哈希"
              />
            </label>
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary license-host-erp__device-fill"
              onClick={applyCurrentDevice}
            >
              <HardDrive size={14} />
              使用当前设备
            </button>
          </div>

          <div className="license-host__remote-field-grid">
            <label className="license-host__field">
              <span>卡片槽位数</span>
              <input
                className="license-input"
                value={form.slotLimitInput}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    slotLimitInput: event.target.value,
                  }))
                }
                placeholder={`留空按默认；输入 1-${MAX_SLOT_LIMIT} 或 无限`}
              />
            </label>
            <label className="license-host__field license-host__field--wide">
              <span>备注</span>
              <textarea
                className="license-host__textarea license-host__textarea--compact"
                value={form.note}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    note: event.target.value,
                  }))
                }
                placeholder="写入签发记录备注"
              />
            </label>
          </div>

          <div className="license-host__remote-switch-row">
            <div className="license-host__toggle-copy">
              <strong>开放内测通道</strong>
            </div>
            <label className="settings-switch" aria-label="切换本地内测通道">
              <input
                className="settings-switch__input"
                type="checkbox"
                checked={form.betaAccess}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    betaAccess: event.target.checked,
                    advancedBetaAccess: event.target.checked ? current.advancedBetaAccess : false,
                  }))
                }
              />
              <span className="settings-switch__track" />
              <span className="settings-switch__thumb" />
            </label>
          </div>

          <div className="license-host__remote-switch-row">
            <div className="license-host__toggle-copy">
              <strong>开放工程师模式</strong>
            </div>
            <label className="settings-switch" aria-label="切换本地工程师模式">
              <input
                className="settings-switch__input"
                type="checkbox"
                checked={form.advancedBetaAccess}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    betaAccess: event.target.checked ? true : current.betaAccess,
                    advancedBetaAccess: event.target.checked,
                  }))
                }
              />
              <span className="settings-switch__track" />
              <span className="settings-switch__thumb" />
            </label>
          </div>

          <div className="license-host-erp__form-hints">
            <span>当前默认时长：{currentDuration?.label ?? currentDurationLabel}</span>
            <span>槽位规则：{currentSlotLimitHint}</span>
          </div>

          <div className="license-host__quick-actions license-host__quick-actions--dense">
            <button
              type="button"
              className="sidebar-button"
              onClick={() => {
                void handleIssue();
              }}
              disabled={busy === 'issuing'}
            >
              <KeyRound size={14} />
              {busy === 'issuing' ? '正在生成授权码' : '生成授权码'}
            </button>
          </div>

          {generatedCode ? (
            <div className="license-host-erp__code-block">
              <div className="license-host-erp__code-head">
                <strong>新生成授权码</strong>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={() => {
                    void handleCopy(generatedCode, '新授权码已复制。');
                  }}
                >
                  <Copy size={14} />
                  复制
                </button>
              </div>
              <pre>{generatedCode}</pre>
            </div>
          ) : null}
        </section>

        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>主机资源</strong>
              <span>资源路径、主机状态与 Cloudflare 接入状态。</span>
            </div>
            <FolderOpen size={16} />
          </div>
          <div className="license-host-erp__settings-list">
            <article className="license-host-erp__settings-item">
              <div>
                <strong>公钥摘要</strong>
                <span>{hostState.publicKeySummary}</span>
              </div>
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => {
                  void bridge?.openPath(hostState.publicKeyPath);
                }}
              >
                打开公钥
              </button>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>存储目录</strong>
                <span>{hostState.storagePath}</span>
              </div>
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => {
                  void bridge?.openPath(hostState.storagePath);
                }}
              >
                打开目录
              </button>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>Cloudflare 状态</strong>
                <span>{remoteDashboard.connected ? '远程源连通' : '等待检查远程状态'}</span>
              </div>
              <small>{remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ?? '未配置远程源'}</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>最近检查</strong>
                <span>{formatDateTime(remoteDashboard.checkedAt)}</span>
              </div>
              <small>当前会话 {hostConsoleUnlocked ? '已解锁' : '未解锁'}</small>
            </article>
          </div>
          <div className="license-host__quick-actions license-host__quick-actions--dense">
            {hostState.remoteAdminConfigured ? (
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => {
                  void refreshRemoteDashboard();
                }}
                disabled={remoteBusy === 'refresh'}
              >
                <RefreshCw size={14} className={remoteBusy === 'refresh' ? 'spin' : ''} />
                刷新远程状态
              </button>
            ) : null}
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={() => {
                void bridge?.restartLicenseHost();
              }}
            >
              <RefreshCw size={14} />
              重启本地主机
            </button>
          </div>
        </section>

        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>控制台操作</strong>
              <span>把会话、轮询、远程连接和主机动作收成真实可操作面板。</span>
            </div>
            <MonitorSmartphone size={16} />
          </div>
          <div className="license-host-erp__settings-list">
            <article className="license-host-erp__settings-item license-host-erp__settings-item--action">
              <div>
                <strong>登录会话</strong>
                <span>{hostLoginRememberDevice ? '记住控制台账号' : '不记住控制台账号'}</span>
              </div>
              <label className="settings-switch" aria-label="切换是否记住控制台账号">
                <input
                  className="settings-switch__input"
                  type="checkbox"
                  checked={hostLoginRememberDevice}
                  onChange={(event) => handleHostRememberDeviceChange(event.target.checked)}
                />
                <span className="settings-switch__track" />
                <span className="settings-switch__thumb" />
              </label>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>远程轮询</strong>
                <span>20 秒自动刷新一次远程仪表盘</span>
              </div>
              <small>{hostState.remoteAdminConfigured ? '已启用' : '未启用'}</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>待处理会话</strong>
                <span>客服未读 {remoteDashboard.unreadFeedbackCount} / 登录提醒 {remoteDashboard.unreadLoginCount}</span>
              </div>
              <small>{remoteDashboard.checkedAt ? `最近检查 ${formatDateTime(remoteDashboard.checkedAt)}` : '等待首次检查'}</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>远程源入口</strong>
                <span>{remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ?? '未配置远程源'}</span>
              </div>
              <small>{remoteDashboard.connected ? '当前已连通' : '当前等待连接'}</small>
            </article>
          </div>
          <div className="license-host__quick-actions license-host__quick-actions--dense">
            {hostState.remoteAdminConfigured ? (
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => {
                  void refreshRemoteDashboard();
                }}
                disabled={remoteBusy === 'refresh'}
              >
                <RefreshCw size={14} className={remoteBusy === 'refresh' ? 'spin' : ''} />
                刷新远程状态
              </button>
            ) : null}
            {remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ? (
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => {
                  void handleOpenExternalLink(
                    remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ?? '',
                    '打开远程源失败。',
                  );
                }}
              >
                <ArrowUpRight size={14} />
                打开远程源
              </button>
            ) : null}
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={() => {
                void bridge?.restartLicenseHost();
              }}
            >
              <RefreshCw size={14} />
              重启本地主机
            </button>
            <button type="button" className="sidebar-button sidebar-button--secondary" onClick={handleHostLogout}>
              <LogOut size={14} />
              退出登录
            </button>
          </div>
        </section>

        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>工作区跳转</strong>
              <span>把客户授权、客服反馈和主机标识做成直接可点的工作区入口。</span>
            </div>
            <BellRing size={16} />
          </div>
          <div className="license-host-erp__settings-list">
            <article className="license-host-erp__settings-item">
              <div>
                <strong>客户与授权</strong>
                <span>{remoteGroups.length} 个客户分组，支持授权、异常解除、密码重置和空间卡片传送。</span>
              </div>
              <small>{selectedRemoteGroup ? getRemoteGroupAccountLabel(selectedRemoteGroup) : '未选中客户'}</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>客服反馈</strong>
                <span>未读消息 {remoteDashboard.unreadFeedbackCount} 条，点击后直接进入收信箱和聊天主区。</span>
              </div>
              <small>{feedbackInboxEntries.length} 个会话</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>主机标识</strong>
                <span>{hostState.hostId}</span>
              </div>
              <small>{hostState.currentDevice.deviceId}</small>
            </article>
          </div>
          <div className="license-host__quick-actions license-host__quick-actions--dense">
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={() => setActiveSection('customers')}
            >
              <Users size={14} />
              进入客户与授权
            </button>
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={() => setActiveSection('codes')}
            >
              <QrCode size={14} />
              进入授权码生成
            </button>
            <button type="button" className="sidebar-button" onClick={() => setActiveSection('feedback')}>
              <MessageSquare size={14} />
              进入客服反馈
            </button>
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={() => {
                void handleCopy(hostState.hostId, '主机 ID 已复制。');
              }}
            >
              <Copy size={14} />
              复制主机 ID
            </button>
          </div>
        </section>

        <section className="license-host-erp__panel license-host-erp__panel--scroll">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>表单与预设</strong>
              <span>把旧的展示卡改成真正能清空、复位、带入当前设备的操作台。</span>
            </div>
            <Settings2 size={16} />
          </div>
          <div className="license-host-erp__settings-list">
            <article className="license-host-erp__settings-item">
              <div>
                <strong>当前签发时长</strong>
                <span>{currentDuration?.label ?? currentDurationLabel}</span>
              </div>
              <small>{hostState.supportedDurations.length} 个可选方案</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>快速授权预设</strong>
                <span>已经为 {remoteGrantPresetCount} 个客户保存预设，下次快速授权会自动带入。</span>
              </div>
              <small>{remoteGrantPresetCount > 0 ? '可一键清空' : '当前没有缓存预设'}</small>
            </article>
            <article className="license-host-erp__settings-item">
              <div>
                <strong>新授权码缓存</strong>
                <span>{generatedCode ? '当前已有一条新生成授权码' : '当前没有待复制的新授权码'}</span>
              </div>
              <small>{generatedCode ? '可直接清空并重置表单' : '等待再次签发'}</small>
            </article>
          </div>
          <div className="license-host__quick-actions license-host__quick-actions--dense">
            <button type="button" className="sidebar-button sidebar-button--secondary" onClick={applyCurrentDevice}>
              <HardDrive size={14} />
              带入当前设备
            </button>
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={handleResetHostIssueForm}
            >
              <RefreshCw size={14} />
              重置签发表单
            </button>
            <button
              type="button"
              className="sidebar-button sidebar-button--secondary"
              onClick={handleClearRemoteGrantPresets}
              disabled={remoteGrantPresetCount === 0}
            >
              <Settings2 size={14} />
              清空快速授权预设
            </button>
          </div>
        </section>

        <section className="license-host-erp__panel license-host-erp__panel--scroll license-host-erp__panel--wide">
          <div className="license-host-erp__panel-head">
            <div>
              <strong>签发记录</strong>
              <span>历史记录、搜索、复制授权码和设备信息都保留。</span>
            </div>
            <Search size={16} />
          </div>

          <div className="license-host__record-search license-host-erp__search">
            <Search size={14} />
            <input
              className="license-input"
              value={recordSearch}
              onChange={(event) => setRecordSearch(event.target.value)}
              placeholder="搜索目标名称 / 设备 ID / 指纹 / 备注"
            />
            {recordSearchBusy ? <span className="license-host-erp__inline-note">检索中</span> : null}
          </div>

          {visibleRecords.length > 0 ? (
            <div className="license-host-erp__record-list">
              {visibleRecords.map((record) => {
                const expanded = expandedRecordId === record.id;
                return (
                  <article key={record.id} className="license-host-erp__record-card">
                    <div className="license-host__record-row">
                      <strong>{record.targetLabel}</strong>
                      <span>{formatDateTime(record.createdAt)}</span>
                    </div>
                    <div className="license-host__record-note">
                      {record.durationLabel} / {record.slotLimitLabel} / {record.codePreview}
                    </div>
                    <div className="license-host__record-row">
                      <span>{record.targetDeviceId ?? '未记录设备 ID'}</span>
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() =>
                          setExpandedRecordId((current) => (current === record.id ? null : record.id))
                        }
                      >
                        {expanded ? '收起' : '展开'}
                      </button>
                    </div>
                    {expanded ? (
                      <div className="license-host-erp__record-expanded">
                        <div className="license-host__record-note">
                          指纹摘要：{record.targetFingerprintSummary}
                        </div>
                        <div className="license-host__record-note">备注：{record.note ?? '无'}</div>
                        <div className="license-host__record-actions">
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() => {
                              void handleCopyRecordCode(record);
                            }}
                          >
                            <Copy size={14} />
                            复制授权码
                          </button>
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={() => {
                              void handleCopyRecordDeviceInfo(record);
                            }}
                          >
                            <Copy size={14} />
                            复制设备信息
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="license-host__empty">当前还没有匹配到签发记录。</div>
          )}
        </section>
      </div>
    </div>
  );

  const hostConsoleWorkspace = (
    <div className="license-host-erp">
      <div className="license-host-erp__layout">
        <aside className="license-host-erp__aside">
          <div className="license-host-erp__brand-card">
            <div className="license-host-erp__brand-icon">
              <img src={appIconSrc} alt="AINIVOX - OAuth切换中心" />
            </div>
            <div className="license-host-erp__brand-copy">
              <strong>AINIVOX</strong>
              <span>{displayHostConsoleName}</span>
            </div>
          </div>

          <nav className="license-host-erp__nav" aria-label="主导航">
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'overview' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <LayoutDashboard size={18} />
              <strong>运营总览</strong>
            </button>
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'customers' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('customers')}
            >
              <Users size={18} />
              <strong>客户与授权</strong>
            </button>
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'orders' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('orders')}
            >
              <ServerCog size={18} />
              <strong>订单发货</strong>
              {orderDeliveryDashboard.summary.pendingOrders > 0 ? (
                <span className="license-host-erp__nav-dot" aria-label={`${orderDeliveryDashboard.summary.pendingOrders} 条待处理发货记录`} />
              ) : null}
            </button>
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'codes' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('codes')}
            >
              <QrCode size={18} />
              <strong>授权码生成</strong>
              {orderDeliveryDashboard.summary.unusedCodes > 0 ? (
                <span className="license-host-erp__nav-dot" aria-label={`${orderDeliveryDashboard.summary.unusedCodes} 条库存授权码`} />
              ) : null}
            </button>
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'feedback' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('feedback')}
            >
              <MessageSquare size={18} />
              <strong>客服反馈</strong>
              {remoteDashboard.unreadFeedbackCount > 0 ? (
                <span className="license-host-erp__nav-dot" aria-label={`${remoteDashboard.unreadFeedbackCount} 条未读客服消息`} />
              ) : null}
            </button>
            <button
              type="button"
              className={`license-host-erp__nav-button${activeSection === 'settings' ? ' is-active' : ''}`}
              onClick={() => setActiveSection('settings')}
            >
              <Settings2 size={18} />
              <strong>系统与设置</strong>
            </button>
          </nav>

          <div className="license-host-erp__aside-links">
            <button
              type="button"
              className="license-host-erp__utility-link"
              onClick={() => {
                void handleOpenExternalLink('https://www.ainivox.cn/', '打开官网失败。');
              }}
            >
              <Globe size={16} />
              <span>官网</span>
              <ArrowUpRight size={14} />
            </button>
            <button
              type="button"
              className="license-host-erp__utility-link"
              onClick={() => {
                void handleOpenExternalLink('https://www.ainivox.cn/download.html', '打开下载页失败。');
              }}
            >
              <Download size={16} />
              <span>下载</span>
              <ArrowUpRight size={14} />
            </button>
            <button
              type="button"
              className="license-host-erp__utility-link"
              onClick={() => {
                void handleOpenExternalLink('https://www.ainivox.cn/activate.html', '打开帮助页失败。');
              }}
            >
              <HelpCircle size={16} />
              <span>帮助</span>
              <ArrowUpRight size={14} />
            </button>
            <button
              type="button"
              className="license-host-erp__utility-link is-danger"
              onClick={handleHostLogout}
            >
              <LogOut size={16} />
              <span>退出登录</span>
            </button>
          </div>
        </aside>

        <section className="license-host-erp__main">
          <header className="license-host-erp__toolbar">
            <div className="license-host-erp__toolbar-title">
              <h1>{activeSectionCopy.title}</h1>
              <span>{workspaceSecondaryLabel}</span>
            </div>
            <label
              className={`license-host-erp__toolbar-search${
                workspaceSearchDisabled ? ' is-disabled' : ''
              }`}
            >
              <Search size={16} />
              <input
                value={workspaceSearchValue}
                disabled={workspaceSearchDisabled}
                placeholder={workspaceSearchPlaceholder}
                onChange={(event) => {
                  if (activeSection === 'customers' || activeSection === 'feedback') {
                    setRemoteQuery(event.target.value);
                    return;
                  }
                  if (activeSection === 'orders' || activeSection === 'codes') {
                    setOrderDeliveryQuery(event.target.value);
                    return;
                  }
                  if (activeSection === 'settings') {
                    setRecordSearch(event.target.value);
                  }
                }}
              />
            </label>
            <div className="license-host-erp__toolbar-actions">
              <span className="license-host-erp__toolbar-pill">{hostConsoleStatusLabel}</span>
              <span
                className={`license-host-erp__toolbar-pill${
                  hostConsolePendingCount > 0 ? '' : ' is-muted'
                }`}
              >
                待处理 {hostConsolePendingCount}
              </span>
              <div className="license-host-erp__session">
                <span className="license-host-erp__session-avatar">{hostConsoleAvatarText}</span>
                <div className="license-host-erp__session-copy">
                  <strong>{hostConsoleAccount}</strong>
                  <small>{hostLoginRememberDevice ? '已记住账号' : '未记住账号'}</small>
                </div>
              </div>
            </div>
          </header>

          {errorMessage ? (
            <div className="license-host__banner is-error license-host-erp__banner">{errorMessage}</div>
          ) : null}
          {toastMessage ? (
            <div className="license-host__banner is-success license-host-erp__banner">{toastMessage}</div>
          ) : null}

          <main className="license-host-erp__workspace">
            {activeSection === 'overview' ? renderOverviewSection() : null}
            {activeSection === 'customers' ? renderCustomersSection() : null}
            {activeSection === 'orders' ? renderOrdersSection() : null}
            {activeSection === 'codes' ? renderCodeGenerationSection() : null}
            {activeSection === 'feedback' ? renderFeedbackSection() : null}
            {activeSection === 'settings' ? renderSettingsSection() : null}
          </main>
        </section>
      </div>
    </div>
  );

  return (
    <div className="license-host">
      <div className="license-host__shell">
        <div
          className={`license-host__content${hostConsoleUnlocked ? ' license-host__content--console' : ' license-host__content--login'}`}
        >
          {!hostConsoleUnlocked ? (
            <div className="license-host__login-stage">
              <div className="license-host__login-ambient" aria-hidden="true">
                <span className="license-host__login-orb is-primary" />
                <span className="license-host__login-orb is-secondary" />
                <span className="license-host__login-orb is-accent" />
              </div>

              <div className="license-host__login-shell">
                <section className="license-host__login-panel">
                  <div className="license-host__login-copy">
                    <h1 className="license-host__login-title">欢迎回来</h1>
                    <p className="license-host__login-description">请登录您的企业账户以继续</p>
                  </div>

                  <div className="license-host__login-form">
                    <label className="license-host__field license-host__login-field">
                      <span>用户名 / 员工ID</span>
                      <div className="license-host__login-input-shell">
                        <UserRound size={18} />
                        <input
                          className="license-input license-host__login-input"
                          value={hostLoginUsername}
                          onChange={(event) => setHostLoginUsername(event.target.value)}
                          placeholder="请输入您的账户名"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          data-1p-ignore="true"
                          data-lpignore="true"
                        />
                      </div>
                    </label>

                    <label className="license-host__field license-host__login-field">
                      <span>密码</span>
                      <div className="license-host__login-input-shell">
                        <LockKeyhole size={18} />
                        <input
                          className="license-input license-host__login-input"
                          type={hostLoginPasswordVisible ? 'text' : 'password'}
                          value={hostLoginPassword}
                          onChange={(event) => setHostLoginPassword(event.target.value)}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void handleSubmitHostLogin();
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="license-host__login-visibility"
                          aria-label={hostLoginPasswordVisible ? '隐藏密码' : '显示密码'}
                          onClick={() => setHostLoginPasswordVisible((current) => !current)}
                        >
                          {hostLoginPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </label>

                    <div className="license-host__login-row">
                      <label className="license-host__login-remember">
                        <input
                          type="checkbox"
                          checked={hostLoginRememberDevice}
                          onChange={(event) => handleHostRememberDeviceChange(event.target.checked)}
                        />
                        <span>记住账号</span>
                      </label>
                      <button
                        type="button"
                        className="license-host__login-forgot"
                        onClick={() => setHostLoginErrorMessage('请联系管理员重置密码。')}
                      >
                        忘记密码?
                      </button>
                    </div>

                    {hostLoginErrorMessage ? (
                      <div className="license-host__login-error">{hostLoginErrorMessage}</div>
                    ) : null}

                    <button
                      type="button"
                      className="sidebar-button license-host__login-submit"
                      onClick={() => {
                        void handleSubmitHostLogin();
                      }}
                      disabled={hostLoginBusy}
                    >
                      {hostLoginBusy ? '登录中...' : '立即登录'}
                    </button>
                  </div>

                  <div className="license-host__login-divider">
                    <span>其他登录方式</span>
                  </div>

                  <div className="license-host__login-alt-grid">
                    <button type="button" className="license-host__login-alt-button">
                      <Building2 size={18} />
                      <span>企业微信</span>
                    </button>
                    <button type="button" className="license-host__login-alt-button">
                      <QrCode size={18} />
                      <span>扫码登录</span>
                    </button>
                  </div>
                </section>

                <footer className="license-host__login-footer">
                  <p>© 2024 AINIVOX 企业核心版. 保留所有权利.</p>
                  <div>
                    <a href="#">隐私政策</a>
                    <a href="#">服务条款</a>
                    <a href="#">联系支持</a>
                  </div>
                </footer>
              </div>
            </div>
          ) : (
            <>
              {hostConsoleWorkspace}
              {false ? (
                <>
              <header className="license-host__console-topbar">
                <div className="license-host__console-overview">
                  <div className="license-host__eyebrow">License Host Control</div>
                  <strong>{displayHostConsoleName}</strong>
                  <div className="license-host__console-meta">
                    <span>主机 {hostState.hostId}</span>
                    <span>{hostState.remoteSourceUrl ? `远程 ${hostState.remoteSourceUrl}` : '远程服务未接管'}</span>
                    <span>最近签发 {formatDateTime(hostState.lastIssuedAt)}</span>
                    <span>当前设备 {hostState.currentDevice.deviceId}</span>
                  </div>
                </div>

                <div className="license-host__console-actions">
                  {(activeSection as string) === 'admin-console' && hostState.remoteAdminConfigured ? (
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() => {
                        void refreshRemoteDashboard();
                      }}
                      disabled={remoteBusy === 'refresh'}
                    >
                      <RefreshCw size={14} className={remoteBusy === 'refresh' ? 'spin' : ''} />
                      刷新远程
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={() => {
                      void bridge?.restartLicenseHost();
                    }}
                  >
                    <RefreshCw size={14} />
                    重启主机
                  </button>
                </div>

                <div className="license-host__console-session">
                  <div className="license-host__console-session-copy">
                    <strong>{hostConsoleAccount}</strong>
                    <span>
                      {hostLoginSession?.unlockedAt
                        ? `进入时间 ${formatDateTime(hostLoginSession?.unlockedAt ?? null)}`
                        : '闲置 2 小时后需重新输入密码'}
                    </span>
                    <small>{hostLoginRememberDevice ? '已记住账号' : '未记住账号'}</small>
                  </div>
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={handleHostLogout}
                  >
                    退出
                  </button>
                </div>
              </header>

              <div className="license-host__workspace">
                <aside className="license-host__sidebar">
                  <div className="license-host__sidebar-card license-host__sidebar-card--compact">
                    <div className="license-host__eyebrow">Workspace</div>
                    <div className="license-host__sidebar-title">功能分栏</div>
                    <div className="license-host__nav">
                      <button
                        type="button"
                        className={`license-host__nav-button${
                          (activeSection as string) === 'license-center' ? ' is-active' : ''
                        }`}
                        onClick={() => setActiveSection('license-center' as LicenseHostWorkspaceSection)}
                      >
                        <ShieldCheck size={16} />
                        <div>
                          <strong>授权中心</strong>
                          <span className="license-host__nav-description">
                            本地签发、历史记录、公钥和主机输出。
                          </span>
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`license-host__nav-button${
                          (activeSection as string) === 'admin-console' ? ' is-active' : ''
                        }`}
                        onClick={() => setActiveSection('admin-console' as LicenseHostWorkspaceSection)}
                      >
                        <MonitorSmartphone size={16} />
                        <div>
                          <strong>
                            后台管理
                            {remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount > 0 ? (
                              <span className="license-host__inline-badge">
                                {remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount}
                              </span>
                            ) : null}
                          </strong>
                          <span className="license-host__nav-description">
                            客户分组、新登录、反馈会话与空间卡片。
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="license-host__sidebar-card license-host__sidebar-card--summary">
                    <div className="license-host__eyebrow">Runtime</div>
                    <div className="license-host__sidebar-title">主机概览</div>
                    <div className="license-host__sidebar-stat-list">
                      <div className="license-host__sidebar-stat">
                        <span>公钥摘要</span>
                        <strong>{hostState.publicKeySummary}</strong>
                      </div>
                      <div className="license-host__sidebar-stat">
                        <span>签发数量</span>
                        <strong>{hostState.issuedCount}</strong>
                      </div>
                      <div className="license-host__sidebar-stat">
                        <span>新登录</span>
                        <strong>{remoteDashboard.unreadLoginCount}</strong>
                      </div>
                      <div className="license-host__sidebar-stat">
                        <span>未读反馈</span>
                        <strong>{remoteDashboard.unreadFeedbackCount}</strong>
                      </div>
                    </div>
                  </div>
                </aside>

                <div className="license-host__panel-stack">
            {(activeSection as string) === 'license-center' ? (
                <div className="license-host__sidebar-note">
                  当前窗口会优先展示本地主机签发与历史记录，适合生成授权码和回查旧记录。
                </div>
              ) : null}
  
            {(activeSection as string) === 'admin-console' ? (
          <section className="license-host-card license-host-card--remote license-host-card--console">
            {!hostState.remoteAdminConfigured ? (
              <div className="license-host__empty">
                当前还没有配置远程授权服务地址。
              </div>
            ) : (
              <>
                <div className="license-host__console-hero">
                  <div className="license-host__console-hero-copy">
                    <div className="license-host__console-hero-badge">Authorization Host Workspace</div>
                    <strong>主机控制中心</strong>
                    <span>
                      把客户分组、新登录提醒、授权动作和反馈会话压缩到同一层工作台里，优先展示当前最需要处理的事项。
                    </span>
                    <div className="license-host__console-hero-meta">
                      <span>主机 {hostState.hostId}</span>
                      <span>远程源 {remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ?? '未配置'}</span>
                      <span>最近检查 {formatDateTime(remoteDashboard.checkedAt)}</span>
                      <span>当前设备 {hostState.currentDevice.deviceId}</span>
                    </div>
                  </div>

                  <div className="license-host__console-hero-actions">
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() => setActiveSection('license-center' as LicenseHostWorkspaceSection)}
                    >
                      <ShieldCheck size={14} />
                      进入授权中心
                    </button>
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() => {
                        void refreshRemoteDashboard();
                      }}
                      disabled={remoteBusy === 'refresh'}
                    >
                      <RefreshCw size={14} className={remoteBusy === 'refresh' ? 'spin' : ''} />
                      刷新状态
                    </button>
                    <button
                      type="button"
                      className="sidebar-button"
                      onClick={() => {
                        void bridge?.restartLicenseHost();
                      }}
                    >
                      <RefreshCw size={14} />
                      重启主机
                    </button>
                  </div>
                </div>

                <div className="license-host__remote-metrics license-host__remote-metrics--dashboard">
                  <div className="about-metric-card">
                    <span className="about-metric-card__label">在线 / 最近活跃</span>
                    <strong className="about-metric-card__value">
                      {remoteGroupSummary.online} / {remoteGroupSummary.recent}
                    </strong>
                  </div>
                  <div className="about-metric-card">
                    <span className="about-metric-card__label">客户分组 / 设备数</span>
                    <strong className="about-metric-card__value">
                      {remoteGroupSummary.total} / {remoteDashboard.totalClients}
                    </strong>
                  </div>
                  <div className="about-metric-card">
                    <span className="about-metric-card__label">待处理登录 / 反馈</span>
                    <strong className="about-metric-card__value">
                      {remoteDashboard.unreadLoginCount} / {remoteDashboard.unreadFeedbackCount}
                    </strong>
                  </div>
                  <div className="about-metric-card">
                    <span className="about-metric-card__label">最近签发</span>
                    <strong className="about-metric-card__value">
                      {formatDateTime(hostState.lastIssuedAt)}
                    </strong>
                  </div>
                </div>

                <div className="license-host__remote-toolbar">
                  <div className="license-host__remote-toolbar-copy">
                    <strong>客户分组与授权队列</strong>
                    <span>点击任一客户卡片即可在下方联动授权动作、登录流与最近操作记录。</span>
                  </div>
                  <div className="license-host__remote-toolbar-meta">
                    <span className="license-host__remote-toolbar-count">{remoteGroups.length} 位成员</span>
                    <div className="license-host__record-search">
                      <Search size={14} />
                      <input
                        className="license-input"
                        value={remoteQuery}
                        onChange={(event) => setRemoteQuery(event.target.value)}
                        placeholder="搜索邮箱 / 用户名 / 设备编号 / 指纹"
                      />
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() => {
                          void refreshRemoteDashboard();
                        }}
                        disabled={remoteBusy === 'refresh'}
                      >
                        <RefreshCw size={14} className={remoteBusy === 'refresh' ? 'spin' : ''} />
                        刷新
                      </button>
                    </div>
                  </div>
                </div>

                <div className="license-host__remote-layout">
                  <div className="license-host__remote-dashboard-main">
                    <section className="license-host__remote-section license-host__remote-section--members">
                      <div className="license-host__remote-section-head">
                        <div>
                          <strong>客户卡片</strong>
                          <span>每张卡片展示账号、设备、授权状态与快捷操作。</span>
                        </div>
                        {remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount > 0 ? (
                          <span className="license-host__pill-badge">
                            {remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount}
                          </span>
                        ) : null}
                      </div>
                      {remoteGroups.length > 0 ? (
                        <div className="license-host__remote-client-list">
                          {remoteGroups.map((group) => (
                            <article
                              key={group.id}
                              className={`license-host__remote-client-card${
                                selectedRemoteGroup?.id === group.id ? ' is-selected' : ''
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedRemoteGroupId(group.id)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setSelectedRemoteGroupId(group.id);
                                }
                              }}
                            >
                              <div className="license-host__remote-client-head">
                                <div className="license-host__remote-client-title">
                                  <span
                                    className={`license-host__presence-dot is-${group.primaryClient.presence}`}
                                  />
                                  <strong>{group.title}</strong>
                                  <span
                                    className={`license-host__remote-access-badge is-${getRemoteAccessBadgeTone(group.primaryClient.accessBadge)}`}
                                  >
                                    {group.primaryClient.accessBadge.label}
                                  </span>
                                  {group.unreadLoginCount > 0 ? (
                                    <span className="license-host__pill-badge">{group.unreadLoginCount}</span>
                                  ) : null}
                                </div>
                                <span className="license-host__remote-client-presence">
                                  {getRemotePresenceLabel(group.primaryClient.presence)}
                                </span>
                              </div>
                              <div className="license-host__remote-client-meta">
                                <span>{getRemoteGroupAccountLabel(group)}</span>
                                <span>
                                  {group.deviceCount} 台设备轨迹 · 当前主设备{' '}
                                  {group.primaryClient.machineName || group.primaryClient.deviceId}
                                </span>
                                <span>
                                  设备 ID {group.primaryClient.deviceId} · 客户端版本{' '}
                                  {group.primaryClient.clientVersion ?? '未上报'}
                                </span>
                                <span>
                                  最近登录 {group.latestLoginAt ? formatDateTime(group.latestLoginAt) : '未记录'}
                                </span>
                                <span>{group.fingerprintSummary}</span>
                              </div>
                              <div className="license-host__remote-client-foot">
                                <span>{group.primaryClient.effectiveLicense.licenseTypeLabel}</span>
                                <span>{group.primaryClient.effectiveLicense.remainingLabel}</span>
                                {group.unreadLoginCount > 0 ? (
                                  <button
                                    type="button"
                                    className="sidebar-button sidebar-button--secondary"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleOpenQuickGrant(group);
                                    }}
                                  >
                                    快速授权
                                  </button>
                                ) : null}
                              </div>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="license-host__empty">
                          当前搜索条件下还没有找到客户分组。可以先让客户端登录并完成一次心跳，再回来刷新。
                        </div>
                      )}
                    </section>

                    <div className="license-host__remote-main-grid license-host__remote-main-grid--streams">
                      <section className="license-host__remote-section">
                        <div className="license-host__remote-section-head">
                          <div>
                            <strong>新登录收件箱</strong>
                            <div className="license-host__record-note">
                              {selectedRemoteGroup && selectedRemoteLoginEvents.length > 0
                                ? `当前分组 ${selectedRemoteGroup.title} 的最近登录提醒`
                                : '独立于最近活动的登录事件流'}
                            </div>
                          </div>
                          <span
                            className={`license-host__pill-badge${
                              unreadRemoteLoginEvents.length > 0 ? ' license-host__pill-badge--danger' : ''
                            }`}
                          >
                            {unreadRemoteLoginEvents.length}
                          </span>
                        </div>
                        {visibleRemoteLoginEvents.length > 0 ? (
                          <div className="license-host__remote-activity-list">
                            {visibleRemoteLoginEvents.map((event) => {
                              const canQuickGrant = remoteGroups.some((group) =>
                                remoteLoginEventMatchesGroup(event, group),
                              );
                              return (
                                <article
                                  key={event.id}
                                  className={`license-host__remote-activity-card${
                                    event.unread ? ' is-unread' : ''
                                  }`}
                                >
                                  <div className="license-host__remote-device-row">
                                    <div className="license-host__remote-client-title">
                                      <strong>{event.groupTitle ?? getRemoteLoginEventAccountLabel(event)}</strong>
                                      <span
                                        className={`license-host__remote-access-badge is-${getRemoteAccessBadgeTone(event.accessBadge)}`}
                                      >
                                        {event.accessBadge.label}
                                      </span>
                                      {event.unread ? (
                                        <span className="license-host__pill-badge license-host__pill-badge--danger">
                                          新
                                        </span>
                                      ) : null}
                                    </div>
                                    <span>{formatDateTime(event.createdAt)}</span>
                                  </div>
                                  <div className="license-host__record-note">{event.summary}</div>
                                  <div className="license-host__remote-device-meta">
                                    <span>
                                      {event.groupTitle ?? '未归组'} · {getRemoteLoginEventAccountLabel(event)}
                                    </span>
                                    <span>{event.fingerprintSummary ?? '未记录指纹'}</span>
                                  </div>
                                  {event.detail ? (
                                    <div className="license-host__remote-device-meta">
                                      <span>{event.detail}</span>
                                    </div>
                                  ) : null}
                                  <div className="license-host__record-actions">
                                    <button
                                      type="button"
                                      className="sidebar-button"
                                      disabled={!canQuickGrant}
                                      onClick={() => handleOpenQuickGrantFromLoginEvent(event)}
                                    >
                                      快速授权
                                    </button>
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="license-host__empty">
                            当前还没有新的登录事件。
                          </div>
                        )}
                      </section>

                      <section className="license-host__remote-section license-host__remote-section--scroll">
                        <div className="license-host__remote-section-head">
                          <div>
                            <strong>最近认证与授权记录</strong>
                            <span>聚焦当前选中客户的近期动作，便于回查和补发授权。</span>
                          </div>
                        </div>
                        {selectedRemoteGroup ? (
                          selectedRemoteGroup.recentActivities.length > 0 ? (
                            <div className="license-host__remote-activity-list">
                              {selectedRemoteGroup.recentActivities.map((activity) => (
                                <article
                                  key={activity.id}
                                  className={`license-host__remote-activity-card${
                                    activity.unread ? ' is-unread' : ''
                                  }`}
                                >
                                  <div className="license-host__remote-device-row">
                                    <strong>{activity.eventLabel}</strong>
                                    <span>{formatDateTime(activity.createdAt)}</span>
                                  </div>
                                  <div className="license-host__record-note">{activity.summary}</div>
                                  {activity.detail ? (
                                    <div className="license-host__remote-device-meta">
                                      <span>{activity.detail}</span>
                                    </div>
                                  ) : null}
                                  <div className="license-host__record-actions">
                                    {(activity.eventType === 'login' || activity.eventType === 'register') ? (
                                      <button
                                        type="button"
                                        className="sidebar-button"
                                        onClick={() => {
                                          void handleOpenQuickGrant(selectedRemoteGroup);
                                        }}
                                      >
                                        快速授权
                                      </button>
                                    ) : null}
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : (
                            <div className="license-host__empty">
                              这一组暂时还没有更多记录。
                            </div>
                          )
                        ) : (
                          <div className="license-host__empty">
                            先选中上方客户卡片。
                          </div>
                        )}
                      </section>
                    </div>
                  </div>


                  <aside className="license-host__console-feedback-rail">
                    <section className="license-host__remote-section license-host__remote-section--status">
                      <div className="license-host__remote-section-head">
                        <div>
                          <strong>主机状态</strong>
                          <span>右侧只保留当前会话、网络和待处理摘要。</span>
                        </div>
                      </div>
                      <div className="license-host__console-side-stats">
                        <div className="license-host__console-side-stat">
                          <span>控制台账号</span>
                          <strong>{hostConsoleAccount}</strong>
                          <small>{hostLoginRememberDevice ? '已记住账号' : '未记住账号'}</small>
                        </div>
                        <div className="license-host__console-side-stat">
                          <span>网络状态</span>
                          <strong>{remoteBusy === 'refresh' ? '刷新中' : '远程已接入'}</strong>
                          <small>{remoteDashboard.sourceUrl ?? hostState.remoteSourceUrl ?? '未配置远程源'}</small>
                        </div>
                        <div className="license-host__console-side-stat">
                          <span>当前会话</span>
                          <strong>{hostConsoleUnlocked ? '已解锁' : '未解锁'}</strong>
                          <small>
                            {hostLoginSession?.unlockedAt
                              ? `进入时间 ${formatDateTime(hostLoginSession?.unlockedAt ?? null)}`
                              : '闲置 2 小时后需重新输入密码'}
                          </small>
                        </div>
                        <div className="license-host__console-side-stat">
                          <span>待处理事项</span>
                          <strong>{remoteDashboard.unreadLoginCount + remoteDashboard.unreadFeedbackCount}</strong>
                          <small>
                            登录 {remoteDashboard.unreadLoginCount} / 反馈 {remoteDashboard.unreadFeedbackCount}
                          </small>
                        </div>
                      </div>
                    </section>

                    <section className="license-host__remote-section license-host__remote-section--feedback">
                      <div className="license-host__remote-section-head">
                        <div>
                          <strong>客户反馈收件箱</strong>
                          <div className="license-host__record-note">
                            顾客建议和客服对话固定挂在右侧，不再占用客户卡片区。
                          </div>
                        </div>
                        {remoteDashboard.unreadFeedbackCount > 0 ? (
                          <span className="license-host__pill-badge license-host__pill-badge--danger">
                            {remoteDashboard.unreadFeedbackCount}
                          </span>
                        ) : null}
                      </div>

                      {remoteDashboard.feedbackEntries.length > 0 ? (
                        <>
                          <div className="license-host__console-feedback-list">
                            {remoteDashboard.feedbackEntries.slice(0, 8).map((entry) => (
                              <article
                                key={entry.id}
                                className={`license-host__record-card license-host__feedback-entry${
                                  selectedFeedbackEntry?.id === entry.id ? ' is-selected' : ''
                                }${entry.status === 'pending' ? ' is-unread' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpenFeedbackConversation(entry)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleOpenFeedbackConversation(entry);
                                  }
                                }}
                              >
                                <div className="license-host__record-row">
                                  <strong>{entry.senderDisplayName ?? entry.senderEmail ?? '匿名反馈'}</strong>
                                  <span>{formatDateTime(entry.latestMessageAt || entry.createdAt)}</span>
                                </div>
                                <div className="license-host__record-note">
                                  {entry.previewText || entry.body || '客户发送了一条新反馈。'}
                                </div>
                                <div className="license-host__record-row">
                                  <span>{entry.senderDeviceId ?? '未记录设备'}</span>
                                  <span className={`license-host__feedback-status is-${entry.status}`}>
                                    {entry.status === 'acknowledged'
                                      ? '已回执'
                                      : entry.status === 'read'
                                        ? '已读'
                                        : '未读'}
                                  </span>
                                </div>
                              </article>
                            ))}
                          </div>

                          {featuredFeedbackEntry ? (
                            <div className="license-host__console-feedback-preview">
                              <div className="license-host__record-row">
                                <strong>
                                  {featuredFeedbackEntry.senderDisplayName ??
                                    featuredFeedbackEntry.senderEmail ??
                                    '匿名客户'}
                                </strong>
                                <span>{featuredFeedbackEntry.messages.length} 条记录</span>
                              </div>
                              <div className="license-host__record-note">
                                {featuredFeedbackEntry.previewText ||
                                  featuredFeedbackEntry.body ||
                                  '客户发送了一条新反馈。'}
                              </div>
                              <div className="license-host__record-row">
                                <span>{featuredFeedbackEntry.senderDeviceId ?? '未记录设备'}</span>
                                <button
                                  type="button"
                                  className="sidebar-button sidebar-button--secondary"
                                  onClick={() => handleOpenFeedbackConversation(featuredFeedbackEntry)}
                                >
                                  打开会话
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div className="license-host__empty">暂时还没有收到客户反馈。</div>
                      )}
                    </section>
                  </aside>
                </div>

                  <div className="license-host__remote-detail">
                    {selectedRemoteGroup && selectedRemoteClient ? (
                      <>
                        <div className="license-host__record-detail-grid license-host__record-detail-grid--remote">
                          <div className="license-host__record-detail-card">
                            <span>当前分组</span>
                            <strong>{selectedRemoteGroup.title}</strong>
                            <small>{getRemotePresenceLabel(selectedRemoteClient.presence)}</small>
                          </div>
                          <div className="license-host__record-detail-card">
                            <span>当前账号</span>
                            <strong>{getRemoteGroupAccountLabel(selectedRemoteGroup)}</strong>
                            <small>{selectedRemoteGroup.userId ?? '未记录云端用户 ID'}</small>
                          </div>
                          <div className="license-host__record-detail-card">
                            <span>当前主设备</span>
                            <strong>{selectedRemoteClient.deviceId}</strong>
                            <small>{selectedRemoteClient.machineName || '未记录机器名'}</small>
                          </div>
                          <div className="license-host__record-detail-card">
                            <span>客户端版本</span>
                            <strong>{selectedRemoteClient.clientVersion ?? '未上报'}</strong>
                            <small>{selectedRemoteClient.hostLabel} / {selectedRemoteClient.platformLabel}</small>
                          </div>
                          <div className="license-host__record-detail-card">
                            <span>当前通道</span>
                            <strong>{getRemoteUpdateChannelLabel(selectedRemoteUpdateChannel)}</strong>
                            <small>
                              {selectedRemoteUpdateChannel.updatedAt
                                ? `${selectedRemoteUpdateChannel.allowEngineerAccess ? '工程师模式已开放 / ' : ''}${formatDateTime(selectedRemoteUpdateChannel.updatedAt)}`
                                : selectedRemoteUpdateChannel.allowEngineerAccess
                                  ? '工程师模式已开放'
                                  : '未由主机接管'}
                            </small>
                          </div>
                            <div className="license-host__record-detail-card">
                              <span>当前授权</span>
                              <strong>{selectedRemoteClient.effectiveLicense.licenseTypeLabel}</strong>
                              <small>{selectedRemoteClient.effectiveLicense.remainingLabel}</small>
                            </div>
                            <div className="license-host__record-detail-card">
                              <span>账号类型</span>
                              <strong>{selectedRemoteClient.accessBadge.label}</strong>
                              <small>{selectedRemoteClient.accessBadge.emphasized ? '需要重点关注' : '常规状态'}</small>
                            </div>
                            <div className="license-host__record-detail-card">
                              <span>异常限制</span>
                              <strong>
                                {selectedRemoteClient.accountRestrictionUntil ? '30 天限制中' : '未限制'}
                              </strong>
                              <small>
                                {selectedRemoteClient.accountRestrictionUntil
                                  ? `至 ${formatDateTime(selectedRemoteClient.accountRestrictionUntil)}${
                                      selectedRemoteClient.accountRestrictionReason
                                        ? ` / ${selectedRemoteClient.accountRestrictionReason}`
                                        : ''
                                    }`
                                  : '当前设备可以正常注册和登录账号'}
                              </small>
                            </div>
                            <button
                              type="button"
                              className="license-host__record-detail-card license-host__record-detail-card--button copy-metric-card"
                            onClick={() => {
                              void handleCopyRemoteFingerprint();
                            }}
                          >
                            <span>机器指纹</span>
                            <strong>{selectedRemoteGroup.fingerprintSummary}</strong>
                            <small>点击复制完整指纹</small>
                          </button>
                        </div>

                        <div className="license-host__remote-section-grid">
                          <section className="license-host__remote-section">
                            <div className="license-host__remote-section-head">
                              <div>
                                <strong>授权与账号操作</strong>
                              </div>
                            </div>

                            <label className="license-host__field">
                              <span>远程授权时长</span>
                              <div className="license-host__duration-grid">
                                {hostState.supportedDurations.map((option) => (
                                  <button
                                    key={`remote-${option.key}`}
                                    type="button"
                                    className={`license-host__duration-chip license-host__duration-chip--compact${
                                      remoteForm.durationKey === option.key ? ' is-active' : ''
                                    }`}
                                    onClick={() =>
                                      setRemoteForm((current) => ({
                                        ...current,
                                        durationKey: option.key,
                                      }))
                                    }
                                  >
                                    <strong>{option.label}</strong>
                                  </button>
                                ))}
                              </div>
                            </label>

                            {remoteForm.durationKey === 'custom-days' ? (
                              <label className="license-host__field">
                                <span>自定义天数</span>
                                <input
                                  className="license-input"
                                  value={remoteForm.customDurationDaysInput}
                                  onChange={(event) =>
                                    setRemoteForm((current) => ({
                                      ...current,
                                      customDurationDaysInput: event.target.value,
                                    }))
                                  }
                                  placeholder={`输入 1-${MAX_CUSTOM_DURATION_DAYS} 天`}
                                />
                              </label>
                            ) : null}

                            <div className="license-host__remote-field-grid">
                              <label className="license-host__field">
                                <span>卡片槽位数</span>
                                <input
                                  className="license-input"
                                  value={remoteForm.slotLimitInput}
                                  onChange={(event) =>
                                    setRemoteForm((current) => ({
                                      ...current,
                                      slotLimitInput: event.target.value,
                                    }))
                                  }
                                  placeholder={`留空按默认；输入 1-${MAX_SLOT_LIMIT} 或 无限`}
                                />
                              </label>

                              <label className="license-host__field">
                                <span>重置后的密码</span>
                                <input
                                  className="license-input"
                                  value={remoteForm.passwordInput}
                                  onChange={(event) =>
                                    setRemoteForm((current) => ({
                                      ...current,
                                      passwordInput: event.target.value,
                                    }))
                                  }
                                  placeholder="至少 6 位，保留账号与机器关联"
                                />
                              </label>
                            </div>

                            <div className="license-host__remote-switch-row">
                              <div className="license-host__toggle-copy">
                                <strong>远程内测通道</strong>
                              </div>
                              <label className="settings-switch" aria-label="切换远程内测通道">
                                <input
                                  className="settings-switch__input"
                                  type="checkbox"
                                  checked={remoteForm.betaAccess}
                                  disabled={remoteBusy !== null}
                                  onChange={(event) => {
                                    void handleRemoteSetUpdateChannel(
                                      event.target.checked,
                                      event.target.checked ? remoteForm.advancedBetaAccess : false,
                                    );
                                  }}
                                />
                                <span className="settings-switch__track" />
                                <span className="settings-switch__thumb" />
                              </label>
                            </div>

                            <div className="license-host__remote-switch-row">
                              <div className="license-host__toggle-copy">
                                <strong>高级内测 / 工程师模式</strong>
                              </div>
                              <label className="settings-switch" aria-label="切换高级内测">
                                <input
                                  className="settings-switch__input"
                                  type="checkbox"
                                  checked={remoteForm.advancedBetaAccess}
                                  disabled={remoteBusy !== null}
                                  onChange={(event) => {
                                    void handleRemoteSetUpdateChannel(
                                      event.target.checked ? true : remoteForm.betaAccess,
                                      event.target.checked,
                                    );
                                  }}
                                />
                                <span className="settings-switch__track" />
                                <span className="settings-switch__thumb" />
                              </label>
                            </div>

                            <div className="license-host__quick-actions license-host__quick-actions--dense">
                              <button
                                type="button"
                                className="sidebar-button"
                                onClick={() => {
                                  void handleRemoteIssueLicense();
                                }}
                                disabled={remoteBusy !== null || !selectedRemoteClient.canIssueRemotely}
                              >
                                <KeyRound size={14} />
                                {remoteBusy === 'issue-license' ? '正在下发远程授权' : '下发远程授权'}
                              </button>
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleRemoteRevokeLicense();
                                }}
                                disabled={remoteBusy !== null}
                              >
                                <ShieldOff size={14} />
                                {remoteBusy === 'revoke-license' ? '正在移除授权' : '移除授权'}
                              </button>
                              {selectedRemoteClient.accountRestrictionUntil ? (
                                <button
                                  type="button"
                                  className="sidebar-button sidebar-button--secondary"
                                  onClick={() => {
                                    void handleRemoteReleaseAccountRestriction();
                                  }}
                                  disabled={remoteBusy !== null}
                                >
                                  <ShieldCheck size={14} />
                                  {remoteBusy === 'release-account-restriction'
                                    ? '正在解除异常限制'
                                    : '解除异常限制'}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleRemoteResetPassword();
                                }}
                                disabled={remoteBusy !== null}
                              >
                                <KeyRound size={14} />
                                {remoteBusy === 'reset-password' ? '正在重置密码' : '重置账号密码'}
                              </button>
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleRemoteResetSecondaryPassword();
                                }}
                                disabled={remoteBusy !== null}
                              >
                                <KeyRound size={14} />
                                {remoteBusy === 'reset-secondary-password'
                                  ? '正在重置二次密码'
                                  : '重置二次密码'}
                              </button>
                            </div>
                          </section>

                          <section className="license-host__remote-section">
                            <div className="license-host__remote-section-head">
                              <div>
                                <strong>空间传送 / 回调提取</strong>
                              </div>
                              {selectedRemoteSnapshot ? (
                                <button
                                  type="button"
                                  className="sidebar-button sidebar-button--secondary"
                                  onClick={() => {
                                    void handleCopyRemoteSnapshot();
                                  }}
                                  disabled={remoteBusy !== null}
                                >
                                  <Copy size={14} />
                                  复制回调
                                </button>
                              ) : null}
                            </div>

                            <div className="license-host__remote-field-grid">
                              <label className="license-host__field">
                                <span>要传送的本地空间</span>
                                <select
                                  className="license-input"
                                  value={remoteForm.profileId}
                                  onChange={(event) =>
                                    setRemoteForm((current) => ({
                                      ...current,
                                      profileId: event.target.value,
                                    }))
                                  }
                                >
                                  <option value="">请选择空间卡片</option>
                                  {remoteProfiles.map((profile) => (
                                    <option key={profile.id} value={profile.id}>
                                      {profile.workspaceName || profile.name}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label className="license-host__field license-host__field--wide">
                                <span>备注</span>
                                <textarea
                                  className="license-host__textarea license-host__textarea--compact"
                                  value={remoteForm.note}
                                  onChange={(event) =>
                                    setRemoteForm((current) => ({
                                      ...current,
                                      note: event.target.value,
                                    }))
                                  }
                                  placeholder="可写客户名、用途、导入提醒"
                                />
                              </label>
                            </div>

                            <div className="license-host__quick-actions license-host__quick-actions--dense">
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleRemoteIssueTransfer();
                                }}
                                disabled={
                                  remoteBusy !== null ||
                                  !selectedRemoteClient.canIssueRemotely ||
                                  !selectedRemoteProfile
                                }
                              >
                                <Sparkles size={14} />
                                {remoteBusy === 'issue-transfer' ? '正在传送空间卡片' : '传送空间卡片'}
                              </button>
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleImportRemoteSnapshot();
                                }}
                                disabled={remoteBusy !== null || !selectedRemoteSnapshot}
                              >
                                <Sparkles size={14} />
                                {remoteBusy === 'import-profile' ? '正在提取回主机' : '提取客户端卡片'}
                              </button>
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary"
                                onClick={() => {
                                  void handleCopyRemoteFingerprint();
                                }}
                                disabled={remoteBusy !== null}
                              >
                                <Copy size={14} />
                                复制客户指纹
                              </button>
                            </div>
                            {errorMessage ? (
                              <div className="license-host__banner is-error">{errorMessage}</div>
                            ) : null}
                            {toastMessage ? (
                              <div className="license-host__banner is-success">{toastMessage}</div>
                            ) : null}

                            {!selectedRemoteClient.canIssueRemotely ? (
                              <div className="license-host__empty">
                                这个客户设备还没有绑定在线账号。
                              </div>
                            ) : null}

                            <div className="license-host__remote-transfer-grid">
                              <div className="license-host__remote-transfer-pane">
                                <div className="license-host__remote-subsection-head">
                                  <strong>客户端回调</strong>
                                  <span>
                                    {selectedRemoteClient.profileSnapshotUpdatedAt
                                      ? formatDateTime(selectedRemoteClient.profileSnapshotUpdatedAt)
                                      : '等待回调'}
                                  </span>
                                </div>

                                {selectedRemoteSnapshots.length > 0 ? (
                                  <div className="license-host__remote-snapshot-list">
                                    {selectedRemoteSnapshots.map((snapshot) => {
                                      const snapshotState = getRemoteSnapshotStateMeta(snapshot);
                                      const snapshotSecurityChips = getRemoteSnapshotSecurityChips(snapshot);
                                      const snapshotSecurityDetail = getRemoteSnapshotSecurityDetail(snapshot);
                                      return (
                                        <article
                                          key={snapshot.id}
                                          className={`license-host__remote-snapshot-card${
                                            selectedRemoteSnapshot?.id === snapshot.id ? ' is-selected' : ''
                                          } is-${snapshotState.tone}-state`}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => setSelectedRemoteSnapshotId(snapshot.id)}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                              event.preventDefault();
                                              setSelectedRemoteSnapshotId(snapshot.id);
                                            }
                                          }}
                                        >
                                          <div className="license-host__remote-snapshot-row">
                                            <strong>{getRemoteSnapshotTitle(snapshot)}</strong>
                                            <div className="license-host__remote-snapshot-badges">
                                              <span
                                                className={`license-host__remote-chip license-host__remote-chip--${snapshotState.tone}`}
                                              >
                                                {snapshotState.label}
                                              </span>
                                              {snapshotSecurityChips.map((chip) => (
                                                <span
                                                  key={chip.key}
                                                  className={`license-host__remote-chip license-host__remote-chip--${chip.tone}`}
                                                >
                                                  {chip.label}
                                                </span>
                                              ))}
                                              <span className="license-host__remote-chip">
                                                {getPlanLabel(snapshot.planType)}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="license-host__remote-snapshot-row">
                                            <span>{snapshot.workspaceName ?? snapshot.name}</span>
                                            <span>{formatRemainingPercent(snapshot.overallRemainingPercent)}</span>
                                          </div>
                                          <div className="license-host__remote-snapshot-row">
                                            <span>
                                              5 小时 {formatRemoteQuotaSnapshotLabel(snapshot.fiveHourQuota)} / 周 {formatRemoteQuotaSnapshotLabel(snapshot.weeklyQuota)}
                                            </span>
                                            <span>{snapshot.transferPackage ? '可提取完整卡片' : '仅回传概要'}</span>
                                          </div>
                                          {snapshotSecurityDetail ? (
                                            <div className="license-host__remote-snapshot-copy">
                                              {snapshotSecurityDetail}
                                            </div>
                                          ) : null}
                                        </article>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="license-host__empty">
                                    当前还没有回调空间数据。
                                  </div>
                                )}
                              </div>

                              <div className="license-host__remote-transfer-pane license-host__remote-transfer-pane--stack">
                                {selectedRemoteSnapshot ? (
                                  <div className="license-host__remote-snapshot-preview">
                                    <div className="license-host__remote-snapshot-preview-head">
                                      <strong>{getRemoteSnapshotTitle(selectedRemoteSnapshot)}</strong>
                                      <span>
                                        {selectedRemoteClient.profileSnapshotUpdatedAt
                                          ? formatDateTime(selectedRemoteClient.profileSnapshotUpdatedAt)
                                          : '未记录更新时间'}
                                      </span>
                                    </div>
                                    <div className="license-host__remote-device-meta">
                                      {selectedRemoteSnapshotState ? (
                                        <span
                                          className={`license-host__remote-chip license-host__remote-chip--${selectedRemoteSnapshotState?.tone ?? 'ready'}`}
                                        >
                                          {selectedRemoteSnapshotState?.label ?? '正常可用'}
                                        </span>
                                      ) : null}
                                      {getRemoteSnapshotSecurityChips(selectedRemoteSnapshot).map((chip) => (
                                        <span
                                          key={chip.key}
                                          className={`license-host__remote-chip license-host__remote-chip--${chip.tone}`}
                                        >
                                          {chip.label}
                                        </span>
                                      ))}
                                      <span className="license-host__remote-chip">
                                        {getPlanLabel(selectedRemoteSnapshot.planType)}
                                      </span>
                                      <span>
                                        总剩余 {formatRemainingPercent(selectedRemoteSnapshot.overallRemainingPercent)}
                                      </span>
                                      <span>
                                        评审 {formatRemoteQuotaSnapshotLabel(selectedRemoteSnapshot.reviewQuota)}
                                      </span>
                                      <span>{formatRemoteAuthStatusLabel(selectedRemoteSnapshot.authStatus)}</span>
                                    </div>
                                    {getRemoteSnapshotSecurityDetail(selectedRemoteSnapshot) ? (
                                      <div className="license-host__remote-snapshot-copy">
                                        {getRemoteSnapshotSecurityDetail(selectedRemoteSnapshot)}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}

                                <div className="license-host__remote-subsection-head">
                                  <strong>本机轨迹</strong>
                                  <span>{selectedRemoteGroup.devices.length} 台设备</span>
                                </div>
                                <div className="license-host__remote-device-list license-host__remote-device-list--compact">
                                  {selectedRemoteGroup.devices.map((client) => (
                                    <article key={client.deviceId} className="license-host__remote-device-card">
                                      <div className="license-host__remote-device-row">
                                        <div className="license-host__remote-device-title">
                                          <span className={`license-host__presence-dot is-${client.presence}`} />
                                          <strong>{client.machineName || client.deviceId}</strong>
                                        </div>
                                        <span>{formatDateTime(client.lastSeenAt)}</span>
                                      </div>
                                      <div className="license-host__remote-device-meta">
                                        <span>{client.deviceId}</span>
                                        <span>
                                          {client.hostLabel} / {client.platformLabel}
                                        </span>
                                        <span>版本 {client.clientVersion ?? '未上报'}</span>
                                        <span>{client.email ?? client.displayName ?? '未绑定账号'}</span>
                                      </div>
                                    </article>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </section>
                        </div>
                      </>
                    ) : (
                      <div className="license-host__empty license-host__remote-placeholder">
                        先从左侧选一个客户分组。
                      </div>
                    )}
                  </div>
              </>
            )}
          </section>
            ) : null}
                </div>
              </div>
                </>
              ) : null}
            </>
          )}

          <Dialog.Root
            open={Boolean(quickGrantGroup)}
            onOpenChange={(open) => {
              if (!open) {
                setQuickGrantGroupId(null);
              }
            }}
          >
            <Dialog.Portal>
              <Dialog.Overlay className="license-host__dialog-overlay" />
              <Dialog.Content className="license-host__dialog-content license-host__dialog-content--quick-grant">
                <div className="license-host__dialog-head">
                  <div>
                    <Dialog.Title>快速授权</Dialog.Title>
                    <Dialog.Description>
                      {quickGrantGroup
                        ? `给 ${quickGrantGroup.title} 直接下发时长与卡槽，不用再回到主表单里重填。`
                        : '给最近登录的客户快速下发授权。'}
                    </Dialog.Description>
                  </div>
                  <Dialog.Close asChild>
                    <button type="button" className="license-host__dialog-close">
                      关闭
                    </button>
                  </Dialog.Close>
                </div>

                {quickGrantGroup ? (
                  <div className="license-host__quick-grant-body">
                    <div className="license-host__quick-grant-hero">
                      <div className="license-host__quick-grant-hero-copy">
                        <span className="license-host__quick-grant-badge">客服快捷授权</span>
                        <strong>{quickGrantGroup.title}</strong>
                        <div className="license-host__remote-device-meta">
                          <span>{getRemoteGroupAccountLabel(quickGrantGroup)}</span>
                          <span>{quickGrantGroup.primaryClient.deviceId}</span>
                          <span>{quickGrantGroup.primaryClient.accessBadge.label}</span>
                        </div>
                      </div>
                      <div className="license-host__quick-grant-summary">
                        <span>当前选择</span>
                        <strong>{currentRemoteDurationLabel}</strong>
                        <small>{currentRemoteSlotLimitHint}</small>
                      </div>
                    </div>

                    <section className="license-host__quick-grant-section">
                      <div className="license-host__quick-grant-section-head">
                        <strong>授权时长</strong>
                        <span>点选常用时长，客服处理消息时不用再回主表单重填。</span>
                      </div>
                      <div className="license-host__duration-grid license-host__duration-grid--quick-grant">
                        {hostState.supportedDurations.map((option) => (
                          <button
                            key={`quick-${option.key}`}
                            type="button"
                            className={`license-host__duration-chip license-host__duration-chip--quick-grant${
                              remoteForm.durationKey === option.key ? ' is-active' : ''
                            }`}
                            onClick={() =>
                              setRemoteForm((current) => ({
                                ...current,
                                durationKey: option.key,
                              }))
                            }
                          >
                            <strong>{option.label}</strong>
                          </button>
                        ))}
                      </div>
                    </section>

                    {remoteForm.durationKey === 'custom-days' ? (
                      <label className="license-host__field">
                        <span>自定义天数</span>
                        <input
                          className="license-input"
                          value={remoteForm.customDurationDaysInput}
                          onChange={(event) =>
                            setRemoteForm((current) => ({
                              ...current,
                              customDurationDaysInput: event.target.value,
                            }))
                          }
                          placeholder="例如 99"
                        />
                      </label>
                    ) : null}

                    <div className="license-host__dialog-grid license-host__dialog-grid--quick-grant">
                      <label className="license-host__field">
                        <span>卡片槽位</span>
                        <input
                          className="license-input"
                          value={remoteForm.slotLimitInput}
                          onChange={(event) =>
                            setRemoteForm((current) => ({
                              ...current,
                              slotLimitInput: event.target.value,
                            }))
                          }
                          placeholder="留空按默认，或填 无限"
                        />
                      </label>
                      <label className="license-host__field">
                        <span>备注</span>
                        <textarea
                          className="license-host__textarea license-host__textarea--compact"
                          value={remoteForm.note}
                          onChange={(event) =>
                            setRemoteForm((current) => ({
                              ...current,
                              note: event.target.value,
                            }))
                          }
                          placeholder="给自己留个备注"
                        />
                      </label>
                    </div>

                    <div className="license-host__quick-grant-switches">
                      <div className="license-host__quick-grant-switch">
                        <div>
                          <strong>同步切到内测更新通道</strong>
                          <span>给当前客户一并打开内测更新权限。</span>
                        </div>
                        <label className="settings-switch" aria-label="切换快速授权内测通道">
                          <input
                            className="settings-switch__input"
                            type="checkbox"
                            checked={remoteForm.betaAccess}
                            onChange={(event) =>
                              setRemoteForm((current) => ({
                                ...current,
                                betaAccess: event.target.checked,
                                advancedBetaAccess: event.target.checked ? current.advancedBetaAccess : false,
                              }))
                            }
                          />
                          <span className="settings-switch__track" />
                          <span className="settings-switch__thumb" />
                        </label>
                      </div>

                      <div className="license-host__quick-grant-switch">
                        <div>
                          <strong>同步开通高级内测 / 工程师模式</strong>
                          <span>只在确实需要的时候给该客户打开更高权限。</span>
                        </div>
                        <label className="settings-switch" aria-label="切换快速授权工程师模式">
                          <input
                            className="settings-switch__input"
                            type="checkbox"
                            checked={remoteForm.advancedBetaAccess}
                            onChange={(event) =>
                              setRemoteForm((current) => ({
                                ...current,
                                betaAccess: event.target.checked ? true : current.betaAccess,
                                advancedBetaAccess: event.target.checked,
                              }))
                            }
                          />
                          <span className="settings-switch__track" />
                          <span className="settings-switch__thumb" />
                        </label>
                      </div>
                    </div>

                    <div className="license-host__record-actions license-host__record-actions--spread">
                      <button
                        type="button"
                        className="sidebar-button sidebar-button--secondary"
                        onClick={() => setQuickGrantGroupId(null)}
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        className="sidebar-button"
                        disabled={remoteBusy === 'issue-license'}
                        onClick={() => {
                          void handleRemoteIssueLicense(quickGrantGroup);
                        }}
                      >
                        {remoteBusy === 'issue-license' ? '下发中...' : '确认授权'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {errorMessage ? <div className="license-host__banner is-error">{errorMessage}</div> : null}
          {toastMessage ? <div className="license-host__banner is-success">{toastMessage}</div> : null}
        </div>
      </div>
    </div>
  );
}
